// en-AU's "short" month is not always three letters — toLocaleDateString renders
// September as "Sept" and June/July in full — so the three-letter lookups this file
// used to carry silently failed on half the period labels ("5 Sept 2026" parsed as
// January, which sorted three real periods to the bottom of the Pay Period list and
// hung "(Current)" on 16 May 2026). One map, every spelling, used everywhere a month
// name is read back out of a label.
const MONTH_NUM = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
  may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8,
  sep: 9, sept: 9, september: 9, oct: 10, october: 10,
  nov: 11, november: 11, dec: 12, december: 12,
};
const monthNum = m => MONTH_NUM[(m || '').toString().trim().toLowerCase().replace(/\.$/, '')] || 0;

function toISO(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return '';
  return `${parts[2]}-${String(monthNum(parts[1])).padStart(2,'0')}-${parts[0].padStart(2,'0')}`;
}

// Date → YYYY-MM-DD in LOCAL time. toISOString() would shift the date backwards for
// AEST, which for a period start would select the previous period's rate activation.
function dateToISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Fuel CATCH-UP lines: an earlier period's fuel that never reached its own RCTI, carried
// into this one in the Fuel Workbench (sheets.js → adj.fuelCatchUp). Each earned-period is
// its OWN line, next to this period's fuel, and names the period it was earned in.
// isFuelLevy so the fuel re-sync and the missing-fuel check treat it as fuel.
function fuelCatchUpLines(adj, reportEndDate) {
  const endISO = toISO(reportEndDate);
  return ((adj && adj.fuelCatchUp) || [])
    .filter(x => x.period === endISO && x.amount > 0)
    .sort((a, b) => (a.fromPeriod < b.fromPeriod ? -1 : 1))
    .map(x => {
      const end = new Date(x.fromPeriod + 'T12:00:00');
      const start = new Date(end); start.setDate(end.getDate() - 27);
      const f = (d, y) => d.toLocaleDateString('en-AU', y ? { day: 'numeric', month: 'short', year: 'numeric' } : { day: 'numeric', month: 'short' });
      const amount = Math.round(x.amount * 100) / 100;
      return { type: 'fuel_levy_catchup', description: `Service Fee — Prevailing Conditions (catch-up ${f(start)} – ${f(end, true)})`, quantity: 1, rate: amount, amount, isFuelLevy: true, isFuelCatchUp: true, fuelFromPeriod: x.fromPeriod };
    });
}

// "19 Apr 2026 to 16 May 2026" → "2026-05-16". Every month spelling goes through
// monthNum, so "13 June", "11 July" and "5 Sept" parse like "16 May" does.
function labelToEndISO(label) {
  const endPart = (label || '').split(' to ')[1] || '';
  const [d, m, y] = endPart.trim().split(/\s+/);
  const mn = monthNum(m);
  return (d && mn && y) ? `${y}-${String(mn).padStart(2,'0')}-${d.padStart(2,'0')}` : '';
}

// "2026-05-16" → "19 Apr 2026 to 16 May 2026"
function isoToPeriodLabel(endISO) {
  if (!endISO) return '';
  const end = new Date(endISO + 'T00:00:00');
  if (isNaN(end.getTime())) return '';
  const start = new Date(end);
  start.setDate(end.getDate() - 27);
  const fmt = d => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${fmt(start)} to ${fmt(end)}`;
}
import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate as useNav, useSearchParams } from 'react-router-dom';
import { Zap, RefreshCw, CheckCircle, ExternalLink, AlertTriangle, ChevronDown, ChevronRight, X, Eye, Mail, Send, Plus, Trash2, Fuel, Calendar, Paperclip } from 'lucide-react';
import { detectPeriodEnd, todayISO } from '../utils/periods';
import { activationFor } from '../utils/rateActivations';
import { gstStatusFor } from '../utils/gstStatus';
import RctiRankings from '../components/RctiRankings';
import DriverAvatar, { fetchDriverPhotos } from '../components/DriverPhoto';

// The first period whose RCTI emails are logged on the server (rcti_email_log). Before
// it, "Sent" was only this browser's memory; from it on, Sent means an email went out.
const EMAIL_LOG_FROM = '2026-10-03';
const ARCHIVE_FN = '/.netlify/functions/appsscript';
const APPS_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzOBv0uBlIglllBCIh8AQHJZKTzRF7jrfVe5I7qr2w8Tc4oy9QnFRsVS0gb9EprnxMliw/exec';
// Manual RCTIs are stored in Supabase (the Apps Script no longer implements
// saveManualRCTI/loadManualRCTIs). save/load/delete go here instead.
const MANUAL_RCTI_URL = '/.netlify/functions/manual-rcti-sb';
const RCTI_ARCHIVE_URL = '/.netlify/functions/rcti-archive-sb';
const ADVANCES_URL = '/.netlify/functions/advances-sb';
// Email send mode is now component state (testMode) inside RCTIPage — persisted in
// localStorage, default Test for safety, flippable via the header toggle. (Was hardcoded.)

const HPR = { name: 'Hollypaul Recruitment Pty Ltd', abn: '60 651 664 885', address: 'PO Box 1710, Warriewood NSW 2102', logo: '/IMG_5199.jpg' };
const DECLARATION = ['I operate an independent transport business.','I invoice for delivery services performed, not for hours worked.','I bear commercial risk in operating my business.','I am responsible for my own taxation, superannuation and insurances.','I may provide services to multiple clients.','I may engage or substitute another suitably qualified person to perform the services.','Vehicle supplied under separate commercial hire agreement where applicable.','Remuneration has been assessed against the applicable Minimum Contract Remuneration Order. Any required adjustment has been included above.'];
const FOOTER = ['The recipient and the supplier declare that this recipient created tax invoice reflects the agreement between the parties.','The supplier will not issue tax invoices in respect of the supplies.','The supplier acknowledges that it is registered for GST and will notify the recipient if it ceases to be registered.','The recipient acknowledges that it is registered for GST and will notify the supplier if it ceases to be registered.'];

const PRESET_DESCRIPTIONS = [
  'Shortfall Payment — Period',
  'Service Fee Correction — Period',
  'Ad Hoc Service Fee',
  'Route Familiarisation Fee',
  'DoE Newtown Collection Tue - Thu',
  'Service Fee — Prevailing Conditions',
  'Parcel Delivery Coordination Fee',
  'Express Delivery Coordination Fee',
  'GCCD Top-Up Adjustment',
  'Other (type below)',
];
const DEDUCTION_DESCRIPTIONS = [
  'Amount Correction (Reduction)',
  'Duplicate Payment Recovery',
  'Period Adjustment (Deduction)',
  'Invoice Correction — Overcharge',
  'Service Fee Reversal',
  'Van Lease Correction',
  'Other (type below)',
];
// Default ex-GST rate to pre-fill when a preset is chosen in "Add Items".
const PRESET_RATES = { 'DoE Newtown Collection Tue - Thu': 20 };
// Count days in [startDate,endDate] whose weekday name (Mon/Tue/…/Sun) appears in
// `desc` — used to default a recurring fee's quantity (e.g. "… Tue - Thu" → number
// of Tue+Thu days in the period). Falls back to 1 when no weekday is named.
function weekdayCountFromDesc(desc, startDate, endDate) {
  const map = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
  const wanted = new Set();
  ((desc || '').toLowerCase().match(/\b(sun|mon|tue|wed|thu|fri|sat)\b/g) || []).forEach(t => wanted.add(map[t]));
  if (!wanted.size) return 1;
  let n = 0;
  const d = new Date(startDate); d.setHours(12, 0, 0, 0);
  const end = new Date(endDate); end.setHours(12, 0, 0, 0);
  while (d <= end) { if (wanted.has(d.getDay())) n++; d.setDate(d.getDate() + 1); }
  return n || 1;
}
const STD_RATE_TYPES = new Set(['Parcel', 'Exp', 'DOE', 'DoE', 'Depot Bags', 'Depot']);
// A van lease is quoted per 4-week RCTI period, which is 20 working days — not 28 calendar
// days. That's the denominator when a part-period driver's lease is pro-rated (buildRCTI).
const LEASE_DAYS_PER_PERIOD = 20;
// A driver's commencement date as a local-midnight Date, or null when blank/unreadable.
// The column holds both shapes: older rows were typed "30/07/2026", while drivers added
// through the engine are saved "2026-07-30". Reading only the slash form treated every
// newer starter as here all along — a full-period lease for DITTOVALJ's 10 days (Sep 2026).
function parseCommencement(s) {
  const t = (s || '').toString().trim();
  let y, m, d;
  let p;
  if ((p = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/))) [, y, m, d] = p;
  else if ((p = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/))) [, d, m, y] = p;
  else return null;
  const c = new Date(Number(y), Number(m) - 1, Number(d));
  return isNaN(c.getTime()) ? null : c;
}
// A %-based van lease is charged on delivery work only — Parcels, Express, DoE and
// Depot Bags. Never on uplifts, fuel, training, extras, or one-off inserted lines
// (reimbursements / deductions like a tyre refund or accident-excess part-payment),
// which would otherwise inflate or shrink the lease fee (Paul, 13 Aug 2026).
const LEASE_BASE_TYPES = new Set(['parcel', 'express', 'doe', 'depot']);
// Recompute an RCTI's money totals after its line items change (add/amend/remove).
// CRITICAL: gross is NOT just the sum of line items — some gross components
// (tempFuel, adHoc) are folded into gross with no line item, so we preserve that
// residual instead of dropping it. Lease fee is recomputed when rate-based (off the
// LEASE_BASE_TYPES lines only), and GST keys off the authoritative gstRegistered flag
// (never a subtotal heuristic).
// ── Advance repayments ────────────────────────────────────────────────────────────────
// An advance is a LOAN, not consideration for a supply. Repaying it must not change the
// value of the taxable supply or the GST on it, so the instalment comes off AFTER the
// GST-inclusive total: grossIncome / subtotal / gst / total are untouched and only the
// amount actually remitted moves. Routing it through `deductions` instead would net it
// off BEFORE GST (subtotal = gross − leaseFee, gst = subtotal × 10%) — the driver would
// lose $1.10 of remittance per $1 of debt and the RCTI would understate his taxable
// supply by the whole instalment. See netlify/functions/advances-sb.js.
//
// The cap is a share of the GST-inclusive total, so it has to be re-derived whenever the
// line items change — hence one pure function that both buildRCTI and recomputeRctiTotals
// call, off metadata carried on the RCTI itself (no ledger read needed to re-price).
// `due` is what the schedule says should have been repaid by the end of this period, less
// what already has been — so a period the cap cut short is automatically made up in the
// next one rather than quietly stretching the schedule (deed cl 5.2: the shortfall "is
// carried forward and added to the next Instalment"). Falls back to a flat instalment
// when there's no start date to count periods from.
function priceAdvance(meta, total) {
  if (!meta || !(meta.balanceBefore > 0)) return null;
  const r2 = (n) => Math.round(n * 100) / 100;
  const capped = meta.capPct > 0 ? r2(meta.capPct * total) : Infinity;
  const wanted = meta.due != null ? meta.due : (meta.instalment || 0);
  const amount = r2(Math.max(0, Math.min(wanted, meta.balanceBefore, capped)));
  return { ...meta, amount, balanceAfter: r2(meta.balanceBefore - amount), capApplied: amount < Math.min(wanted, meta.balanceBefore) - 0.005 };
}
// The amount that actually leaves the bank for this RCTI.
const netRemittance = (rcti) => Math.round(((rcti.total || 0) - ((rcti.advance && rcti.advance.amount) || 0)) * 100) / 100;
// Pick the live advance for a login/period and work out what it stands at BEFORE this
// period is priced — counting only periods strictly earlier, so a rebuild or an
// un-approve → re-approve cycle always re-prices the same period to the same number.
function advanceMetaFor(advances, login, periodEndISO) {
  const key = (login || '').toString().toUpperCase();
  const a = (advances || []).find(x => x.status === 'active'
    && (x.login || '').toUpperCase() === key
    && (!x.firstPeriodEndISO || (periodEndISO || '') >= x.firstPeriodEndISO));
  if (!a) return null;
  const prior = Object.entries(a.applied || {})
    .filter(([k]) => k < (periodEndISO || ''))
    .reduce((s, [, x]) => s + (Number(x.amount) || 0), 0);
  const balanceBefore = Math.round(((Number(a.amount) || 0) - prior) * 100) / 100;
  if (!(balanceBefore > 0)) return null;
  const instalment = Number(a.instalment) || 0;
  // Catch-up target: how many 28-day periods in (inclusive) this one is, times the
  // instalment, capped at the advance — less what's already been repaid.
  let due = null;
  if (instalment > 0 && a.firstPeriodEndISO) {
    const days = Math.round((Date.parse(periodEndISO) - Date.parse(a.firstPeriodEndISO)) / 86400000);
    const n = Math.floor(days / 28) + 1;
    if (n > 0) due = Math.round((Math.min(Number(a.amount) || 0, instalment * n) - prior) * 100) / 100;
  }
  return {
    id: a.id, kind: a.kind || 'advance',
    label: a.label || (a.kind === 'overpayment' ? 'Overpayment Correction' : 'Advance Repayment'),
    instalment, due, capPct: Number(a.capPct) || 0,
    balanceBefore, reference: a.reference || '', finalDueISO: a.finalDueISO || '',
  };
}
function recomputeRctiTotals(rcti, newLineItems) {
  const sum = (arr) => Math.round(arr.reduce((s, li) => s + (li.amount || 0), 0) * 100) / 100;
  const oldLineSum = sum(rcti.lineItems || []);
  const residual = Math.round(((rcti.grossIncome != null ? rcti.grossIncome : oldLineSum) - oldLineSum) * 100) / 100;
  const grossIncome = Math.round((sum(newLineItems) + residual) * 100) / 100;
  const ded = rcti.deductions || {};
  let leaseFee = ded.leaseFee || 0;
  if ((ded.leaseRate || 0) > 0) {
    const leaseBase = sum(newLineItems.filter(li => LEASE_BASE_TYPES.has(li.type)));
    const raw = leaseBase * ded.leaseRate;
    leaseFee = Math.round((ded.leaseCap > 0 ? Math.min(raw, ded.leaseCap) : raw) * 100) / 100;
  }
  const subtotal = Math.round((grossIncome - leaseFee) * 100) / 100;
  const gstRate = rcti.gstRegistered === false ? 0 : 0.1;
  const gst = Math.round(subtotal * gstRate * 100) / 100;
  const total = Math.round((subtotal + gst) * 100) / 100;
  // Re-price the advance off the NEW total — the cap is a share of it, so an amendment
  // that moves the total has to move the capped instalment with it.
  const advance = priceAdvance(rcti.advance, total);
  return { grossIncome, subtotal, gst, total, leaseFee, advance };
}
function generatePDFBase64(rcti) {
  return new Promise(async (resolve) => {
    let logoSrc = '';
    try {
      const logoRes = await fetch('/IMG_5199.jpg');
      const blob = await logoRes.blob();
      logoSrc = await new Promise(r => { const fr = new FileReader(); fr.onload = () => r(fr.result); fr.readAsDataURL(blob); });
    } catch(e) { logoSrc = ''; }
    const today = new Date().toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const lineItemRows = rcti.lineItems.filter(li => li.quantity > 0 || li.amount > 0).map((li, i) =>
      `<tr style="border-bottom:1px solid #ddd;background:${i % 2 === 0 ? '#f9f9f9' : '#fff'}">
        <td style="padding:7px 10px">${li.description}</td>
        <td style="padding:7px 10px;text-align:right">${li.quantity.toLocaleString()}</td>
        <td style="padding:7px 10px;text-align:right">${fmtRate(li.rate)}</td>
        <td style="padding:7px 10px;text-align:right">${fmt(li.amount)}</td>
      </tr>`
    ).join('');
    const leaseFeeRow = rcti.deductions && rcti.deductions.leaseFee > 0
      ? `<tr><td style="padding:5px 10px;color:#c00">${rcti.deductions.leaseLabel || 'Van Lease Fee'}</td><td style="padding:5px 10px;text-align:right;color:#c00">-${fmt(rcti.deductions.leaseFee)}</td></tr>`
      : '';
    const declarationItems = DECLARATION.map(line =>
      `<div style="margin-bottom:4px;padding-left:12px;border-left:2px solid #1a3a6b;line-height:1.5">${line}</div>`
    ).join('');
    const footerItems = FOOTER.map(line =>
      `<div style="font-style:italic;color:#555;margin-bottom:3px;line-height:1.5">${line}</div>`
    ).join('');
    const html = `<!DOCTYPE html>
<html><head>
  <style>* { box-sizing: border-box; margin: 0; padding: 0; } body { font-family: Arial, sans-serif; font-size: 11px; color: #000; background: #fff; padding: 14mm 14mm 10mm 14mm; } table { border-collapse: collapse; } @page { size: A4 portrait; margin: 0; }</style>
</head><body>
  <div style="text-align:center;border-bottom:2px solid #1a3a6b;padding-bottom:12px;margin-bottom:16px">
    <div style="font-size:16px;font-weight:700;color:#1a3a6b">RCTI - RECIPIENT CREATED TAX INVOICE</div>
    <div style="font-size:13px;font-weight:600;margin-top:4px">Tax Invoice</div>
  </div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:16px">
    <div style="border:1px solid #ccc;padding:12px;border-radius:4px">
      <div style="font-weight:700;margin-bottom:6px;color:#1a3a6b">Supplier (contractor)</div>
      <div style="font-weight:600">${rcti.personalName || rcti.driverName}</div>
      <div>Company Name: ${rcti.companyName || '—'}</div>
      <div>ABN: ${rcti.abn || '—'}</div>
      <div style="margin-top:6px">Address: ${rcti.address || '—'}</div>
      <div style="margin-top:6px">Mobile: ${rcti.phone || '—'}</div>
      <div>Email: ${rcti.email || '—'}</div>
    </div>
    <div style="display:flex;flex-direction:column;align-items:flex-end;gap:8px">
<img src="${logoSrc}" alt="HPR" style="height:70px;object-fit:contain" />
      <div style="text-align:right;line-height:1.7">
        <div style="font-weight:600">Hollypaul Recruitment Pty Ltd</div>
        <div>ABN: 60 651 664 885</div>
        <div>PO Box 1710, Warriewood NSW 2102</div>
      </div>
      <div style="border:1px solid #ccc;padding:8px 12px;border-radius:4px;width:100%">
        <div style="display:grid;grid-template-columns:130px 1fr;gap:2px 8px">
          <span style="color:#555">Invoice Number:</span><span style="font-weight:600">${rcti.invoiceNumber}</span>
          <span style="color:#555">Invoice Date:</span><span>${today}</span>
          <span style="color:#555">Billing Period (4 Weeks):</span><span>${rcti.periodLabel}</span>
        </div>
      </div>
    </div>
  </div>
  <img src="data:image/svg+xml;base64,${btoa("<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1000 34'><rect width='1000' height='34' fill='#1a3a6b'/><text x='14' y='23' fill='#ffffff' font-family='Arial' font-size='15' font-weight='bold'>Description</text><text x='604' y='23' fill='#ffffff' font-family='Arial' font-size='15' font-weight='bold' text-anchor='end'>Quantity</text><text x='800' y='23' fill='#ffffff' font-family='Arial' font-size='15' font-weight='bold' text-anchor='end'>Rate (ex GST)</text><text x='986' y='23' fill='#ffffff' font-family='Arial' font-size='15' font-weight='bold' text-anchor='end'>Amount (ex GST)</text></svg>")}" style="width:100%;height:auto;display:block" alt="" />
  <table style="width:100%;margin-bottom:8px;border-collapse:collapse">
    <colgroup><col style="width:46%" /><col style="width:18%" /><col style="width:18%" /><col style="width:18%" /></colgroup>
    <tbody>${lineItemRows}</tbody>
  </table>
  <div style="display:flex;justify-content:flex-end;margin-bottom:16px">
    <table style="width:320px">
      <tbody>
        <tr style="border-top:2px solid #1a3a6b">
          <td style="padding:6px 10px;font-weight:700;color:#1a3a6b">Gross Service Income (ex GST):</td>
          <td style="padding:6px 10px;text-align:right;font-weight:700">${fmt(rcti.grossIncome)}</td>
        </tr>
        ${leaseFeeRow}
        <tr style="border-top:1px solid #ccc">
          <td style="padding:5px 10px">Subtotal (Taxable Supply ex GST):</td>
          <td style="padding:5px 10px;text-align:right;font-weight:600">${fmt(rcti.subtotal)}</td>
        </tr>
        <tr>
          <td style="padding:5px 10px;color:#555">${rcti.gst === 0 ? 'No GST Applicable' : 'GST (10%):'}</td>
          <td style="padding:5px 10px;text-align:right;color:#555">${fmt(rcti.gst)}</td>
        </tr>
        <tr style="border-top:2px solid #1a3a6b;background:#f0f4ff">
          <td style="padding:8px 10px;font-weight:700;font-size:13px">Total Amount Payable (inc GST):</td>
          <td style="padding:8px 10px;text-align:right;font-weight:700;font-size:13px;color:#1a3a6b">${fmt(rcti.total)}</td>
        </tr>${advanceRowsHtml(rcti)}
      </tbody>
    </table>
  </div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px">
    <div>
      <div style="font-weight:700;margin-bottom:8px;color:#1a3a6b">Contractor Declaration</div>
      ${declarationItems}
    </div>
    <div style="border:1px solid #ccc;border-radius:4px;padding:12px">
      <div style="font-weight:700;margin-bottom:8px;color:#1a3a6b">Bank Account Details</div>
      <div style="line-height:2">
        <div><strong>Account Name:</strong> ${rcti.companyName || rcti.personalName}</div>
        <div><strong>BSB:</strong> ${rcti.bsb || '—'}</div>
        <div><strong>Acc Number:</strong> ${rcti.accNumber || '—'}</div>
      </div>
    </div>
  </div>
  <div style="border-top:1px solid #ccc;padding-top:12px">${footerItems}</div>
</body></html>`;

    // Use a hidden iframe to render and capture as blob via print
    // We'll encode the HTML as base64 and send it to Apps Script to convert
    const encoded = btoa(unescape(encodeURIComponent(html)));
    resolve(encoded);
  });
}
function buildWhatsAppLink(rcti) {
  if (!rcti.phone) return null;
  const phone = rcti.phone.replace(/\D/g, '').replace(/^0/, '61');
  const msg = encodeURIComponent(
    `Hi ${rcti.personalName || rcti.companyName},\n\n` +
    `Your RCTI for the period ${rcti.periodLabel} is ready.\n\n` +
    `Invoice: ${rcti.invoiceNumber}\n` +
    `Total Payable (inc GST): ${rcti.totalFormatted || fmt(rcti.total)}\n\n` +
    `Your invoice has been sent to ${rcti.email || 'your email address'}.\n\n` +
    `Please contact us if you have any questions.\n\n` +
    `Hollypaul Recruitment Pty Ltd`
  );
  return `https://wa.me/${phone}?text=${msg}`;
}
  function fmt(n) {
  if (n === undefined || n === null) return '$0.00';
  const abs = Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return n < 0 ? `-$${abs}` : `$${abs}`;
}
// The small print under a deduction. An overpayment recovery isn't a loan, so it says what
// the earlier invoice was and what was actually paid instead of "advance outstanding".
const advanceAside = (a) => (a.kind === 'overpayment' ? '(correction of an earlier overpayment — not a supply)' : '(repayment of loan — not a supply)');
function advanceFootnote(a) {
  if (a.kind === 'overpayment') {
    return `${a.reference || 'Overpayment on an earlier RCTI'}${a.balanceAfter > 0 ? ` · still to recover after this payment: ${fmt(a.balanceAfter)}` : ''}`;
  }
  return `Advance outstanding after this payment: ${fmt(a.balanceAfter)}${a.finalDueISO ? ` · repayable in full by ${a.finalDueISO}` : ''}`;
}
// Totals-table rows for an advance repayment. They sit BELOW "Total Amount Payable
// (inc GST)" on purpose: the invoice still states the full taxable supply and the full
// GST on it, and only the cash actually remitted changes. Prints nothing when no advance
// is running, so every other RCTI is byte-identical to before.
function advanceRowsHtml(rcti) {
  const a = rcti.advance;
  if (!a || !(a.amount > 0)) return '';
  return `
        <tr>
          <td style="padding:5px 10px;color:#c00">${a.label || 'Advance Repayment'} <span style="color:#888;font-size:10px">${advanceAside(a)}</span></td>
          <td style="padding:5px 10px;text-align:right;color:#c00">-${fmt(a.amount)}</td>
        </tr>
        <tr style="border-top:2px solid #1a3a6b;background:#eef6ee">
          <td style="padding:8px 10px;font-weight:700;font-size:13px">Net Payment to Bank Account:</td>
          <td style="padding:8px 10px;text-align:right;font-weight:700;font-size:13px;color:#1a3a6b">${fmt(netRemittance(rcti))}</td>
        </tr>
        <tr>
          <td colspan="2" style="padding:4px 10px;color:#666;font-size:10px">${advanceFootnote(a)}</td>
        </tr>`;
}
function fmtRate(n) {
  if (n === undefined || n === null) return '$0.00';
  const abs = Math.abs(n);
  const rounded2 = Math.round(abs * 100) / 100;
  const decimals = Math.abs(abs - rounded2) > 0.0005 ? 4 : 2;
  const [intPart, decPart] = abs.toFixed(decimals).split('.');
  const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + decPart;
  return n < 0 ? `-$${formatted}` : `$${formatted}`;
}

function PreviewModal({ rcti, onClose }) {
  const today = new Date().toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    const lineItemRows = rcti.lineItems.map((li, i) =>
      `<tr style="border-bottom:1px solid #ddd;background:${i % 2 === 0 ? '#f9f9f9' : '#fff'}">
        <td style="padding:7px 10px">${li.description}</td>
        <td style="padding:7px 10px;text-align:right">${li.quantity.toLocaleString()}</td>
        <td style="padding:7px 10px;text-align:right">${fmtRate(li.rate)}</td>
        <td style="padding:7px 10px;text-align:right">${fmt(li.amount)}</td>
      </tr>`
    ).join('');
    const leaseFeeRow = rcti.deductions && rcti.deductions.leaseFee > 0
      ? `<tr><td style="padding:5px 10px;color:#c00">${rcti.deductions.leaseLabel || 'Van Lease Fee'}</td><td style="padding:5px 10px;text-align:right;color:#c00">-${fmt(rcti.deductions.leaseFee)}</td></tr>`
      : '';
    const declarationItems = DECLARATION.map(line =>
      `<div style="margin-bottom:4px;padding-left:12px;border-left:2px solid #1a3a6b;line-height:1.5">${line}</div>`
    ).join('');
    const footerItems = FOOTER.map(line =>
      `<div style="font-style:italic;color:#555;margin-bottom:3px;line-height:1.5">${line}</div>`
    ).join('');
    printWindow.document.write(`<!DOCTYPE html>
<html><head>
  <title>${rcti.companyName || rcti.personalName} ${rcti.invoiceNumber} ${rcti.periodLabel}</title>
  <style>* { box-sizing: border-box; margin: 0; padding: 0; } body { font-family: Arial, sans-serif; font-size: 11px; color: #000; background: #fff; padding: 14mm 14mm 10mm 14mm; } table { border-collapse: collapse; } @page { size: A4 portrait; margin: 0; }</style>
</head><body>
  <div style="text-align:center;border-bottom:2px solid #1a3a6b;padding-bottom:12px;margin-bottom:16px">
    <div style="font-size:16px;font-weight:700;color:#1a3a6b">RCTI - RECIPIENT CREATED TAX INVOICE</div>
    <div style="font-size:13px;font-weight:600;margin-top:4px">Tax Invoice</div>
  </div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:16px">
    <div style="border:1px solid #ccc;padding:12px;border-radius:4px">
      <div style="font-weight:700;margin-bottom:6px;color:#1a3a6b">Supplier (contractor)</div>
      <div style="font-weight:600">${rcti.personalName || rcti.driverName}</div>
      <div>Company Name: ${rcti.companyName || '—'}</div>
      <div>ABN: ${rcti.abn || '—'}</div>
      <div style="margin-top:6px">Address: ${rcti.address || '—'}</div>
      <div style="margin-top:6px">Mobile: ${rcti.phone || '—'}</div>
      <div>Email: ${rcti.email || '—'}</div>
    </div>
    <div style="display:flex;flex-direction:column;align-items:flex-end;gap:8px">
      <img src="${HPR.logo}" alt="HPR" style="height:70px;object-fit:contain" />
      <div style="text-align:right;line-height:1.7">
        <div style="font-weight:600">${HPR.name}</div>
        <div>ABN: ${HPR.abn}</div>
        <div>${HPR.address}</div>
      </div>
      <div style="border:1px solid #ccc;padding:8px 12px;border-radius:4px;width:100%">
        <div style="display:grid;grid-template-columns:130px 1fr;gap:2px 8px">
          <span style="color:#555">Invoice Number:</span><span style="font-weight:600">${rcti.invoiceNumber}</span>
          <span style="color:#555">Invoice Date:</span><span>${today}</span>
          <span style="color:#555">Billing Period (4 Weeks):</span><span>${rcti.periodLabel}</span>
        </div>
      </div>
    </div>
  </div>
  <img src="data:image/svg+xml;base64,${btoa("<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1000 34'><rect width='1000' height='34' fill='#1a3a6b'/><text x='14' y='23' fill='#ffffff' font-family='Arial' font-size='15' font-weight='bold'>Description</text><text x='604' y='23' fill='#ffffff' font-family='Arial' font-size='15' font-weight='bold' text-anchor='end'>Quantity</text><text x='800' y='23' fill='#ffffff' font-family='Arial' font-size='15' font-weight='bold' text-anchor='end'>Rate (ex GST)</text><text x='986' y='23' fill='#ffffff' font-family='Arial' font-size='15' font-weight='bold' text-anchor='end'>Amount (ex GST)</text></svg>")}" style="width:100%;height:auto;display:block" alt="" />
  <table style="width:100%;margin-bottom:8px;border-collapse:collapse">
    <colgroup><col style="width:46%" /><col style="width:18%" /><col style="width:18%" /><col style="width:18%" /></colgroup>
    <tbody>${lineItemRows}</tbody>
  </table>
  <div style="display:flex;justify-content:flex-end;margin-bottom:16px">
    <table style="width:320px">
      <tbody>
        <tr style="border-top:2px solid #1a3a6b">
          <td style="padding:6px 10px;font-weight:700;color:#1a3a6b">Gross Service Income (ex GST):</td>
          <td style="padding:6px 10px;text-align:right;font-weight:700">${fmt(rcti.grossIncome)}</td>
        </tr>
        ${leaseFeeRow}
        <tr style="border-top:1px solid #ccc">
          <td style="padding:5px 10px">Subtotal (Taxable Supply ex GST):</td>
          <td style="padding:5px 10px;text-align:right;font-weight:600">${fmt(rcti.subtotal)}</td>
        </tr>
        <tr>
          <td style="padding:5px 10px;color:#555">${rcti.gst === 0 ? 'No GST Applicable' : 'GST (10%):'}</td>
          <td style="padding:5px 10px;text-align:right;color:#555">${fmt(rcti.gst)}</td>
        </tr>
        <tr style="border-top:2px solid #1a3a6b;background:#f0f4ff">
          <td style="padding:8px 10px;font-weight:700;font-size:13px">Total Amount Payable (inc GST):</td>
          <td style="padding:8px 10px;text-align:right;font-weight:700;font-size:13px;color:#1a3a6b">${fmt(rcti.total)}</td>
        </tr>${advanceRowsHtml(rcti)}
      </tbody>
    </table>
  </div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px">
    <div>
      <div style="font-weight:700;margin-bottom:8px;color:#1a3a6b">Contractor Declaration</div>
      ${declarationItems}
    </div>
    <div style="border:1px solid #ccc;border-radius:4px;padding:12px">
      <div style="font-weight:700;margin-bottom:8px;color:#1a3a6b">Bank Account Details</div>
      <div style="line-height:2">
        <div><strong>Account Name:</strong> ${rcti.companyName || rcti.personalName}</div>
        <div><strong>BSB:</strong> ${rcti.bsb || '—'}</div>
        <div><strong>Acc Number:</strong> ${rcti.accNumber || '—'}</div>
      </div>
    </div>
  </div>
  <div style="border-top:1px solid #ccc;padding-top:12px">${footerItems}</div>
</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 600);
  };

  return (
    <div id="rcti-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, overflowY: 'auto', display: 'flex', justifyContent: 'center', padding: '40px 20px' }}>
      <div style={{ background: '#fff', color: '#000', width: '100%', maxWidth: 820, borderRadius: 8, padding: '40px', fontFamily: 'Arial, sans-serif', fontSize: '11px', position: 'relative' }}>
        <div style={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 8 }}>
          <button onClick={handlePrint} style={{ background: '#1a3a6b', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>Download PDF</button>
          <button onClick={onClose} style={{ background: '#eee', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}><X size={16} /></button>
        </div>
        <div style={{ textAlign: 'center', borderBottom: '2px solid #1a3a6b', paddingBottom: 12, marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1a3a6b' }}>RCTI - RECIPIENT CREATED TAX INVOICE</div>
          <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>Tax Invoice</div>
        </div>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 16 }}>
          <div style={{ border: '1px solid #ccc', padding: 12, borderRadius: 4 }}>
            <div style={{ fontWeight: 700, marginBottom: 6, color: '#1a3a6b' }}>Supplier (contractor)</div>
            <div style={{ fontWeight: 600 }}>{rcti.personalName || rcti.driverName}</div>
            <div>Company Name: {rcti.companyName || '—'}</div>
            <div>ABN: {rcti.abn || '—'}</div>
            <div style={{ marginTop: 6 }}>Address: {rcti.address || '—'}</div>
            <div style={{ marginTop: 6 }}>Mobile: {rcti.phone || '—'}</div>
            <div>Email: {rcti.email || '—'}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <img src={HPR.logo} alt="HPR" style={{ height: 70, objectFit: 'contain' }} />
            <div style={{ textAlign: 'right', lineHeight: 1.7 }}>
              <div style={{ fontWeight: 600 }}>{HPR.name}</div>
              <div>ABN: {HPR.abn}</div>
              <div>{HPR.address}</div>
            </div>
            <div style={{ border: '1px solid #ccc', padding: '8px 12px', borderRadius: 4, width: '100%' }}>
              <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '2px 8px' }}>
                <span style={{ color: '#555' }}>Invoice Number:</span><span style={{ fontWeight: 600 }}>{rcti.invoiceNumber}</span>
                <span style={{ color: '#555' }}>Invoice Date:</span><span>{today}</span>
                <span style={{ color: '#555' }}>Billing Period (4 Weeks):</span><span>{rcti.periodLabel}</span>
              </div>
            </div>
          </div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
          <thead>
            <tr style={{ background: '#1a3a6b', color: '#fff' }}>
              <th style={{ padding: '8px 10px', textAlign: 'left' }}>Description</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Quantity</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Rate (ex GST)</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Amount (ex GST)</th>
            </tr>
          </thead>
          <tbody>
            {rcti.lineItems.map((li, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #ddd', background: i % 2 === 0 ? '#f9f9f9' : '#fff' }}>
                <td style={{ padding: '7px 10px' }}>{li.description}</td>
                <td style={{ padding: '7px 10px', textAlign: 'right' }}>{li.quantity.toLocaleString()}</td>
                <td style={{ padding: '7px 10px', textAlign: 'right' }}>{fmtRate(li.rate)}</td>
                <td style={{ padding: '7px 10px', textAlign: 'right' }}>{fmt(li.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <table style={{ width: 320, borderCollapse: 'collapse' }}>
            <tbody>
              <tr style={{ borderTop: '2px solid #1a3a6b' }}>
                <td style={{ padding: '6px 10px', fontWeight: 700, color: '#1a3a6b' }}>Gross Service Income (ex GST):</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700 }}>{fmt(rcti.grossIncome)}</td>
              </tr>
              {rcti.deductions && rcti.deductions.leaseFee > 0 && (
                <tr>
                  <td style={{ padding: '5px 10px', color: '#c00' }}>{rcti.deductions.leaseLabel || 'Van Lease Fee'}</td>
                  <td style={{ padding: '5px 10px', textAlign: 'right', color: '#c00' }}>-{fmt(rcti.deductions.leaseFee)}</td>
                </tr>
              )}
              <tr style={{ borderTop: '1px solid #ccc' }}>
                <td style={{ padding: '5px 10px' }}>Subtotal (Taxable Supply ex GST):</td>
                <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: 600 }}>{fmt(rcti.subtotal)}</td>
              </tr>
                <tr>
                <td style={{ padding: '5px 10px', color: '#555' }}>{rcti.gst === 0 ? 'No GST Applicable' : 'GST (10%):'}</td>
                <td style={{ padding: '5px 10px', textAlign: 'right', color: '#555' }}>{fmt(rcti.gst)}</td>
              </tr>
              <tr style={{ borderTop: '2px solid #1a3a6b', background: '#f0f4ff' }}>
                <td style={{ padding: '8px 10px', fontWeight: 700, fontSize: 13 }}>Total Amount Payable (inc GST):</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, fontSize: 13, color: '#1a3a6b' }}>{fmt(rcti.total)}</td>
              </tr>
              {rcti.advance && rcti.advance.amount > 0 && (
                <>
                  <tr>
                    <td style={{ padding: '5px 10px', color: '#c00' }}>
                      {rcti.advance.label || 'Advance Repayment'}{' '}
                      <span style={{ color: '#888', fontSize: 10 }}>{advanceAside(rcti.advance)}</span>
                    </td>
                    <td style={{ padding: '5px 10px', textAlign: 'right', color: '#c00' }}>-{fmt(rcti.advance.amount)}</td>
                  </tr>
                  <tr style={{ borderTop: '2px solid #1a3a6b', background: '#eef6ee' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 700, fontSize: 13 }}>Net Payment to Bank Account:</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, fontSize: 13, color: '#1a3a6b' }}>{fmt(netRemittance(rcti))}</td>
                  </tr>
                  <tr>
                    <td colSpan={2} style={{ padding: '4px 10px', color: '#666', fontSize: 10 }}>
                      {advanceFootnote(rcti.advance)}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <div style={{ fontWeight: 700, marginBottom: 8, color: '#1a3a6b' }}>Contractor Declaration</div>
            {DECLARATION.map((line, i) => (
              <div key={i} style={{ marginBottom: 4, paddingLeft: 12, borderLeft: '2px solid #1a3a6b', lineHeight: 1.5 }}>{line}</div>
            ))}
          </div>
          <div style={{ border: '1px solid #ccc', borderRadius: 4, padding: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 8, color: '#1a3a6b' }}>Bank Account Details</div>
            <div style={{ lineHeight: 2 }}>
              <div><strong>Account Name:</strong> {rcti.companyName || rcti.personalName}</div>
              <div><strong>BSB:</strong> {rcti.bsb || '—'}</div>
              <div><strong>Acc Number:</strong> {rcti.accNumber || '—'}</div>
            </div>
          </div>
        </div>
        <div style={{ borderTop: '1px solid #ccc', paddingTop: 12 }}>
          {FOOTER.map((line, i) => (
            <div key={i} style={{ fontStyle: 'italic', color: '#555', marginBottom: 3, lineHeight: 1.5 }}>{line}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

// A manual RCTI lives whole in the manual store. Editing one (add/amend/remove a line,
// pro-rate the lease) used to re-save it flagged `isModification` under its own id — and
// the loader drops modifications from the manual list, so the edited invoice vanished from
// the register. Built rows are still saved as modifications of their built original.
const storedEdit = (updated, original) => (original.isManual
  ? { ...updated, isManual: true, isModification: false }
  : { ...updated, isModification: true, originalId: original.id });

// The manual store never holds Approved or the Xero/email state (those live on the page),
// so a reload of the store must not reset a pushed manual RCTI back to pending.
const carryManualFlags = (stored, onPage) => (onPage ? {
  ...stored,
  approved: onPage.approved,
  xeroStatus: onPage.xeroStatus || stored.xeroStatus,
  xeroInvoiceId: onPage.xeroInvoiceId || stored.xeroInvoiceId,
  xeroInvoiceGuid: onPage.xeroInvoiceGuid || stored.xeroInvoiceGuid,
  emailStatus: onPage.emailStatus || stored.emailStatus,
} : stored);

const emptyLine = () => ({ id: Date.now() + Math.random(), preset: '', customDesc: '', quantity: 1, rate: '', amount: 0, sign: '+' });

function ManualRCTIBuilder({ drivers, driversLoading, payPeriods, rctis, onGenerate }) {
  const [selectedDriver, setSelectedDriver] = useState('');
  const [periodMode, setPeriodMode] = useState('select');
  const [selectedPeriodEnd, setSelectedPeriodEnd] = useState(payPeriods[0] || '');
  const [customPeriod, setCustomPeriod] = useState('');
  const [lines, setLines] = useState([emptyLine()]);
  // Van lease deduction. A manual RCTI used to have no way to take the lease off, so a
  // new starter's first invoice (DITTOVALJ, Sep 2026) couldn't be raised here at all. It
  // lands in `deductions.leaseFee` exactly like a built RCTI, so the PDF shows it and the
  // Xero push books it to the lease account (211) instead of netting it into fees.
  const [leaseDays, setLeaseDays] = useState('');
  const [leaseFee, setLeaseFee] = useState('');

  const driver = drivers.find(d => d.loginName === selectedDriver);
  const fullLeaseFee = driver ? (Number(driver.leaseFee) || 0) : 0;

  const pickDriver = (login) => {
    setSelectedDriver(login);
    const d = drivers.find(x => x.loginName === login);
    const fee = d ? (Number(d.leaseFee) || 0) : 0;
    setLeaseDays(fee > 0 ? String(LEASE_DAYS_PER_PERIOD) : '');
    setLeaseFee(fee > 0 ? fee.toFixed(2) : '');
  };
  const changeLeaseDays = (v) => {
    setLeaseDays(v);
    const days = parseFloat(v);
    if (fullLeaseFee > 0 && !isNaN(days)) setLeaseFee((Math.round(fullLeaseFee * (days / LEASE_DAYS_PER_PERIOD) * 100) / 100).toFixed(2));
  };

  const updateLine = (id, field, value) => {
    setLines(prev => prev.map(l => {
      if (l.id !== id) return l;
      const updated = { ...l, [field]: value };
      if (field === 'quantity' || field === 'rate') {
        const q = parseFloat(field === 'quantity' ? value : updated.quantity) || 0;
        const r = parseFloat(field === 'rate' ? value : updated.rate) || 0;
        updated.amount = Math.round(q * r * 100) / 100;
      }
      return updated;
    }));
  };

  const addLine = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (id) => setLines(prev => prev.length > 1 ? prev.filter(l => l.id !== id) : prev);

  const round2 = n => Math.round(n * 100) / 100;
  const grossIncome = round2(lines.reduce((s, l) => s + (l.amount || 0), 0));
  const leaseDeduction = round2(Math.max(0, parseFloat(leaseFee) || 0));
  const subtotal = round2(grossIncome - leaseDeduction);
  // Same rule as a built RCTI: GST keys off the contractor's flag, never a flat 10%.
  const gstRegistered = driver ? driver.gstRegistered !== false : true;
  const gst = round2(subtotal * (gstRegistered ? 0.1 : 0));
  const total = round2(subtotal + gst);

  // Build period label — payPeriods contains full labels like "19 Apr 2026 to 16 May 2026"
  const getPeriodLabel = () => {
    if (periodMode === 'custom') return customPeriod;
    return selectedPeriodEnd; // already the full period label string
  };

  // "9 Aug 2026 to 5 Sept 2026" → "2026-09-05". labelToEndISO understands "June"/"Sept";
  // the old three-letter map sent those to January.
  const getPeriodEndISO = () => labelToEndISO(getPeriodLabel());

  const handleGenerate = async () => {
    if (!driver) { alert('Please select a contractor.'); return; }
    if (!(driver.abn || '').toString().trim()) { alert(`${driver.loginName} has no ABN on file — an RCTI can't be issued until they supply one.`); return; }
    const periodLabel = getPeriodLabel();
    if (!periodLabel) { alert('Please select or enter a period.'); return; }
    const validLines = lines.filter(l => (l.preset || l.customDesc) && l.amount > 0);
    if (validLines.length === 0) { alert('Please add at least one line item with a description and amount.'); return; }
    if (subtotal <= 0) { alert('The lease deduction is larger than the fees — nothing would be payable.'); return; }

    // Draw the number from the shared ledger so a manual RCTI never collides with an
    // auto-build number. No fallback: this builder no longer needs a build first, so the
    // register may hold nothing to count from, and a guessed number would reuse one that
    // is already on an issued invoice.
    const manualId = `MANUAL-${driver.loginName}-${Date.now()}`;
    let nextNum = null;
    try {
      const res = await fetch('/.netlify/functions/rcti-numbers?action=assign', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [manualId] }),
      });
      const data = await res.json();
      if (data.success && data.map && data.map[manualId] != null) nextNum = data.map[manualId];
    } catch { /* handled below */ }
    if (nextNum == null) { alert('Could not reach the RCTI number ledger — nothing was created. Try again in a moment.'); return; }

    const lineItems = validLines.map(l => ({
      description: l.preset === 'Other (type below)' ? l.customDesc : l.preset,
      quantity: parseFloat(l.quantity) || 1,
      rate: parseFloat(l.rate) || l.amount,
      amount: l.amount,
    }));

    const rcti = {
      id: manualId,
      invoiceNumber: `RCTI-${nextNum}`,
      driverLogin: driver.loginName,
      periodEndISO: getPeriodEndISO(),
      driverName: driver.loginName,
      personalName: driver.name,
      companyName: driver.companyName,
      abn: driver.abn,
      address: driver.address,
      phone: driver.phone,
      email: driver.email,
      bsb: driver.bsb,
      accNumber: driver.accNumber,
      leaseLabel: driver.leaseLabel,
      gstRegistered,
      route: driver.route || '',
      periodLabel,
      reportEndDate: getPeriodEndISO() || customPeriod,
      status: 'Not Posted',
      xeroStatus: 'pending',
      emailStatus: 'unsent',
      isManual: true,
      lineItems,
      gccd: { meetsMinimum: true, totalHours: 0, minRate: 0, minPayable: 0, actualEarnings: grossIncome, variance: 0, gccdLevy: 0 },
      deductions: { leaseFee: leaseDeduction, fixedLeaseFee: fullLeaseFee, leaseLabel: driver.leaseLabel || 'Van Lease Fee' },
      grossIncome,
      subtotal,
      gst,
      total,
      totalFormatted: fmt(total),
    };

    onGenerate(rcti);
    // Reset form
    setSelectedDriver('');
    setLines([emptyLine()]);
    setCustomPeriod('');
    setLeaseDays('');
    setLeaseFee('');
  };

  return (
    <div className="card" style={{ marginBottom: 20, border: '1px solid rgba(201,168,76,.3)', background: 'rgba(201,168,76,.03)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Plus size={15} color="var(--hp-gold)" />
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.7rem', textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--hp-gold)', fontWeight: 700 }}>Manual RCTI Builder</span>
      </div>

      {drivers.length === 0 ? (
        <div style={{ color: 'var(--hp-dim)', fontSize: '.8rem', padding: '12px 0' }}>
          {driversLoading ? 'Loading contractors…' : <>Couldn't load contractors. Close and reopen the builder to try again — there is <strong>no need to build RCTIs</strong> first.</>}
        </div>
      ) : (
        <>
          {/* Row 1: Contractor + Period */}
          <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: '.72rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 6 }}>Contractor</label>
              <select className="form-select" value={selectedDriver} onChange={e => pickDriver(e.target.value)}>
                <option value="">— Select contractor —</option>
                {[...drivers].sort((a, b) => (a.companyName || a.name || '').localeCompare(b.companyName || b.name || '')).map(d => (
                  <option key={d.loginName} value={d.loginName}>{d.companyName || d.name} ({d.loginName})</option>
                ))}
              </select>
              {driver && (
                <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 6, lineHeight: 1.7 }}>
                  <div>{driver.name} · ABN {driver.abn || <strong style={{ color: 'var(--hp-red)' }}>none — can't issue</strong>}</div>
                  <div>{driver.gstRegistered === false ? 'Not registered for GST' : 'Registered for GST'} · {driver.email || '—'}</div>
                </div>
              )}
            </div>
            <div>
              <label style={{ fontSize: '.72rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 6 }}>Period Being Adjusted</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <button className={`btn btn-sm ${periodMode === 'select' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setPeriodMode('select')} style={{ fontSize: '.7rem' }}>From register</button>
                <button className={`btn btn-sm ${periodMode === 'custom' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setPeriodMode('custom')} style={{ fontSize: '.7rem' }}>Custom</button>
              </div>
              {periodMode === 'select' ? (
                <select className="form-select" value={selectedPeriodEnd} onChange={e => setSelectedPeriodEnd(e.target.value)}>
                  <option value="">— Select period —</option>
                  {payPeriods.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              ) : (
                <input
                  className="form-input"
                  placeholder="e.g. 22 Mar 2026 to 18 Apr 2026"
                  value={customPeriod}
                  onChange={e => setCustomPeriod(e.target.value)}
                />
              )}
              {getPeriodLabel() && (
                <div style={{ fontSize: '.72rem', color: 'var(--hp-gold)', marginTop: 6 }}>Period: {getPeriodLabel()}</div>
              )}
            </div>
          </div>

          {/* Line items */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 8, marginBottom: 6 }}>
              {['Description', 'Qty', 'Rate (ex GST)', 'Amount (ex GST)', ''].map(h => (
                <div key={h} style={{ fontSize: '.62rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{h}</div>
              ))}
            </div>
            {lines.map(line => (
              <div key={line.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'start' }}>
                <div>
                  <select
                    className="form-select"
                    value={line.preset}
                    onChange={e => updateLine(line.id, 'preset', e.target.value)}
                    style={{ marginBottom: line.preset === 'Other (type below)' ? 6 : 0 }}
                  >
                    <option value="">— Select description —</option>
                    {PRESET_DESCRIPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  {line.preset === 'Other (type below)' && (
                    <input
                      className="form-input"
                      placeholder="Type custom description..."
                      value={line.customDesc}
                      onChange={e => updateLine(line.id, 'customDesc', e.target.value)}
                    />
                  )}
                </div>
                <input
                  className="form-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.quantity}
                  onChange={e => updateLine(line.id, 'quantity', e.target.value)}
                  style={{ textAlign: 'right' }}
                />
                <input
                  className="form-input"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={line.rate}
                  onChange={e => updateLine(line.id, 'rate', e.target.value)}
                  style={{ textAlign: 'right' }}
                />
                <div style={{ padding: '8px 10px', background: 'var(--hp-surface)', borderRadius: 6, textAlign: 'right', fontSize: '.8rem', fontWeight: 600 }}>
                  {fmt(line.amount)}
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => removeLine(line.id)} style={{ padding: '6px 8px', color: 'var(--hp-red)' }}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
            <button className="btn btn-ghost btn-sm" onClick={addLine} style={{ fontSize: '.75rem', marginTop: 4 }}>
              <Plus size={12} /> Add Line
            </button>
          </div>

          {/* Van lease deduction */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, margin: '4px 0 14px', padding: '10px 12px', border: '1px solid var(--hp-border)', borderRadius: 6 }}>
            <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{(driver && driver.leaseLabel) || 'Van Lease Fee'} (deducted)</span>
            {fullLeaseFee > 0 && (
              <span style={{ fontSize: '.75rem', color: 'var(--hp-dim)' }}>
                <input className="form-input" type="number" min="0" max={LEASE_DAYS_PER_PERIOD} step="1" value={leaseDays}
                  onChange={e => changeLeaseDays(e.target.value)} style={{ width: 64, textAlign: 'right', display: 'inline-block' }} />
                {' '}of {LEASE_DAYS_PER_PERIOD} working days × {fmt(fullLeaseFee)}
              </span>
            )}
            <span style={{ fontSize: '.75rem', color: 'var(--hp-dim)' }}>
              − $ <input className="form-input" type="number" min="0" step="0.01" placeholder="0.00" value={leaseFee}
                onChange={e => setLeaseFee(e.target.value)} style={{ width: 110, textAlign: 'right', display: 'inline-block' }} />
            </span>
            {driver && !(fullLeaseFee > 0) && <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)' }}>No van lease on {driver.loginName}'s record — leave blank unless one applies.</span>}
          </div>

          {/* Totals + Generate */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderTop: '1px solid var(--hp-border)', paddingTop: 12 }}>
            <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)', lineHeight: 2 }}>
              <div>Gross Income (ex GST): <strong style={{ color: 'var(--hp-gold)' }}>{fmt(grossIncome)}</strong></div>
              {leaseDeduction > 0 && <div>Less {(driver && driver.leaseLabel) || 'Van Lease Fee'}: <strong style={{ color: 'var(--hp-red)' }}>-{fmt(leaseDeduction)}</strong></div>}
              {leaseDeduction > 0 && <div>Subtotal (ex GST): <strong>{fmt(subtotal)}</strong></div>}
              <div>GST ({gstRegistered ? '10%' : 'not registered'}): <strong>{fmt(gst)}</strong></div>
              <div>Total (inc GST): <strong style={{ color: 'var(--hp-gold)', fontSize: '1rem' }}>{fmt(total)}</strong></div>
            </div>
            <button className="btn btn-primary" onClick={handleGenerate} style={{ fontWeight: 700 }}>
              <Plus size={14} /> Generate Manual RCTI
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const XERO_LINE_TYPES = [
  ['contractorGst',   'Contractor Fees — GST registered (e.g. 484)'],
  ['contractorNoGst', 'Contractor Fees — No GST (e.g. 484.01)'],
  ['lease',           'Van lease deduction (e.g. 211)'],
];
const XERO_ROUTES = ['City', 'Broadway', 'Darlinghurst'];

function XeroSettingsPanel({ config, saving, onSave, accounts }) {
  const [c, setC] = React.useState(config);
  React.useEffect(() => { setC(config); }, [config]);
  const setAccount = (k, v) => setC(p => ({ ...p, accounts: { ...p.accounts, [k]: v } }));
  const setTrack = (k, v) => setC(p => ({ ...p, tracking: { ...p.tracking, [k]: v } }));
  const setRouteOpt = (route, v) => setC(p => ({ ...p, tracking: { ...p.tracking, optionByRoute: { ...(p.tracking.optionByRoute || {}), [route]: v } } }));
  const inp = { width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '.8rem' };

  return (
    <div style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 16, marginBottom: 16, background: 'var(--hp-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: '.85rem' }}>Xero settings</strong>
        <label style={{ fontSize: '.8rem', display: 'flex', alignItems: 'center', gap: 6 }}>
          Push as
          <select value={c.pushStatus} onChange={e => setC(p => ({ ...p, pushStatus: e.target.value }))} style={{ ...inp, width: 'auto' }}>
            <option value="DRAFT">Draft (review in Xero)</option>
            <option value="AUTHORISED">Authorised (awaiting payment)</option>
          </select>
        </label>
        <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} disabled={saving} onClick={() => onSave(c)}>{saving ? 'Saving…' : 'Save settings'}</button>
      </div>

      <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>
        Account codes (chart of accounts){accounts && accounts.length ? ` — ${accounts.length} accounts loaded from Xero` : ''}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))', gap: 8, marginBottom: 16 }}>
        {XERO_LINE_TYPES.map(([k, label]) => (
          <label key={k} style={{ fontSize: '.78rem', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ color: 'var(--hp-dim)' }}>{label}</span>
            {accounts && accounts.length ? (
              <select value={(c.accounts || {})[k] || ''} onChange={e => setAccount(k, e.target.value)} style={inp}>
                <option value="">— not mapped —</option>
                {accounts.map(a => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
              </select>
            ) : (
              <input value={(c.accounts || {})[k] || ''} onChange={e => setAccount(k, e.target.value)} placeholder="e.g. 310" style={inp} />
            )}
          </label>
        ))}
      </div>

      <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>Tracking (optional)</div>
      <label style={{ fontSize: '.8rem', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <input type="checkbox" checked={!!c.tracking?.enabled} onChange={e => setTrack('enabled', e.target.checked)} />
        Tag bills with a Route tracking category
      </label>
      {c.tracking?.enabled && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))', gap: 8 }}>
          <label style={{ fontSize: '.78rem', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ color: 'var(--hp-dim)' }}>Category name (as in Xero)</span>
            <input value={c.tracking.categoryName || ''} onChange={e => setTrack('categoryName', e.target.value)} placeholder="e.g. Route" style={inp} />
          </label>
          {XERO_ROUTES.map(route => (
            <label key={route} style={{ fontSize: '.78rem', display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ color: 'var(--hp-dim)' }}>{route} → option</span>
              <input value={(c.tracking.optionByRoute || {})[route] || ''} onChange={e => setRouteOpt(route, e.target.value)} placeholder={`Xero option for ${route}`} style={inp} />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Advances panel ────────────────────────────────────────────────────────────────────
// Money advanced to a subcontractor ahead of the work, recovered from later RCTIs. The
// deduction lands AFTER GST (see priceAdvance), so nothing here touches the value of the
// supply or the GST on it — only what leaves the bank.
//
// `login` is the login the RCTI is ISSUED UNDER, which for a network principal is not the
// personnel login: PARRACASA's invoices are built under ALANAS. Key it wrong and the
// deduction silently never attaches, so the field says so and the ledger shows both.
function AdvancesPanel({ advances, rctis, form, setForm, busy, onSave, onVoid, onRefresh, onSend, onWithdraw, onDownload }) {
  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const logins = [...new Set((rctis || []).map(r => (r.driverLogin || '').toUpperCase()).filter(Boolean))].sort();
  const blank = {
    login: '', driverLogin: '', entity: '', personalName: '', abn: '',
    amount: '', instalment: '', capPct: 40, advancedOn: todayISO(),
    firstPeriodEndISO: '', finalDueISO: '', reference: '', notes: '',
    label: 'Advance Repayment', acn: '', address: '', authorityOnFile: false,
  };
  const f = form || blank;
  const set = (k, v) => setForm({ ...f, [k]: v });
  // Which advance is being sent, and the purpose typed for it. This used to be a
  // window.prompt — wrong control twice over: some browsers suppress prompt() silently
  // (click Send, nothing happens, no error), and the purpose is a clause in a legal
  // document where an untrue answer is an event of default. It deserves a form that
  // shows what is about to be sent and gives room to write.
  const [sendFor, setSendFor] = useState(null);
  const [purpose, setPurpose] = useState('');
  const live = (advances || []).filter(a => a.status !== 'void');
  const voided = (advances || []).filter(a => a.status === 'void');
  const totalOut = r2(live.reduce((s, a) => s + (a.outstanding || 0), 0));
  // How many periods the remaining balance still needs, at the stated instalment.
  const periodsLeft = (a) => (a.instalment > 0 ? Math.ceil((a.outstanding || 0) / a.instalment) : null);

  const row = (a) => {
    const applied = Object.entries(a.applied || {}).sort(([x], [y]) => x.localeCompare(y));
    const left = periodsLeft(a);
    const ag = a.agreement || {};
    const overpaid = a.kind === 'overpayment';
    const authorised = overpaid || a.authorityOnFile === true || ag.status === 'signed';
    return (
      <div key={a.id} style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: '12px 14px', marginBottom: 10, opacity: a.status === 'void' ? 0.55 : 1 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'baseline', justifyContent: 'space-between' }}>
          <div>
            <b style={{ fontSize: '.95rem' }}>{a.entity || a.personalName || a.login}</b>
            <span style={{ color: 'var(--hp-dim)', fontSize: '.75rem', marginLeft: 8, fontFamily: 'var(--font-mono)' }}>
              deducts on {a.login}{a.driverLogin && a.driverLogin !== a.login ? ` · driver ${a.driverLogin}` : ''}
            </span>
            {a.status === 'settled' && <span style={{ marginLeft: 8, color: 'var(--hp-green, #2e7d32)', fontSize: '.72rem', fontWeight: 700 }}>✓ REPAID IN FULL</span>}
            {a.status === 'void' && <span style={{ marginLeft: 8, color: 'var(--hp-dim)', fontSize: '.72rem', fontWeight: 700 }}>VOID</span>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {overpaid && <span style={{ color: 'var(--hp-gold)', fontSize: '.72rem', fontWeight: 700, alignSelf: 'center' }}>OVERPAYMENT RECOVERY</span>}
            {!overpaid && ag.status !== 'signed' && a.status !== 'void' && (
              <button
                className="btn-sm btn-primary"
                onClick={() => { setSendFor(sendFor === a.id ? null : a.id); setPurpose(ag.purpose || ''); }}
                disabled={busy}
              >
                {ag.status === 'pending' ? 'Resend' : 'Send for signature'}
              </button>
            )}
            {ag.status === 'pending' && <button className="btn-sm" onClick={() => onWithdraw(a)} disabled={busy}>Withdraw</button>}
            {ag.status === 'signed' && <button className="btn-sm" onClick={() => onDownload(a)} disabled={busy}>Signed PDF</button>}
            <button className="btn-sm" onClick={() => setForm({ ...a, capPct: (a.capPct || 0) * 100 })} disabled={busy}>Edit</button>
            <button className="btn-sm" onClick={() => onVoid(a, a.status === 'void')} disabled={busy}>{a.status === 'void' ? 'Restore' : 'Void'}</button>
          </div>
        </div>

        {/* The authority line. Until this reads signed, approving an RCTI will refuse to
            book a deduction — there is no contractual right to take the money yet. */}
        <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 6, fontSize: '.78rem',
          background: authorised ? 'rgba(46,125,50,.10)' : 'rgba(200,0,0,.08)',
          border: `1px solid ${authorised ? 'rgba(46,125,50,.5)' : 'var(--hp-red)'}` }}>
          {overpaid
            ? <>✓ <b>Overpayment</b> — our money paid over the RCTI by mistake, not a loan. It comes off the next RCTI after GST and is stated on it as a correction.</>
            : a.authorityOnFile && ag.status !== 'signed'
            ? <>✓ <b>Authority on file</b> — signed on paper. Deductions permitted.</>
            : ag.status === 'signed'
              ? <>✓ <b>Agreement signed</b> {ag.signedAt ? `on ${ag.signedAt.slice(0, 10)}` : ''}{ag.signedName ? ` by ${ag.signedName}` : ''}. Deductions permitted.</>
              : ag.status === 'pending'
                ? <>⏳ <b>Awaiting signature</b> — sent {ag.sentAt ? ag.sentAt.slice(0, 10) : ''}. It's on his portal home screen. <b>No deduction can be booked until he signs.</b></>
                : ag.status === 'declined'
                  ? <>✗ <b>Declined</b>{ag.declineReason ? ` — "${ag.declineReason}"` : ''}. No deduction can be booked.</>
                  : ag.status === 'withdrawn'
                    ? <>↩ <b>Withdrawn.</b> Send it again when the terms are settled. No deduction can be booked.</>
                    : <>⚠ <b>Not sent.</b> There is no signed authority to deduct from this contractor — the subcontractor agreement only covers lost or damaged parcels. Send it for signature before you pay the money out.</>}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, marginTop: 8, fontSize: '.8rem' }}>
          <span>{overpaid ? 'Overpaid' : 'Advanced'} <b>{fmt(a.amount)}</b>{a.advancedOn ? ` on ${a.advancedOn}` : ''}</span>
          <span>{overpaid ? 'Recovered' : 'Repaid'} <b>{fmt(a.repaid)}</b></span>
          <span style={{ color: a.outstanding > 0 ? 'var(--hp-red)' : 'inherit' }}>Outstanding <b>{fmt(a.outstanding)}</b></span>
          <span>Instalment <b>{fmt(a.instalment)}</b>/period</span>
          {a.capPct > 0 && <span>Cap <b>{Math.round(a.capPct * 100)}%</b> of net payable</span>}
          {left != null && a.outstanding > 0 && <span>{left} period{left === 1 ? '' : 's'} to go</span>}
          {a.finalDueISO && <span>Due in full by <b>{a.finalDueISO}</b></span>}
        </div>
        {a.reference && <div style={{ marginTop: 6, fontSize: '.75rem', color: 'var(--hp-dim)' }}>{a.reference}</div>}

        {(a.agreementHistory || []).length > 0 && (
          <div style={{ marginTop: 8, fontSize: '.74rem', color: 'var(--hp-dim)' }}>
            {a.agreementHistory.map((h, i) => (
              <div key={h.id || i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '2px 0' }}>
                <span>
                  Superseded {h.supersededAt ? h.supersededAt.slice(0, 10) : ''} —{' '}
                  {h.status === 'signed'
                    ? `was signed ${h.signedAt ? h.signedAt.slice(0, 10) : ''}${h.signedName ? ` by ${h.signedName}` : ''}`
                    : h.status}
                  {h.supersededReason ? ` · ${h.supersededReason}` : ''}
                </span>
                {h.signedPath && (
                  <button className="btn-sm" style={{ padding: '1px 6px', fontSize: '.7rem' }}
                    onClick={() => onDownload(a, h.id)} disabled={busy}>PDF</button>
                )}
              </div>
            ))}
          </div>
        )}

        {sendFor === a.id && (
          <div style={{ marginTop: 12, border: '1px solid var(--hp-gold)', borderRadius: 8, padding: 14 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Send the advance agreement to {a.driverLogin}</div>
            <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)', marginBottom: 10 }}>
              It appears on his portal home screen. He reads the document, signs once for{' '}
              {a.entity || 'the company'} <b>and as personal guarantor</b>, and submits.
            </div>

            {/* Exactly what he is about to be asked to agree to — no surprises at the far end. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, padding: '10px 12px', borderRadius: 6, background: 'var(--hp-surface)', fontSize: '.78rem', marginBottom: 12 }}>
              <span>He receives <b>{fmt(a.amount)}</b></span>
              <span><b>{fmt(a.instalment)}</b> off each RCTI</span>
              <span>Interest &amp; fees <b>none</b></span>
              <span>First deduction <b>{a.firstPeriodEndISO || '—'}</b></span>
              <span>Repaid in full by <b>{a.finalDueISO || '—'}</b></span>
            </div>

            <div style={{ fontSize: '.78rem', fontWeight: 600, marginBottom: 4 }}>
              What did {a.personalName || 'he'} say the money is for?
            </div>
            <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginBottom: 6 }}>
              This becomes clause 3.1 — his own statement of purpose, in his words. It must be
              true: clause 9.1(d) makes a false statement here an event of default. There is no
              "business purposes" declaration to sign, so write what he actually told you.
            </div>
            <textarea
              value={purpose}
              onChange={e => setPurpose(e.target.value)}
              rows={3}
              placeholder="e.g. to assist the director with payments on a personal loan taken to meet visa and migration costs for himself and his family"
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'var(--hp-card)', color: 'var(--hp-text)', fontSize: '.82rem', boxSizing: 'border-box', fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
              <button
                className="btn-sm btn-primary"
                disabled={busy || !purpose.trim()}
                onClick={() => onSend(a, purpose.trim()).then(ok => { if (ok) { setSendFor(null); setPurpose(''); } })}
              >
                {busy ? 'Sending…' : 'Send to portal'}
              </button>
              <button className="btn-sm" onClick={() => setSendFor(null)} disabled={busy}>Cancel</button>
              {!purpose.trim() && <span style={{ fontSize: '.74rem', color: 'var(--hp-dim)' }}>A purpose is required.</span>}
            </div>
          </div>
        )}
        {applied.length > 0 && (
          <table style={{ width: '100%', marginTop: 10, fontSize: '.74rem', borderCollapse: 'collapse' }}>
            <tbody>
              {applied.map(([p, x]) => (
                <tr key={p}>
                  <td style={{ padding: '3px 6px', color: 'var(--hp-dim)' }}>Period ending {p}</td>
                  <td style={{ padding: '3px 6px', color: 'var(--hp-dim)' }}>{x.invoiceNumber || ''}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right' }}>{fmt(x.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        <div>
          <b>Advances</b>
          <span style={{ color: 'var(--hp-dim)', fontSize: '.8rem', marginLeft: 10 }}>
            {live.length} live · {fmt(totalOut)} outstanding
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-sm" onClick={onRefresh} disabled={busy}>Refresh</button>
          <button className="btn-sm btn-primary" onClick={() => setForm(blank)} disabled={busy}>+ New advance</button>
        </div>
      </div>

      <div style={{ background: 'var(--hp-surface)', borderLeft: '3px solid var(--hp-gold)', padding: '10px 12px', fontSize: '.76rem', color: 'var(--hp-dim)', marginBottom: 14, lineHeight: 1.55 }}>
        An advance is a <b>loan</b>, not a prepayment for parcels. The repayment comes off the
        RCTI <b>after</b> GST, so the invoice still states the full parcels delivered and the
        full GST on them — only the amount paid to the bank changes. The deduction is booked
        against the ledger when you <b>approve</b> the RCTI, and withdrawn if you un-approve,
        so a rebuild can never double-count it.
      </div>

      {form && (
        <div style={{ border: '1px solid var(--hp-gold)', borderRadius: 8, padding: 14, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>{f.id ? 'Edit advance' : 'New advance'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <label>Deduct on RCTI login
              <input list="adv-logins" value={f.login} onChange={e => set('login', e.target.value.toUpperCase())} placeholder="e.g. ALANAS" />
              <datalist id="adv-logins">{logins.map(l => <option key={l} value={l} />)}</datalist>
            </label>
            <label>Driver / personnel login
              <input value={f.driverLogin} onChange={e => set('driverLogin', e.target.value.toUpperCase())} placeholder="e.g. PARRACASA" />
            </label>
            <label>Entity<input value={f.entity} onChange={e => set('entity', e.target.value)} placeholder="ALANNAS DELIVERY PTY LTD" /></label>
            <label>Director / contact<input value={f.personalName} onChange={e => set('personalName', e.target.value)} /></label>
            <label>ABN<input value={f.abn} onChange={e => set('abn', e.target.value)} /></label>
            <label>Amount advanced<input type="number" step="0.01" value={f.amount} onChange={e => set('amount', e.target.value)} /></label>
            <label>Instalment per period<input type="number" step="0.01" value={f.instalment} onChange={e => set('instalment', e.target.value)} /></label>
            <label>Cap (% of net payable)<input type="number" step="1" value={f.capPct} onChange={e => set('capPct', e.target.value)} /></label>
            <label>Date advanced<input type="date" value={f.advancedOn} onChange={e => set('advancedOn', e.target.value)} /></label>
            <label>First period ending<input type="date" value={f.firstPeriodEndISO} onChange={e => set('firstPeriodEndISO', e.target.value)} /></label>
            <label>Repayable in full by<input type="date" value={f.finalDueISO} onChange={e => set('finalDueISO', e.target.value)} /></label>
            <label>Reference<input value={f.reference} onChange={e => set('reference', e.target.value)} placeholder="Advance and Repayment Deed, 1 Sep 2026" /></label>
            <label>Label on the RCTI<input value={f.label} onChange={e => set('label', e.target.value)} placeholder="Advance Repayment" /></label>
            <label>ACN<input value={f.acn} onChange={e => set('acn', e.target.value)} placeholder="695 654 534" /></label>
            <label>Registered address<input value={f.address} onChange={e => set('address', e.target.value)} placeholder="16 Cahill Street, Beverly Hills NSW 2209" /></label>
          </div>
          {f.amount > 0 && f.instalment > 0 && (
            <div style={{ marginTop: 10, fontSize: '.78rem', color: 'var(--hp-dim)' }}>
              {Math.ceil(f.amount / f.instalment)} instalments of {fmt(f.instalment)}
              {Math.ceil(f.amount / f.instalment) * f.instalment !== Number(f.amount)
                ? ` — the last one is ${fmt(r2(f.amount - (Math.ceil(f.amount / f.instalment) - 1) * f.instalment))}`
                : ''}
            </div>
          )}
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, fontSize: '.8rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!f.authorityOnFile} onChange={e => set('authorityOnFile', e.target.checked)} style={{ marginTop: 3 }} />
            <span>
              <b>Signed authority already on file</b> — tick only if the agreement was signed on paper.
              Without this, or a signed portal agreement, approving an RCTI will refuse to book a deduction.
            </span>
          </label>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn-sm btn-primary" onClick={() => onSave(f)} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
            <button className="btn-sm" onClick={() => setForm(null)} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}

      {live.length === 0 && !form && (
        <div style={{ color: 'var(--hp-dim)', fontSize: '.85rem', padding: '18px 0' }}>No advances on the books.</div>
      )}
      {live.map(row)}
      {voided.length > 0 && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: 'pointer', color: 'var(--hp-dim)', fontSize: '.8rem' }}>{voided.length} void</summary>
          <div style={{ marginTop: 10 }}>{voided.map(row)}</div>
        </details>
      )}
    </div>
  );
}

export default function RCTIPage() {
  const navigate = useNav();
  const { xeroConnected, setXeroConnected, xeroOrg, setXeroOrg, addNotification, sheetLoading, sheetData, rctis, setRctis, rctiDrivers: drivers, setRctiDrivers: setDrivers, activePeriodISO, setActivePeriodISO } = useApp();
  const XERO_FN = '/.netlify/functions/xero';
  const [showXeroSettings, setShowXeroSettings] = useState(false);
  const [xeroConfig, setXeroConfig] = useState(null);
  const [xeroAccounts, setXeroAccounts] = useState([]);
  const [savingConfig, setSavingConfig] = useState(false);
  // buildPeriodISO is now the shared active period across the app
  const buildPeriodISO    = activePeriodISO;
  const setBuildPeriodISO = setActivePeriodISO;
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [sending, setSending] = useState(null);
  const [emailLog, setEmailLog] = useState({});       // 'periodEndISO|LOGIN' → { invoiceNumber, to, at }
  const [xeroAttach, setXeroAttach] = useState({});   // RCTI reference → { found, hasAttachments } from Xero
  const [sendingAll, setSendingAll] = useState(false);
  // Email send mode: Test routes every RCTI email to paul@hollypaul.com; Live emails the
  // real contractors. Persisted per-browser and DEFAULTS TO TEST, so a fresh device is
  // never accidentally live. Flipping to Live is gated by a confirm in toggleSendMode.
  const [testMode, setTestMode] = useState(() => {
    try { return localStorage.getItem('hp-rcti-sendmode') !== 'live'; } catch { return true; }
  });
  const toggleSendMode = () => {
    if (testMode) {
      if (!window.confirm('Switch to LIVE send mode?\n\n"Send" and "Send All" will then email RCTIs to the REAL contractors — not your inbox. Only do this once you have reviewed them.')) return;
      setTestMode(false);
      try { localStorage.setItem('hp-rcti-sendmode', 'live'); } catch {}
      addNotification('⚠ LIVE send mode — emails now go to contractors', 'error');
    } else {
      setTestMode(true);
      try { localStorage.setItem('hp-rcti-sendmode', 'test'); } catch {}
      addNotification('Test send mode — emails go to paul@hollypaul.com only', 'info');
    }
  };
  const [expanded, setExpanded] = useState(null);
  const [activeTab, setActiveTab] = useState('register');
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [previewRCTI, setPreviewRCTI] = useState(null);
const [showManualBuilder, setShowManualBuilder] = useState(false);
const [manualDriversLoading, setManualDriversLoading] = useState(false);
  const [editingLines, setEditingLines] = useState({});
  const [editLI, setEditLI] = useState(null);   // { rctiId, idx, quantity, rate } — amending one inserted line
  const [editLease, setEditLease] = useState(null);   // { rctiId, days } — pro-rating the van lease fee
  const [historicalPeriods, setHistoricalPeriods] = useState([]);
  // periodEndISO → the archived (approved) RCTIs for that period, fetched on demand.
  const [archiveRows, setArchiveRows] = useState({});
  // periodEndISO → { LOGIN → entry } — RCTIs taken out of a run with "Remove from run".
  const [excludedRuns, setExcludedRuns] = useState({});
  // ?driver= deep link from the global search palette pre-fills the driver filter.
  const [searchParams] = useSearchParams();
  const driverParam = searchParams.get('driver') || '';
  const [driverFilter, setDriverFilter] = useState(driverParam);
  useEffect(() => { if (driverParam) setDriverFilter(driverParam); }, [driverParam]);
  const [photos, setPhotos] = useState({});
  // Advance ledger — loans paid ahead of the work, repaid by deduction from the RCTI
  // remittance (after GST). Loaded once; refreshed after any write.
  const [advances, setAdvances] = useState([]);
  const [advForm, setAdvForm] = useState(null);   // null = closed; {} = new; {...} = editing
  const [advBusy, setAdvBusy] = useState(false);
  const loadAdvances = React.useCallback(async () => {
    try {
      const res = await fetch(ADVANCES_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', body: JSON.stringify({ action: 'listAdvances' }),
      });
      const d = await res.json();
      if (d.success) setAdvances(d.advances || []);
    } catch (err) { console.warn('Advance ledger unavailable:', err.message); }
  }, []);
  React.useEffect(() => { loadAdvances(); }, [loadAdvances]);
  const advPost = async (body) => {
    const res = await fetch(ADVANCES_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin', body: JSON.stringify(body),
    });
    return res.json();
  };
  const saveAdvance = async (f) => {
    if (!f.login) { addNotification('Which RCTI login should the deduction come off?', 'error'); return; }
    if (!(Number(f.amount) > 0)) { addNotification('Enter the amount advanced', 'error'); return; }
    setAdvBusy(true);
    try {
      // capPct is entered as a percentage and stored as a fraction.
      const d = await advPost({ action: 'saveAdvance', advance: { ...f, capPct: (Number(f.capPct) || 0) / 100 } });
      if (!d.success) { addNotification(`Advance: ${d.error}`, 'error'); return; }
      await loadAdvances();
      setAdvForm(null);
      addNotification(`Advance for ${d.advance.entity || d.advance.login} saved — deducts on ${d.advance.login}`, 'success');
    } catch (err) { addNotification(`Advance: ${err.message}`, 'error'); }
    finally { setAdvBusy(false); }
  };
  // Push the agreement to the driver's portal. The purpose is asked for here rather than
  // stored on the advance because it is a statement the CONTRACTOR makes — clause 3.1
  // records what he told us, and it has to be in his words, not a tick-box.
  // Returns true when it actually sent, so the panel knows whether to close its form.
  const sendAdvanceAgreement = async (a, purpose) => {
    if (!purpose) { addNotification('A purpose is required before the agreement can be sent', 'error'); return false; }
    setAdvBusy(true);
    try {
      const d = await advPost({
        action: 'sendAgreement', advanceId: a.id, purpose,
        acn: a.acn || '', address: a.address || '',
        bankLine: a.bankLine || '',
      });
      if (!d.success) { addNotification(`Advance agreement: ${d.error}`, 'error'); return false; }
      await loadAdvances();
      addNotification(`Agreement sent to ${a.driverLogin} — it's on his portal home screen now`, 'success');
      return true;
    } catch (err) { addNotification(`Advance agreement: ${err.message}`, 'error'); return false; }
    finally { setAdvBusy(false); }
  };
  const withdrawAdvanceAgreement = async (a) => {
    if (!window.confirm(`Withdraw the agreement sent to ${a.driverLogin}?\n\nIt disappears from his portal and he can no longer sign it.`)) return;
    setAdvBusy(true);
    try {
      const d = await advPost({ action: 'withdrawAgreement', advanceId: a.id });
      if (!d.success) { addNotification(`Advance agreement: ${d.error}`, 'error'); return; }
      await loadAdvances();
      addNotification('Agreement withdrawn', 'success');
    } catch (err) { addNotification(`Advance agreement: ${err.message}`, 'error'); }
    finally { setAdvBusy(false); }
  };
  const downloadSignedAdvance = async (a, agreementId) => {
    setAdvBusy(true);
    try {
      const d = await advPost({ action: 'getSignedAdvancePdf', advanceId: a.id, agreementId: agreementId || '' });
      if (!d.success) { addNotification(`Signed PDF: ${d.error}`, 'error'); return; }
      const bin = atob(d.pdfBase64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url; link.download = d.filename || 'Signed-Advance-Agreement.pdf';
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { addNotification(`Signed PDF: ${err.message}`, 'error'); }
    finally { setAdvBusy(false); }
  };
  const voidAdvance = async (a, restore) => {
    if (!restore && !window.confirm(`Void the ${fmt(a.amount)} advance for ${a.entity || a.login}?\n\nDeductions already recorded are kept — this only stops future ones.`)) return;
    setAdvBusy(true);
    try {
      const d = await advPost({ action: 'voidAdvance', advanceId: a.id, restore: !!restore });
      if (!d.success) { addNotification(`Advance: ${d.error}`, 'error'); return; }
      await loadAdvances();
      addNotification(restore ? 'Advance restored' : 'Advance voided — no further deductions', 'success');
    } catch (err) { addNotification(`Advance: ${err.message}`, 'error'); }
    finally { setAdvBusy(false); }
  };

  React.useEffect(() => {
    const logins = [...new Set((rctis || []).map(x => (x.driverLogin || '').toUpperCase()).filter(Boolean))];
    if (logins.length) fetchDriverPhotos(logins).then(p => setPhotos(prev => ({ ...p, ...prev })));
  }, [rctis]);
  
  // Where the Pay Period list comes from. This used to read Apps Script getGCCDHistory —
  // an action the deployed script no longer has, so `records` was always undefined and the
  // list silently fell back to whatever this browser's localStorage happened to hold. A
  // period built on another machine, or lost to a quota-failed cache write, simply stopped
  // existing on the page (12 Jul–8 Aug 2026, found missing 8 Sep 2026). rcti_archive is the
  // server's record of what was built, so it is the source of the list now.
  React.useEffect(() => {
    fetch('/.netlify/functions/appsscript', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin',
      body: JSON.stringify({ action: 'listRctiPeriods' }),
    })
      .then(r => r.json())
      .then(d => {
        if (!d.success) return;
        setHistoricalPeriods((d.periods || [])
          .map(p => p.periodLabel || isoToPeriodLabel(p.periodEndISO))
          .filter(Boolean));
      })
      .catch(() => {});
  }, []);

  // Selecting a period this browser holds no built rows for pulls that period's archived
  // RCTIs, so the page shows what actually went out instead of an empty table.
  React.useEffect(() => {
    if (!selectedPeriod || selectedPeriod === 'all') return;
    const endISO = labelToEndISO(selectedPeriod);
    if (!endISO || archiveRows[endISO] !== undefined) return;
    // Built in this session? The live rows are authoritative — leave them alone.
    // A correction row (one archived RCTI opened by Correct) isn't a build — the rest of the
    // period still comes from the archive.
    if (rctis.some(r => !r.isManual && !r.isCorrection && (r.periodEndISO === endISO || r.periodLabel === selectedPeriod))) return;
    fetch('/.netlify/functions/appsscript', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin',
      body: JSON.stringify({ action: 'getRctiPeriodArchive', periodEndISO: endISO }),
    })
      .then(r => r.json())
      // Cache the miss too (as []), otherwise a period with nothing archived re-fetches forever.
      .then(d => setArchiveRows(prev => ({ ...prev, [endISO]: (d.success && d.rctis) || [] })))
      .catch(() => setArchiveRows(prev => ({ ...prev, [endISO]: [] })));
  }, [selectedPeriod, rctis, archiveRows]);

  // ── Xero connection status + config ────────────────────────────────────────
  // A connection made before the payroll scopes existed still reports connected — it just
  // can't touch timesheets. Track that separately so the night-sort push can prompt for a
  // re-authorise instead of the user having to disconnect (which would stop RCTI pushes).
  const [xeroPayrollReady, setXeroPayrollReady] = React.useState(true);
  const refreshXeroStatus = React.useCallback(() => {
    fetch(`${XERO_FN}?action=status`).then(r => r.json())
      .then(d => {
        setXeroConnected(!!d.connected); setXeroOrg(d.org || null);
        setXeroPayrollReady(!d.connected || !!d.payrollReady);
      })
      .catch(() => {});
  }, [setXeroConnected, setXeroOrg]);

  // Pull the Xero chart of accounts (read-only) to populate the mapping dropdowns
  React.useEffect(() => {
    if (!xeroConnected) { setXeroAccounts([]); return; }
    fetch(`${XERO_FN}?action=accounts`).then(r => r.json())
      .then(d => setXeroAccounts(Array.isArray(d.accounts) ? d.accounts : []))
      .catch(() => {});
  }, [xeroConnected]);

  React.useEffect(() => {
    refreshXeroStatus();
    fetch(`${XERO_FN}?action=get-config`).then(r => r.json()).then(setXeroConfig).catch(() => {});
    // Handle OAuth redirect back from Xero
    const params = new URLSearchParams(window.location.search);
    if (params.get('xero') === 'connected') { addNotification('Xero connected ✓', 'success'); refreshXeroStatus(); window.history.replaceState({}, '', window.location.pathname); }
    else if (params.get('xero') === 'error') { const reason = params.get('reason'); addNotification(reason ? `Xero connection failed: ${reason}` : 'Xero connection failed — please try again', 'error'); window.history.replaceState({}, '', window.location.pathname); }
  }, [refreshXeroStatus, addNotification]);

  const connectXero = async () => {
    try { const d = await (await fetch(`${XERO_FN}?action=auth`)).json(); if (d.authUrl) window.location.href = d.authUrl; }
    catch { addNotification('Could not start Xero connection', 'error'); }
  };
  const disconnectXero = async () => {
    await fetch(`${XERO_FN}?action=disconnect`).catch(() => {});
    setXeroConnected(false); setXeroOrg(null); addNotification('Xero disconnected', 'info');
  };
  const saveXeroConfig = async (next) => {
    setSavingConfig(true); setXeroConfig(next);
    try { await fetch(`${XERO_FN}?action=save-config`, { method: 'POST', body: JSON.stringify(next) }); addNotification('Xero settings saved', 'success'); }
    catch { addNotification('Could not save Xero settings', 'error'); }
    finally { setSavingConfig(false); }
  };
  // The exact subset of an RCTI the driver-portal archive stores — single source for the
  // Build-time bulk archive AND the per-row publish on Approve.
  const portalArchiveShape = (r) => ({
    driverLogin: r.driverLogin, invoiceNumber: r.invoiceNumber, periodLabel: r.periodLabel,
    periodEndISO: r.periodEndISO, companyName: r.companyName, personalName: r.personalName,
    abn: r.abn, address: r.address, phone: r.phone, bsb: r.bsb, accNumber: r.accNumber,
    email: r.email, isNetwork: r.isNetwork || false, lineItems: r.lineItems,
    grossIncome: r.grossIncome, subtotal: r.subtotal, gst: r.gst, total: r.total,
    totalFormatted: r.totalFormatted, deductions: r.deductions, gccd: r.gccd, gstRegistered: r.gstRegistered,
    // The driver's own copy must show the deduction and what actually hit his bank —
    // otherwise his portal PDF states a total he was never paid.
    advance: r.advance || null,
  });
  // Keep the driver portal in step with the Approve flag OUTSIDE a full Build: approving
  // publishes the row, un-approving withdraws it. The Build-time archive only runs during
  // "Build RCTIs from Sheets", so an un-approve → rebuild → re-approve cycle used to leave
  // that RCTI missing from the portal until the NEXT build (bit RCTI-1220, 14 Aug 2026).
  // Manual/History rows never go to the portal archive (mirrors the Build-time filter).
  const syncPortalArchive = async (rcti, approving) => {
    if (rcti.isManual || rcti.isHistorical || !rcti.invoiceNumber || rcti.invoiceNumber === '—' || !rcti.periodEndISO) return;
    try {
      const body = approving
        ? { action: 'archiveRctiOne', rcti: portalArchiveShape(rcti) }
        : { action: 'removeRctiOne', login: rcti.driverLogin, periodEndISO: rcti.periodEndISO };
      const res = await fetch('/.netlify/functions/appsscript', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin', body: JSON.stringify(body) });
      const d = await res.json();
      if (!d.success) addNotification(`Portal archive ${approving ? 'publish' : 'withdraw'} failed for ${rcti.invoiceNumber}: ${d.error || 'unknown error'}`, 'error');
    } catch (err) {
      addNotification(`Portal archive ${approving ? 'publish' : 'withdraw'} failed for ${rcti.invoiceNumber}: ${err.message}`, 'error');
    }
  };
  // Record (or withdraw) an advance repayment against the ledger. Approve is the lock —
  // the same trigger that publishes/withdraws the portal archive — so it is also the point
  // at which a deduction becomes a fact. Writing at BUILD time instead would book a
  // repayment on every rebuild and against RCTIs Paul never approved. Idempotent per
  // period, so an un-approve → re-approve cycle nets to one deduction.
  const syncAdvanceLedger = async (rcti, approving) => {
    const adv = rcti.advance;
    if (!adv || !adv.id || !rcti.periodEndISO) return;
    if (approving && !(adv.amount > 0)) return;
    try {
      const res = await fetch(ADVANCES_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin',
        body: JSON.stringify(approving
          ? { action: 'applyAdvance', advanceId: adv.id, periodEndISO: rcti.periodEndISO, amount: adv.amount, invoiceNumber: rcti.invoiceNumber }
          : { action: 'unapplyAdvance', advanceId: adv.id, periodEndISO: rcti.periodEndISO }),
      });
      const d = await res.json();
      if (!d.success) { addNotification(`Advance ledger (${rcti.invoiceNumber}): ${d.error || 'failed'}`, 'error'); return; }
      await loadAdvances();
      if (approving) {
        addNotification(`${rcti.invoiceNumber}: ${fmt(adv.amount)} advance repayment recorded — ${fmt(adv.balanceAfter)} outstanding`, 'success');
      }
    } catch (err) {
      addNotification(`Advance ledger (${rcti.invoiceNumber}): ${err.message}`, 'error');
    }
  };
  // Manual RCTIs used to reach the page only inside a build, so raising or approving one
  // meant pressing Build first — a full rebuild of the period, the one step that can move
  // RCTIs already approved and in Xero. Load them on their own instead.
  // The store is the record of a manual RCTI, so a copy this browser cached earlier is
  // refreshed from it — RCTI-1299 was moved to another period after it was raised, and the
  // page kept showing (and would have pushed) the old one. Approved or pushed rows are the
  // exception: like a built RCTI, Approve freezes what is on screen.
  React.useEffect(() => {
    fetch(MANUAL_RCTI_URL, { method: 'POST', body: JSON.stringify({ action: 'loadManualRCTIs' }) })
      .then(r => r.json())
      .then(d => {
        if (!d.success) return;
        const manuals = (d.rctis || []).filter(r => !r.isModification && /^MANUAL-/i.test(r.id || ''));
        const stored = new Map(manuals.map(m => [m.id, m]));
        setRctis(prev => {
          let changed = false;
          const next = prev.map(r => {
            const m = r.isManual && stored.get(r.id);
            if (!m || r.approved || r.xeroStatus === 'synced') return r;
            if (JSON.stringify(m) === JSON.stringify(r)) return r;
            changed = true;
            return carryManualFlags(m, r);
          });
          const have = new Set(prev.map(r => r.id));
          const add = manuals.filter(m => !have.has(m.id));
          return (changed || add.length) ? [...next, ...add] : prev;
        });
      })
      .catch(() => {});
  }, [setRctis]);

  // The builder's contractor list, without a build: the same read-only loader a build uses.
  // One attempt per opening of the builder, so a failed load doesn't retry in a loop.
  const manualDriversTried = React.useRef(false);
  React.useEffect(() => {
    if (!showManualBuilder) { manualDriversTried.current = false; return; }
    if (drivers.length || manualDriversTried.current) return;
    manualDriversTried.current = true;
    setManualDriversLoading(true);
    import('../services/googleSheets')
      .then(({ fetchAllSheetData }) => fetchAllSheetData(activePeriodISO))
      .then(data => setDrivers(data.drivers || []))
      .catch(err => addNotification(`Couldn't load contractors: ${err.message}`, 'error'))
      .finally(() => setManualDriversLoading(false));
  }, [showManualBuilder, drivers.length, activePeriodISO, setDrivers, addNotification]);

  const toggleApprove = (rcti) => {
    const approving = !rcti.approved;
    setRctis(prev => prev.map(r => r.id === rcti.id ? { ...r, approved: approving } : r));
    syncPortalArchive(rcti, approving);
    syncAdvanceLedger(rcti, approving);
  };

const handleGenerateManual = async (rcti) => {
    setRctis(prev => [...prev, rcti]);
    setShowManualBuilder(false);
    setActiveTab('register');
    addNotification(`${rcti.invoiceNumber} created — saving...`, 'info');
    try {
      const res = await fetch(MANUAL_RCTI_URL, {
        method: 'POST',
        body: JSON.stringify({ action: 'saveManualRCTI', rcti }),
      });
      const data = await res.json();
      if (data.success) {
        addNotification(`${rcti.invoiceNumber} saved permanently`, 'success');
      } else {
        addNotification(`Warning: RCTI created but not saved — ${data.error}`, 'error');
      }
    } catch (err) {
      addNotification(`Warning: RCTI created but not saved — ${err.message}`, 'error');
    }
  };

  const deleteManual = async (rcti) => {
    if (!window.confirm(`Delete ${rcti.invoiceNumber} — ${rcti.companyName || rcti.personalName || rcti.driverLogin} (${rcti.totalFormatted || fmt(rcti.total)})? This removes it permanently.`)) return;
    try {
      const res = await fetch(MANUAL_RCTI_URL, { method: 'POST', body: JSON.stringify({ action: 'deleteManualRCTI', id: rcti.id }) });
      const data = await res.json();
      if (data.success) {
        setRctis(prev => prev.filter(r => r.id !== rcti.id));
        addNotification(`${rcti.invoiceNumber} deleted`, 'success');
      } else {
        addNotification(`Delete failed: ${data.error || 'unknown error'}`, 'error');
      }
    } catch (e) { addNotification(`Error: ${e.message}`, 'error'); }
  };

  // ── Remove from run / Restore ───────────────────────────────────────────────
  // One RCTI out of a period's run: off the register and the driver's portal, and left out
  // of every rebuild of that period. Nothing else is rebuilt or un-approved. The row is
  // snapshotted server-side first, so Restore puts back exactly what was there.
  const loadExcluded = React.useCallback(() => {
    fetch(RCTI_ARCHIVE_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin', body: JSON.stringify({ action: 'listExcluded' }) })
      .then(r => r.json())
      .then(d => { if (d.success) setExcludedRuns(d.excluded || {}); })
      .catch(() => {});
  }, []);
  React.useEffect(() => { loadExcluded(); }, [loadExcluded]);

  const removeFromRun = async (rcti) => {
    const login = (rcti.driverLogin || rcti.driverName || '').toUpperCase();
    const periodEndISO = rcti.periodEndISO || labelToEndISO(rcti.periodLabel);
    if (!login || !periodEndISO) { addNotification('Can\'t tell which login/period this RCTI belongs to', 'error'); return; }
    const who = rcti.companyName || rcti.personalName || login;
    const inXero = rcti.xeroStatus === 'synced' || !!rcti.xeroInvoiceId;
    const msg = `Remove ${rcti.invoiceNumber} — ${who} (${rcti.totalFormatted || fmt(rcti.total)}) from the ${rcti.periodLabel || periodEndISO} run?\n\n`
      + `Only this RCTI changes: it comes off the register and ${login}'s portal, and rebuilds of this period leave ${login} out. `
      + `No other RCTI is rebuilt or un-approved.`
      + (inXero ? `\n\nIt is already in Xero — the app doesn't touch Xero, so void bill ${rcti.invoiceNumber} there if you want it gone.` : '')
      + `\n\nYou can put it back with Restore under "Removed from this run".`;
    if (!window.confirm(msg)) return;
    const { isHistorical, ...snapshot } = rcti;
    try {
      const res = await fetch(RCTI_ARCHIVE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin',
        body: JSON.stringify({ action: 'removeFromRun', login, periodEndISO, rcti: isHistorical ? null : snapshot }),
      });
      const d = await res.json();
      if (!d.success) { addNotification(`Remove failed: ${d.error || 'unknown error'}`, 'error'); return; }
      setRctis(prev => prev.filter(r => r.id !== rcti.id));
      setArchiveRows(prev => (prev[periodEndISO]
        ? { ...prev, [periodEndISO]: prev[periodEndISO].filter(r => (r.driverLogin || '').toUpperCase() !== login) }
        : prev));
      setExcludedRuns(prev => ({ ...prev, [periodEndISO]: { ...(prev[periodEndISO] || {}), [login]: d.entry } }));
      // An approved row may have booked an advance repayment — release it with the row.
      if (rcti.advance && (rcti.approved || isHistorical)) syncAdvanceLedger(rcti, false);
      addNotification(`${rcti.invoiceNumber} (${login}) removed from the run${inXero ? ' — void the bill in Xero' : ''}`, 'success');
    } catch (err) { addNotification(`Remove failed: ${err.message}`, 'error'); }
  };

  const restoreToRun = async (entry) => {
    if (!window.confirm(`Put ${entry.invoiceNumber || entry.login} — ${entry.name || entry.login} back into the run?`)) return;
    try {
      const res = await fetch(RCTI_ARCHIVE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin',
        body: JSON.stringify({ action: 'restoreToRun', login: entry.login, periodEndISO: entry.periodEndISO }),
      });
      const d = await res.json();
      if (!d.success) { addNotification(`Restore failed: ${d.error || 'unknown error'}`, 'error'); return; }
      const p = entry.periodEndISO;
      const row = entry.rcti;
      // Back onto the page only where this browser holds the period's built rows; otherwise
      // the archive view re-reads the (re-published) archive.
      if (row && rctis.some(r => !r.isManual && !r.isCorrection && r.periodEndISO === p)) {
        setRctis(prev => [...prev.filter(r => r.id !== row.id), row]);
        if (row.advance && row.approved) syncAdvanceLedger(row, true);
      }
      setArchiveRows(prev => { const n = { ...prev }; delete n[p]; return n; });
      setExcludedRuns(prev => { const n = { ...prev, [p]: { ...(prev[p] || {}) } }; delete n[p][entry.login]; return n; });
      addNotification(`${entry.invoiceNumber || entry.login} restored to the run`, 'success');
    } catch (err) { addNotification(`Restore failed: ${err.message}`, 'error'); }
  };

  const buildRCTI = (d, freshData, invoiceNum) => {
    const key = d.loginName.toUpperCase();
    // A network member who left (or joined) mid-period bills only the days outside their
    // membership window — the rest already rolled into the principal's RCTI. quantities[]
    // still holds their whole-period total, which would double-count those parcels.
    const ownQty = (freshData.networkMemberOwnQuantities || {})[key];
    const qty = ownQty || freshData.quantities[key] || { Parcel: 0, Exp: 0, 'Depot Bags': 0, DOE: 0 };
    // Started after this period opened → only part of it is his, so his fixed lease is
    // pro-rated below. A blank commencement date means "already here", as everywhere else.
    const startedMidPeriod = (() => {
      const c = parseCommencement(d.commencementDate);
      if (!c) return false;
      const s = new Date(freshData.controls.reportEndDate);
      s.setDate(s.getDate() - 27);
      return c > s;
    })();
    const gccd = freshData.gccdSummary[key] || {};
    const hourlyInputRecord = (freshData.hourlyInput || []).find(r => (r.Login || '').toUpperCase() === key && r.Period === freshData.controls.reportEndDate);
    const agreedHoursRaw = hourlyInputRecord ? parseFloat(hourlyInputRecord['Agreed Hours']) : null;
    const agreedHours = agreedHoursRaw != null && agreedHoursRaw > 0 ? agreedHoursRaw : null;
    const adj = freshData.adjustments[key] || {};
    const c = freshData.controls;
    const end = new Date(c.reportEndDate);
    const start = new Date(end);
    start.setDate(end.getDate() - 27);
    // Dated rates (rate-activations-sb). A driver who has been activated bills the set
    // pinned to THIS period's start, so rebuilding a closed period reproduces what was
    // issued rather than re-pricing it on today's rate — this page always rebuilds from
    // live data and never reads the archive, so without this an old rate change is
    // retrospective. No activation → the live sheet + gccd_rates, exactly as before, which
    // is why every un-activated driver is unaffected.
    const act = activationFor((freshData.rateActivations || {})[key], dateToISO(start));
    const rates = act ? (act.rates || {}) : (freshData.ratesMap[d.loginName] || freshData.ratesMap[key] || {});
    const hourlyRate = act ? (act.minHourlyRate || 0) : ((freshData.gccdRates[key] || {}).minHourlyRate || 0);
    // Dated GST (gst-status-sb) — the same argument as the rates above, for the same
    // reason: flipping a driver's GST flag would otherwise re-price every rebuilt earlier
    // period by 10%. No dated status → the live drivers.gst_registered flag, as before.
    const gstDated = gstStatusFor((freshData.gstStatus || {})[key], dateToISO(start));
    const gstReg = gstDated ? gstDated.gstRegistered !== false : d.gstRegistered !== false;
    // Recurring per-driver service fees from the Rates tab (any non-standard rate
    // key, e.g. "DoE Newtown Collection Tue - Thu"). Qty defaults to the count of
    // the named weekdays in the period; editable per RCTI via amend.
    const extraFeeLines = Object.keys(rates)
      .filter(t => !STD_RATE_TYPES.has(t) && (Number(rates[t]) || 0) > 0)
      .map(t => {
        const r = Number(rates[t]) || 0;
        const q = weekdayCountFromDesc(t, start, end);
        return { type: 'extra', description: t, quantity: q, rate: r, amount: Math.round(q * r * 100) / 100 };
      });
    const extraTotal = extraFeeLines.reduce((s, l) => s + l.amount, 0);
    const parcelAmt = (qty.Parcel || 0) * (rates['Parcel'] || 0);
    const expAmt = (qty.Exp || 0) * (rates['Exp'] || 0);
    const doeAmt = (qty.DOE || 0) * (rates['DOE'] || 0);
    const depotAmt = (qty['Depot Bags'] || 0) * (rates['Depot Bags'] || 0);
    const actual = parcelAmt + expAmt + doeAmt + depotAmt;
    const minPayable = agreedHours !== null && hourlyRate > 0 ? Math.round(agreedHours * hourlyRate * 100) / 100 : gccd.minPayable || 0;
    const variance = actual - minPayable;
    // A CONFIRMED Variable Uplift Rate from the Uplift Workbench overrides the auto
    // top-up for this driver. The workbench computes the GCCD minimum off the correct
    // hours basis (agreed/scanner); the auto path here uses the stale gccdSummary and
    // can miss a shortfall entirely (e.g. GALLEGUIL showed 0 hours → "meets min").
    // A confirmed uplift is the operator's explicit, audited decision, so it wins.
    // It REPLACES the auto levy (never adds) → no double-pay. Pending uplifts are
    // ignored until confirmed in the workbench.
    const autoLevy = variance < 0 ? Math.abs(variance) : 0;
    const confirmedUplift = (freshData.confirmedUplifts || {})[key] || 0;
    const gccdLevy = confirmedUplift > 0 ? confirmedUplift : autoLevy;
    const trainingRecord = (freshData.training || []).find(r => (r.LOGIN || '').toUpperCase() === key && r.Period === freshData.controls.reportEndDate);
    const trainingHours = trainingRecord ? parseFloat(trainingRecord.Hours) || 0 : 0;
    const trainingRate = trainingRecord ? parseFloat(trainingRecord.Rate) || 31 : 31;
    const trainingAmt = Math.round(trainingHours * trainingRate * 100) / 100;
    // Only apply fuel confirmed FOR THIS billing period — RCTI_Adjustments holds
    // one row per driver, so otherwise last period's fuel carries forward.
    const fuelForPeriod = adj.periodEnd && toISO(adj.periodEnd) === toISO(c.reportEndDate);
    const fuelLevy = fuelForPeriod ? (adj.fuelLevy || 0) : 0;
    const catchUpLines = fuelCatchUpLines(adj, c.reportEndDate);
    const catchUpAmt = catchUpLines.reduce((s, li) => s + li.amount, 0);
    const opAdj = fuelLevy + catchUpAmt + (adj.tempFuel || 0) + (adj.adHoc || 0);
    const gross = actual + gccdLevy + opAdj + trainingAmt + extraTotal;
    const leaseRate = d.leaseRate || 0;
    const leaseCap = d.leaseCap || 0;
    const fixedFee = d.leaseFee || 0;
    let leaseFee = 0;
    // %-lease is charged on delivery work only (Parcels + Express + DoE + Depot Bags) —
    // not on uplifts, fuel, training or extra fees. Mirrors LEASE_BASE_TYPES in
    // recomputeRctiTotals.
    const leaseBase = parcelAmt + expAmt + doeAmt + depotAmt;
    // Round the % lease to the cent HERE (mirrors recomputeRctiTotals) — a half-cent
    // raw fee (e.g. 15% × $3,061.30 = $459.195) otherwise rounds one way in the stored
    // subtotal and the other way in the Xero line, and the push reconcile guard trips.
    if (leaseRate > 0) { const raw = leaseBase * leaseRate; leaseFee = Math.round((leaseCap > 0 ? Math.min(raw, leaseCap) : raw) * 100) / 100; }
    // A driver who was only with us for PART of the period pays lease for the days they
    // actually worked, not the whole 4 weeks — otherwise a few days' work is swallowed by a
    // full period's lease. Two ways that happens: a network member who left mid-period
    // (VALBUENAS, 3 Jul) and a starter who joined mid-period (PAZMINOCM, 15 Jun). Everyone
    // who was here for the whole period pays in full, however many days they happened to
    // deliver — the van is leased for the period, not per drop. (A % lease already scales
    // with gross, so it needs no pro-rata.)
    else if (fixedFee > 0) {
      const days = ownQty ? (ownQty.daysBilled || 0)
        : (startedMidPeriod ? ((freshData.billedDays || {})[key] || 0) : null);
      leaseFee = days == null
        ? fixedFee
        : Math.round(fixedFee * (Math.min(days, LEASE_DAYS_PER_PERIOD) / LEASE_DAYS_PER_PERIOD) * 100) / 100;
    }
const subtotal = Math.round((gross - leaseFee) * 100) / 100;
const gst = gstReg ? Math.round(subtotal * 0.1 * 100) / 100 : 0;
const total = Math.round((subtotal + gst) * 100) / 100;
    // An hourly driver can be owed his ENTIRE RCTI as the GCCD top-up: with no per-unit
    // rate on the Rates tab `actual` is 0, so this guard dropped him from the build
    // altogether — no invoice and no warning, which reads as "nothing to pay" rather than
    // "priced at nothing" (VELASQUEZJ, 8-Aug-2026: 17.69 agreed hours, $726 owed, no row
    // produced). The levy only rescues a zero-earnings driver when it is backed by a
    // DELIBERATE entry — agreed hours set for THIS period on the Hourly Drivers page, or a
    // confirmed uplift from the workbench. The gccdSummary fallback is deliberately not
    // enough: that tab stopped being maintained after 9 May 2026 (see minRate below), and
    // a stale minimum on a dormant login must never grow an RCTI by itself.
    const floorBacked = (agreedHours !== null && hourlyRate > 0) || confirmedUplift > 0;
    if (actual === 0 && trainingAmt === 0 && opAdj === 0 && extraTotal === 0
        && !(floorBacked && gccdLevy > 0)) return null;
    const fmtDate = d2 => d2.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    const periodLabel = `${fmtDate(start)} to ${fmtDate(end)}`;
    return {
      id: `${d.loginName}|${c.reportEndDate}`,
      invoiceNumber: `RCTI-${invoiceNum}`,
      driverLogin: d.loginName,
      periodEndISO: toISO(c.reportEndDate),
      driverName: d.loginName, personalName: d.name, companyName: d.companyName,
      abn: d.abn, address: d.address, phone: d.phone, email: d.email,
      bsb: d.bsb, accNumber: d.accNumber, leaseLabel: d.leaseLabel,
      periodLabel, reportEndDate: c.reportEndDate,
      status: 'Not Posted', xeroStatus: 'pending', xeroInvoiceId: null,
      emailStatus: 'unsent',
      lineItems: [
        { type: 'parcel', description: 'Service Fee - Parcel Delivery Services - Route', quantity: qty.Parcel || 0, rate: rates['Parcel'] || 0, amount: parcelAmt },
        { type: 'express', description: 'Service Fee - 2nd Wave Express Post', quantity: qty.Exp || 0, rate: rates['Exp'] || 0, amount: expAmt },
        { type: 'doe', description: 'Service Fee - Dept Of Education', quantity: qty.DOE || 0, rate: rates['DOE'] || 0, amount: doeAmt },
        { type: 'depot', description: 'Service Fee - Depot Bags', quantity: qty['Depot Bags'] || 0, rate: rates['Depot Bags'] || 0, amount: depotAmt },
        ...(gccdLevy > 0 ? [{ type: 'gccd_uplift', description: 'Service Fee Adjustment - Route Conditions', quantity: qty.Parcel || 1, rate: qty.Parcel > 0 ? gccdLevy / qty.Parcel : gccdLevy, upliftRate: qty.Parcel > 0 ? gccdLevy / qty.Parcel : gccdLevy, amount: gccdLevy, isUplift: true }] : []),
        ...(fuelLevy > 0 ? [{ type: 'fuel_levy', description: 'Service Fee — Prevailing Conditions', quantity: 1, rate: fuelLevy, amount: fuelLevy, isFuelLevy: true }] : []),
        ...catchUpLines,
        ...(trainingAmt > 0 ? [{ type: 'training', description: 'Route Familiarisation Fee', quantity: trainingHours, rate: trainingRate, amount: trainingAmt }] : []),
        ...extraFeeLines,
      ],
      gstRegistered: gstReg,
      route: d.route || '',
      // minRate is the rate that actually priced minPayable just above, NOT the one off
      // GCCD_Summary: that tab stopped being maintained after the week of 9 May 2026, so
      // for any period since, `gccd` is empty and it printed $0 next to a real minimum.
      // Same shape the depot/team-leader branch below already uses. Where there are no
      // agreed hours the minimum still comes off the tab, so the tab's rate is the one
      // that reconciles and it wins.
      gccd: { totalHours: agreedHours !== null ? agreedHours : (gccd.totalHours || 0), minRate: (agreedHours !== null && hourlyRate > 0) ? hourlyRate : (gccd.minRate || hourlyRate || 0), minPayable, actualEarnings: actual, variance, gccdLevy, meetsMinimum: gccdLevy === 0 },
      deductions: { leaseFee, leaseRate, leaseCap, fixedLeaseFee: fixedFee, leaseLabel: d.leaseLabel || 'Van Lease Fee' },
      grossIncome: gross, subtotal, gst, total,
      totalFormatted: fmt(total),
    };
  };

  const writeGCCDHistory = async (built, period) => {
    try {
      const records = built.map(r => ({
        period, driver: r.driverName, company: r.companyName || r.personalName || '',
        actualEarnings: r.gccd.actualEarnings || 0, minPayable: r.gccd.minPayable || 0,
        variance: r.gccd.variance || 0, topUp: r.gccd.gccdLevy || 0,
        status: r.gccd.meetsMinimum ? 'Meets Minimum' : 'Top Up Applied',
        grossIncome: r.grossIncome || 0, leaseFee: r.deductions.leaseFee || 0, netPayable: r.subtotal || 0,
      }));
      await fetch(APPS_SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'gccd', records }) });
    } catch (err) { console.warn('GCCD History write failed:', err.message); }
  };

  React.useEffect(() => {
    fetch(ARCHIVE_FN, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin', body: JSON.stringify({ action: 'getRctiEmailLog' }) })
      .then(r => r.json()).then(d => { if (d.success) setEmailLog(d.log || {}); }).catch(() => {});
  }, []);
  // Record a live send on the server. Test sends go to Paul, so they are not a send.
  const logEmailed = async (rcti, to) => {
    if (testMode) return;
    const periodEndISO = (rcti.periodEndISO || '').slice(0, 10);
    const login = (rcti.driverLogin || '').toUpperCase();
    try {
      const d = await fetch(ARCHIVE_FN, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin',
        body: JSON.stringify({ action: 'markRctiEmailed', periodEndISO, login, invoiceNumber: rcti.invoiceNumber, to, total: rcti.total }) }).then(r => r.json());
      if (d.success) setEmailLog(prev => ({ ...prev, [`${periodEndISO}|${login}`]: { invoiceNumber: rcti.invoiceNumber, to, total: rcti.total, at: d.emailedAt } }));
      else addNotification(`${rcti.invoiceNumber} emailed, but the send wasn't logged: ${d.error || ''}`, 'error');
    } catch (e) { addNotification(`${rcti.invoiceNumber} emailed, but the send wasn't logged: ${e.message}`, 'error'); }
  };

  const handleSendOne = async (rcti) => {
    // Approval gate — never email an RCTI that hasn't been approved (locked). Sending is
    // approval-gated the same way Xero push is; the 2 held-back RCTIs stay unsendable
    // until approved. See handleSendAll + the archive filter (portal is gated too).
    if (!rcti.approved) { addNotification(`Approve ${rcti.invoiceNumber} before sending`, 'error'); return; }
    // LIVE guard — an individual Send goes straight to the contractor (no batch confirm).
    if (!testMode && !window.confirm(`LIVE send: email RCTI ${rcti.invoiceNumber} to ${rcti.companyName || rcti.personalName || 'the contractor'} (${rcti.email || 'no email on file'}) now?\n\nThis goes to the contractor and cannot be undone.`)) return;
    setSending(rcti.id);
    try {
      addNotification(`Sending ${rcti.invoiceNumber}...`, 'info');
      const htmlBase64 = await generatePDFBase64(rcti);   // base64 HTML — Apps Script converts to a PDF attachment
      const payload = {
        action: 'sendEmail', testMode: testMode,
        rctis: [{
          invoiceNumber: rcti.invoiceNumber, driverName: rcti.driverName, personalName: rcti.personalName,
          companyName: rcti.companyName, abn: rcti.abn, email: rcti.email, address: rcti.address,
          phone: rcti.phone, bsb: rcti.bsb, accNumber: rcti.accNumber, periodEndISO: rcti.periodEndISO,
          driverLogin: rcti.driverLogin, periodLabel: rcti.periodLabel, total: rcti.total,
          totalFormatted: rcti.totalFormatted, grossIncome: rcti.grossIncome, subtotal: rcti.subtotal,
          gst: rcti.gst, deductions: rcti.deductions, lineItems: rcti.lineItems,
          fileName: `${rcti.companyName || rcti.personalName} ${rcti.invoiceNumber} ${rcti.periodLabel}.pdf`,
          htmlBase64,
        }],
      };
      const res = await fetch(APPS_SCRIPT_URL, { method: 'POST', body: JSON.stringify(payload) });
      const data = await res.json();
      if (data.success && data.sent > 0) {
        setRctis(prev => prev.map(r => r.id === rcti.id ? { ...r, emailStatus: 'sent' } : r));
        await logEmailed(rcti, (data.results && data.results[0] && data.results[0].to) || rcti.email);
        addNotification(`${testMode ? '[TEST → paul@hollypaul.com] ' : ''}Email sent for ${rcti.invoiceNumber}`, 'success');
      } else {
        const why = (data.results && data.results[0] && data.results[0].error) || data.error || 'Unknown error';
        addNotification(`${rcti.invoiceNumber} send failed: ${why}`, 'error');
      }
    } catch (err) { addNotification(`Send failed: ${err.message}`, 'error'); }
    finally { setSending(null); }
  };

  const handleSendAll = async () => {
    const unsent = filtered.filter(r => r.approved && r.emailStatus !== 'sent');
    if (unsent.length === 0) { addNotification('No approved RCTIs left to send', 'info'); return; }
    const confirmed = window.confirm(`${testMode ? '[TEST MODE] ' : ''}Send ${unsent.length} RCTIs to ${testMode ? 'paul@hollypaul.com (test)' : 'all contractors'}?\n\nThis cannot be undone.`);
    if (!confirmed) return;
    setSendingAll(true);
    let successCount = 0, failCount = 0;
    for (const rcti of unsent) {
      try {
        const htmlBase64 = await generatePDFBase64(rcti);   // was missing — Apps Script needs this to build the PDF
        const payload = {
          action: 'sendEmail', testMode: testMode,
          rctis: [{
            invoiceNumber: rcti.invoiceNumber, driverName: rcti.driverName, personalName: rcti.personalName,
            companyName: rcti.companyName, abn: rcti.abn, email: rcti.email, address: rcti.address,
            phone: rcti.phone, bsb: rcti.bsb, accNumber: rcti.accNumber, periodEndISO: rcti.periodEndISO,
            driverLogin: rcti.driverLogin, periodLabel: rcti.periodLabel, total: rcti.total,
            totalFormatted: rcti.totalFormatted, grossIncome: rcti.grossIncome, subtotal: rcti.subtotal,
            gst: rcti.gst, deductions: rcti.deductions, lineItems: rcti.lineItems,
            fileName: `${rcti.companyName || rcti.personalName} ${rcti.invoiceNumber} ${rcti.periodLabel}.pdf`,
            htmlBase64,
          }],
        };
        const res = await fetch(APPS_SCRIPT_URL, { method: 'POST', body: JSON.stringify(payload) });
        const data = await res.json();
        if (data.success && data.sent > 0) {
          setRctis(prev => prev.map(r => r.id === rcti.id ? { ...r, emailStatus: 'sent' } : r));
          await logEmailed(rcti, (data.results && data.results[0] && data.results[0].to) || rcti.email);
          successCount++;
        } else {
          failCount++;
          console.warn(`Send failed ${rcti.invoiceNumber}:`, (data.results && data.results[0] && data.results[0].error) || data.error);
        }
      } catch (err) { failCount++; console.warn(`Failed ${rcti.invoiceNumber}:`, err.message); }
    }
    setSendingAll(false);
    addNotification(`${testMode ? '[TEST] ' : ''}${successCount} of ${unsent.length} emails sent${failCount ? ` — ${failCount} failed (not marked sent)` : ' ✓'}`, failCount ? 'error' : 'success');
  };

  const buildTeamLeaderRCTI = (tl, freshData, invoiceNum) => {
    const c = freshData.controls;
    const reportEndISO = toISO(c.reportEndDate);
    const driverRouteMap = freshData.driverRouteMap || {};
    const attendance = freshData.teamLeaderAttendance || {};
    const ovr = (freshData.tlOverrides || {})[tl.login.toUpperCase()] || {};
    const end = new Date(c.reportEndDate);
    const start = new Date(end);
    start.setDate(end.getDate() - 27);
    const fmtDate = d => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    const periodLabel = `${fmtDate(start)} to ${fmtDate(end)}`;
    const lineItems = [];
    let grossIncome = 0;
    let depotHourlyGccd = null;   // populated in the isDepotHourly branch so the GCCD panel shows real min-pay
    const isDepotHourly = tl.login.toUpperCase() === 'SOTOASTOA';
    const isHourly = tl.unitTypes.includes('Hourly');
    const hasDepots = tl.unitTypes.includes('Depots') || tl.unitTypes.includes('Depot Bags');
    const isPurePerUnit = !isHourly;
    if (isDepotHourly) {
      const qty = freshData.quantities?.[tl.login] || {};
      const rates = freshData.ratesMap?.[tl.login] || {};
      const parcelQty = qty.Parcel || 0; const expQty = qty.Exp || 0; const depotBagQty = qty['Depot Bags'] || 0;
      const parcelRate = rates['Parcel'] || 0; const expRate = rates['Exp'] || 0; const depotBagRate = rates['Depot Bags'] || 0;
      const parcelAmt = Math.round(parcelQty * parcelRate * 100) / 100;
      const expAmt = Math.round(expQty * expRate * 100) / 100;
      const depotBagAmt = Math.round(depotBagQty * depotBagRate * 100) / 100;
      if (parcelQty > 0) { lineItems.push({ description: 'Parcel Delivery', quantity: parcelQty, rate: parcelRate, amount: parcelAmt }); grossIncome += parcelAmt; }
      if (expQty > 0) { lineItems.push({ description: 'Express Delivery', quantity: expQty, rate: expRate, amount: expAmt }); grossIncome += expAmt; }
      if (depotBagQty > 0) { lineItems.push({ description: 'Depot Bag Delivery', quantity: depotBagQty, rate: depotBagRate, amount: depotBagAmt }); grossIncome += depotBagAmt; }
      const gccd = freshData.gccdSummary?.[tl.login] || {};
      // Min-pay floor = her AGREED HOURS × GCCD rate (entered on the Hourly Drivers page),
      // like a standard hourly driver — NOT the stale gccdSummary (which used wrong hours/rate
      // and left her with no top-up). `actual` excludes depot ON PURPOSE: her hourly minimum
      // covers delivery (parcel+express); depot bags are a separate service billed on top.
      const tlKey = tl.login.toUpperCase();
      const hourlyInputRecord = (freshData.hourlyInput || []).find(r => (r.Login || '').toUpperCase() === tlKey && r.Period === freshData.controls.reportEndDate);
      const agreedHoursRaw = hourlyInputRecord ? parseFloat(hourlyInputRecord['Agreed Hours']) : null;
      const agreedHours = agreedHoursRaw != null && agreedHoursRaw > 0 ? agreedHoursRaw : null;
      const hourlyRate = (freshData.gccdRates?.[tlKey] || {}).minHourlyRate || 0;
      const actual = parcelAmt + expAmt;
      const minPayable = agreedHours !== null && hourlyRate > 0 ? Math.round(agreedHours * hourlyRate * 100) / 100 : (gccd.minPayable || 0);
      const variance = actual - minPayable; const gccdLevy = variance < 0 ? Math.abs(variance) : 0;
      if (gccdLevy > 0) {
        const totalParcels = parcelQty || 1;
        lineItems.push({ description: 'Service Fee Adjustment — Route Conditions', quantity: totalParcels, rate: Math.round((gccdLevy / totalParcels) * 10000) / 10000, amount: gccdLevy, isUplift: true });
        grossIncome += gccdLevy;
      }
      // Surface the min-pay reconciliation so the GCCD panel isn't blank. actualEarnings here is
      // her DELIVERY earnings (parcel+express) compared to the floor — depot is billed separately.
      depotHourlyGccd = { meetsMinimum: gccdLevy === 0, totalHours: agreedHours || 0, minRate: hourlyRate || 0, minPayable, actualEarnings: actual, variance, gccdLevy };
    } else if (isHourly) {
      const hoursPerDay = tl.hoursPerDay || 0; const hourlyRateIncGST = tl.hourlyRateIncGST || 0;
      // loads override = total loads entered in-app; else attendance days × loads/day (legacy)
      const totalHours = ovr.loads != null ? ovr.loads : (attendance[tl.login]?.[reportEndISO] || []).length * hoursPerDay;
      const rateExGST = Math.round((hourlyRateIncGST / 1.1) * 10000) / 10000;
      const hourlyAmount = Math.round(totalHours * rateExGST * 100) / 100;
      if (hourlyAmount > 0) { lineItems.push({ description: `${ovr.label || tl.descriptionOverride || 'Team Leader Fee'} — ${totalHours} loads`, quantity: totalHours, rate: rateExGST, amount: hourlyAmount }); grossIncome += hourlyAmount; }
      if (hasDepots) {
        const qty = freshData.quantities?.[tl.login] || {}; const depotQty = qty['Depot Bags'] || 0;
        const depotRateExGST = 4.55;   // $4.55 is already GST-EXCLUSIVE — do NOT /1.1 (that gave $4.1364)
        const depotAmount = Math.round(depotQty * depotRateExGST * 100) / 100;
        if (depotQty > 0) { lineItems.push({ description: 'Depot Bag Delivery Fee', quantity: depotQty, rate: depotRateExGST, amount: depotAmount }); grossIncome += depotAmount; }
      }
    }
if (isPurePerUnit) {
      const quantities = freshData.quantities || {};
      const dailyParcels = freshData.dailyParcels || {};
      const dailyDepots = freshData.dailyDepots || {};
      const excludeDates = new Set(tl.excludeDates || []);
      let totalParcels = 0;
      let totalDepotBags = 0;

const tlLoginSet = new Set((freshData.teamLeaders || []).map(t => t.login.toUpperCase()));

// THIS FILTER DEFINES WHAT A TEAM LEADER IS PAID FOR. Its twin is the team rule in
// netlify/functions/eos-tl-summary-sb.js (inTeam), which builds the Cut off period summary
// the leader verifies this invoice against — the two must agree or the leader is checking
// his pay against a different number. They have drifted twice: the summary excluded network
// sub-teams (−3,378 parcels for DONGB3) and dropped inactive drivers, both of which this
// rule counts. Change one, change the other.
if (tl.unitTypes.includes('Parcels')) {
  Object.keys(dailyParcels).forEach(key => {
    const [keyDriver, keyDate] = key.split('|');
    if (excludeDates.has(keyDate)) return;
    // For route-specific TLs, exclude other TLs' scans to avoid double-counting on shared routes.
    // For 'ALL' route TLs (e.g. SOTOCRUZL), count every driver's parcels — their fee covers
    // the full network volume regardless of who delivered each parcel.
    if (tl.route !== 'ALL' && tlLoginSet.has(keyDriver.toUpperCase()) && keyDriver.toUpperCase() !== tl.login.toUpperCase()) return;
    if (tl.route !== 'ALL') {
      const driverRoute = driverRouteMap[keyDriver] || '';
      if (driverRoute !== tl.route) return;
    }
    totalParcels += dailyParcels[key] || 0;
  });
}
  
// Depot bags — use route-tagged dailyDepots for accuracy
    if (tl.unitTypes.includes('Depot Bags')) {
      Object.keys(dailyDepots).forEach(key => {
        const parts = key.split('|');
        const keyDate = parts[1];
        const keyRoute = parts[2] || '';
        if (excludeDates.has(keyDate)) return;
        if (tl.route !== 'ALL' && keyRoute !== tl.route) return;
        totalDepotBags += dailyDepots[key] || 0;
      });
    }

      if (totalParcels > 0) {
        const parcelAmount = Math.round(totalParcels * tl.rate * 100) / 100;
        const baseDesc = tl.descriptionOverride || 'Service Fee - Parcel Delivery';
        lineItems.push({
          description: `${baseDesc}${tl.route !== 'ALL' ? ` (${tl.route})` : ''}`,
          quantity: totalParcels,
          rate: tl.rate,
          amount: parcelAmount,
        });
        grossIncome += parcelAmount;
      }

      if (totalDepotBags > 0) {
        const depotAmount = Math.round(totalDepotBags * tl.rate * 100) / 100;
        lineItems.push({
          description: `Service Fee - Depot Bags${tl.route !== 'ALL' ? ` (${tl.route})` : ''}`,
          quantity: totalDepotBags,
          rate: tl.rate,
          amount: depotAmount,
        });
        grossIncome += depotAmount;
      }
    }

    const isExemptFromFuelLevy = tl.login.toUpperCase() === 'ASTORGACV';
    if (!isExemptFromFuelLevy) {
      const tlAdj = freshData.adjustments[(tl.login || '').toUpperCase()] || {};
      const tlFuelLevy = (tlAdj.periodEnd && toISO(tlAdj.periodEnd) === toISO(c.reportEndDate)) ? (tlAdj.fuelLevy || 0) : 0;
      if (tlFuelLevy > 0) { lineItems.push({ description: 'Service Fee — Prevailing Conditions', quantity: 1, rate: tlFuelLevy, amount: tlFuelLevy, isFuelLevy: true }); grossIncome += tlFuelLevy; }
      fuelCatchUpLines(tlAdj, c.reportEndDate).forEach(li => { lineItems.push(li); grossIncome += li.amount; });
    }
    (ovr.extras || []).forEach(e => { lineItems.push({ description: e.description, quantity: 1, rate: e.amount, amount: e.amount, type: 'extra' }); grossIncome += e.amount; });
    if (grossIncome === 0) return null;
    const leaseFee = tl.leaseFee || 0;
    const subtotal = Math.round((grossIncome - leaseFee) * 100) / 100;
    const gst = Math.round(subtotal * 0.1 * 100) / 100;
    const total = Math.round((subtotal + gst) * 100) / 100;
    return {
      id: `${tl.login}|${c.reportEndDate}`, invoiceNumber: `RCTI-${invoiceNum}`, driverLogin: tl.login,
      periodEndISO: reportEndISO, driverName: tl.login, personalName: tl.name, companyName: tl.companyName,
      abn: tl.abn, address: tl.address, phone: tl.phone, email: tl.email, bsb: tl.bsb, accNumber: tl.accNumber,
      periodLabel, reportEndDate: c.reportEndDate, status: 'Not Posted', xeroStatus: 'pending', emailStatus: 'unsent',
      isTeamLeader: true, lineItems, gstRegistered: tl.gstRegistered !== false, route: tl.route || '',
      gccd: depotHourlyGccd || { meetsMinimum: true, totalHours: 0, minRate: 0, minPayable: 0, actualEarnings: grossIncome, variance: 0, gccdLevy: 0 },
      deductions: { leaseFee, leaseLabel: 'Lease Fee' }, grossIncome, subtotal, gst, total, totalFormatted: fmt(total),
    };
  };
  const buildNetworkRCTI = (tl, freshData, invoiceNum) => {
    const c = freshData.controls;
    const reportEndISO = toISO(c.reportEndDate);
    const end = new Date(c.reportEndDate); const start = new Date(end); start.setDate(end.getDate() - 27);
    const fmtDate = d => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    const periodLabel = `${fmtDate(start)} to ${fmtDate(end)}`;
    const principal = tl.login.toUpperCase();
    const networkQuantities = freshData.networkQuantities || {};
    const qty = networkQuantities[principal] || {};
    const ovr = (freshData.tlOverrides || {})[principal] || {};
    const lineItems = []; let grossIncome = 0;
    if (principal === 'TNHA') {
      const parcels = qty['Parcel'] || 0;
      if (ovr.perParcelRate > 0) {
        // Audit-friendly: fee shown against actual parcels delivered × per-parcel rate
        const amt = Math.round(parcels * ovr.perParcelRate * 100) / 100;
        lineItems.push({ description: ovr.label || 'Parcel Delivery Coordination Fee', quantity: parcels, rate: ovr.perParcelRate, amount: amt });
        grossIncome += amt;
      } else {
        const flatExGST = Math.round((400 / 1.1) * 100) / 100; const units = tl.units || 1;
        const flatAmount = Math.round(units * flatExGST * 100) / 100;
        lineItems.push({ description: 'Network Coordination Fee', quantity: units, rate: flatExGST, amount: flatAmount });
        grossIncome += flatAmount;
      }
      const hourlyRateExGST = Math.round((41.04 / 1.1) * 10000) / 10000;
      const hourlyAmount = Math.round(1.5 * hourlyRateExGST * 100) / 100;
      if (hourlyAmount > 0) { lineItems.push({ description: 'Network Support — 1.5 loads', quantity: 1.5, rate: hourlyRateExGST, amount: hourlyAmount }); grossIncome += hourlyAmount; }
    }
    if (principal === 'ALANAS') {
      const rates = freshData.ratesMap?.['PARRACASA'] || {};
      const parcelQty = qty['Parcel'] || 0; const expQty = qty['Exp'] || 0; const depotBagQty = qty['Depot Bags'] || 0;
      const parcelRate = rates['Parcel'] || 0; const expRate = rates['Exp'] || 0; const depotBagRate = rates['Depot Bags'] || 0;
      if (parcelQty > 0) { const amt = Math.round(parcelQty * parcelRate * 100) / 100; lineItems.push({ description: 'Parcel Delivery Coordination Fee', quantity: parcelQty, rate: parcelRate, amount: amt }); grossIncome += amt; }
      if (expQty > 0) { const amt = Math.round(expQty * expRate * 100) / 100; lineItems.push({ description: 'Express Delivery Coordination Fee', quantity: expQty, rate: expRate, amount: amt }); grossIncome += amt; }
      if (depotBagQty > 0) { const amt = Math.round(depotBagQty * depotBagRate * 100) / 100; lineItems.push({ description: 'Depot Bag Coordination Fee', quantity: depotBagQty, rate: depotBagRate, amount: amt }); grossIncome += amt; }
    }
    const fuelLevyRefLogin = principal === 'ALANAS' ? 'PARRACASA' : principal === 'TNHA' ? 'NGUYENHAT' : principal;
    const networkAdj = freshData.adjustments[fuelLevyRefLogin] || {};
    const networkFuelLevy = (networkAdj.periodEnd && toISO(networkAdj.periodEnd) === toISO(c.reportEndDate)) ? (networkAdj.fuelLevy || 0) : 0;
    if (networkFuelLevy > 0) { lineItems.push({ description: 'Service Fee — Prevailing Conditions', quantity: 1, rate: networkFuelLevy, amount: networkFuelLevy, isFuelLevy: true }); grossIncome += networkFuelLevy; }
    fuelCatchUpLines(networkAdj, c.reportEndDate).forEach(li => { lineItems.push(li); grossIncome += li.amount; });
    (ovr.extras || []).forEach(e => { lineItems.push({ description: e.description, quantity: 1, rate: e.amount, amount: e.amount, type: 'extra' }); grossIncome += e.amount; });
    if (grossIncome === 0) return null;
    const leaseFee = tl.leaseFee || 0;
    const subtotal = Math.round((grossIncome - leaseFee) * 100) / 100;
    const gst = Math.round(subtotal * 0.1 * 100) / 100;
    const total = Math.round((subtotal + gst) * 100) / 100;
    return {
      id: `${tl.login}|${c.reportEndDate}`, invoiceNumber: `RCTI-${invoiceNum}`, driverLogin: tl.login,
      periodEndISO: reportEndISO, driverName: tl.login, personalName: tl.name, companyName: tl.companyName,
      abn: tl.abn, address: tl.address, phone: tl.phone, email: tl.email, bsb: tl.bsb, accNumber: tl.accNumber,
      periodLabel, reportEndDate: c.reportEndDate, status: 'Not Posted', xeroStatus: 'pending', emailStatus: 'unsent',
      isTeamLeader: true, isNetwork: true, lineItems, gstRegistered: tl.gstRegistered !== false, route: tl.route || '',
      gccd: { meetsMinimum: true, totalHours: 0, minRate: 0, minPayable: 0, actualEarnings: grossIncome, variance: 0, gccdLevy: 0 },
      deductions: { leaseFee, leaseLabel: 'Lease Fee' }, grossIncome, subtotal, gst, total, totalFormatted: fmt(total),
    };
  };

  const importFromSheets = async () => {
    setLoading(true);
    try {
      const { fetchAllSheetData } = await import('../services/googleSheets');
      const freshData = await fetchAllSheetData(buildPeriodISO || undefined);
      // TL/network RCTI overrides edited in-app (Hourly Drivers → Team Leaders tab)
      freshData.tlOverrides = {};
      try {
        const ovPeriod = toISO(freshData.controls.reportEndDate);
        const ovRes = await fetch(`/.netlify/functions/tl-rcti?action=get&period=${encodeURIComponent(ovPeriod)}`);
        const ovData = await ovRes.json();
        if (ovData.success) freshData.tlOverrides = ovData.overrides || {};
        addNotification(`TL overrides for ${ovPeriod}: ${Object.keys(freshData.tlOverrides).join(', ') || 'none'}`, 'info');
      } catch (err) { console.warn('Could not load TL overrides:', err.message); }
      // CONFIRMED GCCD Variable Uplift Rates (GCCD Report → Uplift Workbench). These
      // override the auto-computed GCCD top-up per driver in buildRCTI. Only CONFIRMED
      // uplifts are pulled — Pending ones must be confirmed in the workbench first.
      // Non-fatal: if this read fails, buildRCTI falls back to the legacy auto levy.
      freshData.confirmedUplifts = {};
      try {
        const upPeriod = toISO(freshData.controls.reportEndDate);
        const upRes = await fetch('/.netlify/functions/gccd-uplift-sb', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'getUpliftWorkbenchData', period: upPeriod }),
        });
        const upData = await upRes.json();
        (upData.drivers || []).forEach(dr => {
          const conf = (dr.uplifts || [])
            .filter(u => String(u.status).toLowerCase() === 'confirmed')
            .reduce((s, u) => s + (Number(u.upliftTotal) || 0), 0);
          if (conf > 0) freshData.confirmedUplifts[(dr.driver || '').toUpperCase()] = Math.round(conf * 100) / 100;
        });
        const upNames = Object.keys(freshData.confirmedUplifts);
        addNotification(`Confirmed GCCD uplifts for ${upPeriod}: ${upNames.length ? upNames.join(', ') : 'none'}`, 'info');
      } catch (err) { console.warn('Could not load confirmed uplifts:', err.message); }
      const sorted = [...freshData.drivers].sort((a, b) => (a.companyName || a.name || '').localeCompare(b.companyName || b.name || ''));
      let invoiceNum = freshData.controls.nextInvoiceNumber || 1061;
      setDrivers(freshData.drivers);
      // Load persisted manual RCTIs from Sheets
      let savedManuals = [];
      try {
        const manualRes = await fetch(MANUAL_RCTI_URL, {
          method: 'POST',
          body: JSON.stringify({ action: 'loadManualRCTIs' }),
        });
        const manualData = await manualRes.json();
        if (manualData.success) savedManuals = manualData.rctis || [];
      } catch (err) {
        console.warn('Could not load manual RCTIs:', err.message);
      }
      const teamLeaderLogins = new Set((freshData.teamLeaders || []).map(tl => tl.login.toUpperCase()));
      const networkMemberLogins = new Set(freshData.networkMemberLogins || []);
const periodEndDate = new Date(freshData.controls.reportEndDate);
// An RCTI is a tax invoice we raise on the contractor's behalf, so it CANNOT be issued
// without their ABN — paying one without it triggers no-ABN withholding. A driver with no
// ABN is held out of the build entirely until they supply one (LANDERR, 2026-07). Held
// BEFORE the build so no ledger invoice number is drawn for an RCTI that must not exist.
const noAbnHeld = [];
const builtDrivers = sorted
  .filter(d => !teamLeaderLogins.has(d.loginName.toUpperCase()) && !networkMemberLogins.has(d.loginName.toUpperCase()))
  .filter(d => {
    const commence = parseCommencement(d.commencementDate);
    if (!commence) return true; // blank/unreadable = include
    return commence <= periodEndDate; // only include if commenced by period end
  })
  .filter(d => {
    if ((d.abn || '').toString().trim()) return true;
    // Only worth flagging if they'd have been paid — several no-ABN logins (staff, dormant
    // drivers) build nothing anyway, and naming them every time would train you to ignore it.
    const q = freshData.quantities[d.loginName.toUpperCase()] || {};
    const worked = (q.Parcel || 0) + (q.Exp || 0) + (q.DOE || 0) + (q['Depot Bags'] || 0);
    if (worked > 0) noAbnHeld.push(d.loginName);
    return false;
  })
  .map(d => buildRCTI(d, freshData, invoiceNum++)).filter(Boolean);
if (noAbnHeld.length) {
  addNotification(`HELD — no ABN on file: ${noAbnHeld.join(', ')}. They have billable work this period but no RCTI can be issued until they supply an ABN.`, 'error');
}
      const teamLeaders = freshData.teamLeaders || [];
      const networkPrincipals = new Set(Object.keys(freshData.networkQuantities || {}));
      const builtTeamLeaders = teamLeaders
        .filter(tl => !networkPrincipals.has(tl.login.toUpperCase()))
        .map(tl => buildTeamLeaderRCTI(tl, freshData, invoiceNum++)).filter(Boolean);
      const networkTLRows = teamLeaders.filter(tl => networkPrincipals.has(tl.login.toUpperCase()));
      const builtNetworks = networkTLRows.map(tl => buildNetworkRCTI(tl, freshData, invoiceNum++)).filter(Boolean);
      const trueManuals = savedManuals.filter(r => !r.isModification);
      const sheetsMods = savedManuals.filter(r => r.isModification);
      let lsMods = {};
      try { lsMods = JSON.parse(localStorage.getItem('hp-rcti-mods') || '{}'); } catch {}
      // A saved mod is a FULL line-item snapshot from when the operator amended the row.
      // Fuel is SOURCE-driven (Fuel Workbench confirm), so a snapshot taken before the
      // fuel was confirmed must not revert it: re-sync the fuel line from the fresh build
      // and recompute totals. (15 Aug 2026: every amended RCTI silently lost its fuel
      // adjustment on the fuel rebuild — the period's 15 snapshots all predated the
      // confirm. The operator's amendments stay; only the fuel line follows the source.)
      const isFuelLine = (li) => !!li.isFuelLevy || /Prevailing Conditions/i.test(li.description || '');
      // Every fuel line follows the source — this period's AND any catch-up line.
      const fuelSig = (row) => (row.lineItems || []).filter(isFuelLine).map(li => `${li.description}|${li.amount}`).sort().join(';');
      const syncFuelFromFresh = (freshRow, moddedRow) => {
        if (fuelSig(freshRow) === fuelSig(moddedRow)) return moddedRow;
        const items = (moddedRow.lineItems || []).filter(li => !isFuelLine(li));
        items.push(...(freshRow.lineItems || []).filter(isFuelLine));
        const t = recomputeRctiTotals(moddedRow, items);
        return { ...moddedRow, lineItems: items, ...t, totalFormatted: fmt(t.total) };
      };
      const applyMod = (r) => {
        // TL / network rows are driven live by the in-app editor (Hourly Drivers → Team
        // Leader RCTI). When an override exists for this login the freshly-built row IS the
        // source of truth — never let an old frozen full-snapshot mod overwrite it. (This
        // was hiding TN HA's per-parcel coordination fee behind a stale flat line.)
        const lg = (r.driverLogin || '').toUpperCase();
        if (freshData.tlOverrides && freshData.tlOverrides[lg]) return r;
        // Prefer Sheets-saved mod (most authoritative), fall back to localStorage
        const sheetMod = sheetsMods.filter(m => m.originalId === r.id).pop();
        if (sheetMod) return syncFuelFromFresh(r, { ...r, lineItems: sheetMod.lineItems, grossIncome: sheetMod.grossIncome, subtotal: sheetMod.subtotal, gst: sheetMod.gst, total: sheetMod.total, totalFormatted: sheetMod.totalFormatted, deductions: sheetMod.deductions || r.deductions });
        const lsMod = lsMods[r.id];
        if (lsMod) return syncFuelFromFresh(r, { ...r, ...lsMod });
        return r;
      };
      // Attach any live advance repayment LAST — after applyMod, so the cap is priced off
      // the final total. One place covers drivers, team leaders and networks alike. Note
      // the ledger keys on the RCTI login, which for a network principal is the principal
      // (PARRACASA's RCTI is issued under ALANAS), not the personnel login.
      // Read the ledger FRESH rather than trusting component state: a build that raced the
      // mount fetch, or ran after another admin recorded an advance, would otherwise skip
      // the deduction silently — and a missed deduction is money that never comes back.
      let liveAdvances = advances;
      try {
        const advRes = await fetch(ADVANCES_URL, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin', body: JSON.stringify({ action: 'listAdvances' }),
        });
        const advData = await advRes.json();
        if (advData.success) { liveAdvances = advData.advances || []; setAdvances(liveAdvances); }
        else addNotification(`Advance ledger unreadable (${advData.error}) — built with no advance deductions`, 'error');
      } catch (err) {
        addNotification(`Advance ledger unreachable (${err.message}) — built with no advance deductions`, 'error');
      }
      const attachAdvance = (r) => {
        const meta = advanceMetaFor(liveAdvances, r.driverLogin, r.periodEndISO);
        const priced = priceAdvance(meta, r.total || 0);
        return priced ? { ...r, advance: priced } : r;
      };
      // Logins taken out of this period with "Remove from run" stay out of every rebuild —
      // read fresh, like the advance ledger, so another browser's removal is honoured.
      // Filtered BEFORE numbering: a removed RCTI keeps the number it was issued and no
      // new one is drawn for it.
      const buildEndISO = [...builtDrivers, ...builtTeamLeaders, ...builtNetworks][0]?.periodEndISO;
      let removedHere = {};
      if (buildEndISO) {
        try {
          const exRes = await fetch(RCTI_ARCHIVE_URL, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin',
            body: JSON.stringify({ action: 'listExcluded', periodEndISO: buildEndISO }),
          });
          const exData = await exRes.json();
          if (exData.success) {
            removedHere = (exData.excluded || {})[buildEndISO] || {};
            setExcludedRuns(prev => ({ ...prev, [buildEndISO]: removedHere }));
          } else addNotification(`Removed-from-run list unreadable (${exData.error}) — check the register for RCTIs you removed`, 'error');
        } catch (err) {
          addNotification(`Removed-from-run list unreachable (${err.message}) — check the register for RCTIs you removed`, 'error');
        }
      }
      const allBuilt = [...builtDrivers, ...builtTeamLeaders, ...builtNetworks]
        .filter(r => !removedHere[(r.driverLogin || '').toUpperCase()])
        .sort((a, b) => (a.companyName || a.personalName || '').localeCompare(b.companyName || b.personalName || ''))
        .map(applyMod)
        .map(attachAdvance);
      if (Object.keys(removedHere).length) {
        addNotification(`Kept out of this run (removed): ${Object.keys(removedHere).join(', ')}`, 'info');
      }
      // Assign STABLE, sequential invoice numbers from the persistent ledger (Blob),
      // keyed by each RCTI's id ("<LOGIN>|<periodEnd>"). This replaces the old positional
      // counter that restarted at Controls→nextInvoiceNumber every build — which gave the
      // same driver the same number in every period (ALANAS = RCTI-1111 in both Apr & May).
      // Idempotent: a period already in the ledger keeps its numbers on rebuild; only
      // brand-new RCTIs draw the next number. Non-fatal: if the ledger is unreachable we
      // keep the positional fallback numbers from buildRCTI.
      let numberedBuilt = allBuilt;
      try {
        const ids = allBuilt.map(r => r.id);
        const numRes = await fetch('/.netlify/functions/rcti-numbers?action=assign', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids, startCounter: freshData.controls.nextInvoiceNumber || 1061 }),
        });
        const numData = await numRes.json();
        if (numData.success && numData.map) {
          numberedBuilt = allBuilt.map(r => (numData.map[r.id] != null ? { ...r, invoiceNumber: `RCTI-${numData.map[r.id]}` } : r));
        } else {
          addNotification('Invoice-number ledger error — used positional fallback numbers', 'error');
        }
      } catch (err) { console.warn('Invoice-number ledger unavailable, using positional numbers:', err.message); }
      const currentEndISO = numberedBuilt[0]?.periodEndISO;
      const currentPeriodLabel = numberedBuilt[0]?.periodLabel;
      // "Approve" is a LOCK, not just a green flag. Once Paul has reviewed, corrected and
      // approved an RCTI, a rebuild must leave that ENTIRE row untouched — line items,
      // totals and all — and take only the fresh build for the ones he hasn't approved yet.
      // This is the safeguard he expected: an earlier attempt kept only the approved flag,
      // so a correction made to an approved RCTI still got overwritten by the fresh numbers
      // — worst of all for network / team-leader RCTIs (e.g. ALANAS), where the rebuild
      // deliberately ignores on-page line amendments (see applyMod's tlOverride short-circuit).
      // Freezing the whole approved row sidesteps every one of those overwrite paths.
      // Escape hatch: un-approve → rebuild → re-approve to pull in fresh source data.
      // Matched by the stable id ("<LOGIN>|<periodEnd>").
      const priorById = new Map(rctis.map(r => [r.id, r]));
      const currentPeriodRows = numberedBuilt.map(fresh => {
        const prior = priorById.get(fresh.id);
        return (prior && prior.approved) ? prior : fresh;
      });
      const frozenCount = currentPeriodRows.filter(r => r.approved).length;
      // Fuel reconciliation: every login with fuel confirmed FOR THIS PERIOD must end up
      // with a Prevailing Conditions line on a built row (network fuel lands on the
      // principal's RCTI: PARRACASA→ALANAS, NGUYENHAT→TNHA; ASTORGACV is fuel-exempt).
      // Names any silent miss — frozen approved rows, stale mods, or a fuel login that
      // doesn't match any RCTI login (15 Aug 2026: three misses went unnoticed to Xero).
      const fuelTarget = { PARRACASA: 'ALANAS', NGUYENHAT: 'TNHA' };
      const fuelMisses = [];
      Object.entries(freshData.adjustments || {}).forEach(([lg, adj]) => {
        const ownDue = adj.fuelLevy > 0 && adj.periodEnd && toISO(adj.periodEnd) === toISO(freshData.controls.reportEndDate);
        const catchUpDue = fuelCatchUpLines(adj, freshData.controls.reportEndDate);
        if (!ownDue && !catchUpDue.length) return;
        const target = fuelTarget[lg] || lg;
        if (target === 'ASTORGACV') return;
        const row = currentPeriodRows.find(r => (r.driverLogin || '').toUpperCase() === target);
        if (!row) { fuelMisses.push(`${lg} (no RCTI for ${target})`); return; }
        const who = target === lg ? lg : `${lg}→${target}`;
        if (ownDue && !(row.lineItems || []).some(li => !li.isFuelCatchUp && !/catch-up/i.test(li.description || '') && (li.isFuelLevy || /Prevailing Conditions/i.test(li.description || '')))) {
          fuelMisses.push(who);
        }
        // A catch-up line is checked on its own: this period's fuel being there doesn't
        // mean last period's made it (an approved row frozen before the carry won't have it).
        catchUpDue.forEach(cu => {
          if (!(row.lineItems || []).some(li => li.description === cu.description)) fuelMisses.push(`${who} (catch-up ${cu.fuelFromPeriod})`);
        });
      });
      if (fuelMisses.length) {
        addNotification(`⚠ Fuel confirmed but MISSING from: ${fuelMisses.join(', ')}. Approved rows stay frozen (un-approve → rebuild); otherwise check the fuel login matches the RCTI login.`, 'error');
      }
      setRctis(prev => {
        // Keep auto-built RCTIs from OTHER periods. Match the current period by END DATE,
        // not the label text — an older build may have used a different month format
        // ("13 June" vs "13 Jun"), and a label-only check would leave a stale duplicate.
        const otherPeriodBuilt = prev.filter(r => !r.isManual
          && (currentEndISO ? r.periodEndISO !== currentEndISO : r.periodLabel !== currentPeriodLabel));
        // trueManuals from the store is authoritative for all manual RCTIs across all periods
        // — but the store never holds Approved or the push/email state, so carry those over
        // from the row already on the page instead of resetting a pushed manual to pending.
        const prevById = new Map(prev.filter(r => r.isManual).map(r => [r.id, r]));
        return [...otherPeriodBuilt, ...currentPeriodRows, ...trueManuals.map(m => carryManualFlags(m, prevById.get(m.id)))];
      });
      addNotification(`Built ${numberedBuilt.length} RCTIs (${builtDrivers.length} drivers + ${builtTeamLeaders.length} team leaders + ${builtNetworks.length} networks) — sequential numbers assigned from the ledger${frozenCount ? `. ${frozenCount} approved RCTI${frozenCount === 1 ? '' : 's'} left locked (un-approve to refresh)` : ''}`, 'success');
      await writeGCCDHistory(builtDrivers, builtDrivers[0]?.periodLabel || freshData.controls.reportEndDate);
      addNotification(`GCCD history saved for ${builtDrivers[0]?.periodLabel || freshData.controls.reportEndDate}`, 'success');
      // Archive the FULL built RCTIs (line items + totals) per driver for the driver
      // portal's RCTI history. Upserts by login+period, so rebuilding replaces cleanly.
      // Non-fatal: a failure here never blocks the build.
      try {
        // Only APPROVED RCTIs are published to the driver portal. Unapproved (held-back)
        // ones are withheld from "My RCTIs" until approved. The write is upsert-only — an
        // un-approved RCTI leaves the portal through toggleApprove's removeRctiOne, never
        // because a rebuild didn't mention it (that wiped the 8 Aug period).
        const archiveRctis = currentPeriodRows.filter(r => r.approved).map(portalArchiveShape);
        // The driver-portal "My RCTIs" tab reads getDriverRctis from SUPABASE (rcti-archive-sb),
        // so the archive write MUST go through the Netlify proxy (which routes to Supabase) —
        // NOT the direct Apps Script URL, which only updates the now-orphaned RCTI_Archive sheet
        // that nothing reads. Same-origin fetch carries the admin hp_auth cookie the proxy needs.
        const arcRes = await fetch('/.netlify/functions/appsscript', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin', body: JSON.stringify({ action: 'saveRctiArchive', periodEndISO: toISO(freshData.controls.reportEndDate), rctis: archiveRctis }) });
        const arcData = await arcRes.json();
        if (arcData.success) addNotification(`Archived ${arcData.total} RCTIs to driver portal history`, 'success');
        else addNotification(`RCTI archive: ${arcData.error || 'failed'}`, 'error');
      } catch (err) { console.warn('RCTI archive failed:', err.message); }
      // Reconciliation tripwire — cross-check the built parcel counts against an
      // INDEPENDENT read of Sheet1 (Apps Script getSheet1Totals, a separate code
      // path). They share Sheet1, so they must match; if a future change ever
      // breaks that, this flags it loudly at build instead of months later.
      if (buildPeriodISO) {
        try {
          const _e = new Date(buildPeriodISO + 'T12:00:00');
          const _s = new Date(_e); _s.setDate(_e.getDate() - 27);
          const startISO = `${_s.getFullYear()}-${String(_s.getMonth() + 1).padStart(2, '0')}-${String(_s.getDate()).padStart(2, '0')}`;
          const r = await fetch(APPS_SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'getSheet1Totals', periodStart: startISO, periodEnd: buildPeriodISO }) });
          const j = await r.json();
          if (j.success) {
            const bd = j.byDriver || {};
            const mism = [];
            builtDrivers.forEach(inv => {
              const lg = (inv.driverLogin || '').toUpperCase();
              const p = (inv.lineItems.find(l => l.type === 'parcel') || {}).quantity || 0;
              const e = (inv.lineItems.find(l => l.type === 'express') || {}).quantity || 0;
              const sp = (bd[lg] || {}).parcel || 0, se = (bd[lg] || {}).express || 0;
              if (p !== sp || e !== se) mism.push(`${lg} ${p}/${e} vs Sheet1 ${sp}/${se}`);
            });
            if (mism.length) addNotification(`⚠ Parcel reconciliation FAILED for ${mism.length} driver(s): ${mism.slice(0, 3).join('; ')}${mism.length > 3 ? '…' : ''}`, 'error');
            else addNotification(`✓ Parcel counts reconcile with Sheet1 across ${builtDrivers.length} drivers`, 'success');
          }
        } catch (e) { /* non-fatal — never block a build on the tripwire */ }
      }
    } catch (err) { addNotification(`Import failed: ${err.message}`, 'error'); }
    finally { setLoading(false); }
  };

  const syncToXero = async (rcti) => {
    if (!rcti.approved) { addNotification(`Approve ${rcti.invoiceNumber} before pushing to Xero`, 'error'); return false; }
    setSyncing(rcti.id);
    try {
      const res = await fetch(`${XERO_FN}?action=push-rcti`, { method: 'POST', body: JSON.stringify({ rcti }) });
      const d = await res.json();
      if (d.success) {
        setRctis(prev => prev.map(r => r.id === rcti.id
          ? { ...r, xeroStatus: 'synced', xeroInvoiceId: d.xeroNumber || d.invoiceId, xeroInvoiceGuid: d.invoiceId || r.xeroInvoiceGuid, xeroDeepLink: d.deepLink, status: d.duplicate ? r.status : 'Posted' } : r));
        addNotification(d.duplicate
          ? `${rcti.invoiceNumber} already in Xero (${d.status}) — skipped, no duplicate created`
          : `${rcti.invoiceNumber} → Xero ${d.status} bill${d.xeroNumber ? ' ' + d.xeroNumber : ''}`,
          d.duplicate ? 'info' : 'success');
        if (d.warning && !d.duplicate) addNotification(`${rcti.invoiceNumber}: ${d.warning}`, 'error');
        return d.invoiceId || true;   // return the bill GUID so the caller can auto-attach its PDF
      }
      if (d.code === 'NOT_CONNECTED') { setXeroConnected(false); }
      setRctis(prev => prev.map(r => r.id === rcti.id ? { ...r, xeroStatus: 'error', ...(d.deepLink ? { xeroDeepLink: d.deepLink } : {}) } : r));
      addNotification(`${rcti.invoiceNumber}: ${d.error || 'Xero push failed'}`, 'error');
      return false;
    } catch (err) {
      setRctis(prev => prev.map(r => r.id === rcti.id ? { ...r, xeroStatus: 'error' } : r));
      addNotification(`${rcti.invoiceNumber}: ${err.message}`, 'error');
      return false;
    } finally { setSyncing(null); }
  };

  // Re-push a row the app already marked "synced". The in-app synced flag sticks even after
  // a bill is voided in Xero, and a plain push is blocked by the dup-guard — so this re-checks
  // Xero LIVE by the RCTI reference. If a live (non-voided) bill still exists, voiding it is
  // required first, so we say so + refresh the deep link. If the old bill is gone (voided/
  // deleted), we push the CURRENT (corrected) RCTI fresh and attach its PDF. One click instead
  // of the void → rebuild → push dance.
  const rePushToXero = async (rcti) => {
    if (!xeroConnected) { addNotification('Connect Xero first', 'error'); return; }
    setSyncing(rcti.id);
    let info = null;
    try {
      const chk = await fetch(`${XERO_FN}?action=get-invoice&reference=${encodeURIComponent(rcti.invoiceNumber)}`);
      info = await chk.json();
    } catch (err) {
      addNotification(`${rcti.invoiceNumber}: couldn't check Xero — ${err.message}`, 'error');
      setSyncing(null); return;
    }
    if (info && info.found) {
      const link = info.invoiceId ? `https://go.xero.com/AccountsPayable/Edit.aspx?InvoiceID=${info.invoiceId}` : rcti.xeroDeepLink;
      setRctis(prev => prev.map(r => r.id === rcti.id ? { ...r, xeroDeepLink: link } : r));
      addNotification(`${rcti.invoiceNumber} is still a live ${info.status} bill in Xero ($${info.total}). Void it in Xero first (Open in Xero), then Re-push.`, 'error');
      setSyncing(null); return;
    }
    // No live bill — the old one was voided/deleted. Push the current RCTI fresh + attach its PDF.
    setSyncing(null);
    addNotification(`${rcti.invoiceNumber}: no live bill in Xero — pushing the current RCTI fresh…`, 'info');
    const invId = await syncToXero({ ...rcti, approved: true });
    if (invId) await attachPdf(rcti, typeof invId === 'string' ? invId : null, true);
  };

  const syncAll = async () => {
    const queue = filtered.filter(r => r.approved && !r.isHistorical && r.xeroStatus !== 'synced');
    if (!queue.length) { addNotification('No approved RCTIs to push', 'info'); return; }
    // Pace the loop under Xero's 60-calls/min ceiling. Each bill now both PUSHES (create
    // contact + invoice, ~2-3 calls) AND auto-attaches its PDF (+1 PUT) — the PDF build adds
    // natural spacing on top of the gap, keeping us comfortably under the limit.
    const GAP_MS = 2600;
    const etaMin = Math.max(1, Math.round((queue.length * 5500) / 60000));
    addNotification(`Pushing ${queue.length} RCTIs to Xero and attaching each PDF — paced under the 60/min limit (~${etaMin} min). Keep this tab open.`, 'info');
    let ok = 0, attached = 0;
    for (let i = 0; i < queue.length; i++) {
      if (i > 0) await new Promise(res => setTimeout(res, GAP_MS));
      const invId = await syncToXero(queue[i]);
      if (invId) {
        ok++;
        // Auto-attach the RCTI PDF to the bill we just created — pass the GUID so it skips
        // the lookup. Silent: the run summary reports the totals. Failure still toasts.
        if (await attachPdf(queue[i], typeof invId === 'string' ? invId : null, true)) attached++;
      }
    }
    addNotification(
      `Pushed ${ok}/${queue.length} RCTIs (${attached} PDFs attached)${ok < queue.length ? ' — click “Push to Xero” again to retry the rest (already-sent ones are skipped)' : ' ✓'}`,
      ok === queue.length ? 'success' : 'error');
  };
  const approveAll = () => {
    const targets = filtered.filter(r => r.xeroStatus !== 'synced');
    const ids = new Set(targets.map(r => r.id));
    setRctis(prev => prev.map(r => ids.has(r.id) ? { ...r, approved: true } : r));
    addNotification(`Approved ${ids.size} RCTIs for pushing`, 'success');
    // Publish the approved rows to the driver portal — but ONLY rows in the active
    // Build Period. Old-period rows in view carry session-rebuilt numbers that may
    // differ from what was actually invoiced/paid; overwriting their archive entries
    // from a bulk approve would rewrite portal history. Per-row toggleApprove remains
    // the deliberate way to (re)publish an individual old-period RCTI. Upserts are
    // idempotent, so already-approved rows republish harmlessly (self-healing).
    // Same guard for the advance ledger: only the active Build Period may book a
    // repayment. A bulk approve that swept an old period in view would otherwise
    // re-book deductions that were already taken.
    targets.filter(r => buildPeriodISO && r.periodEndISO === buildPeriodISO)
      .forEach(r => { syncPortalArchive(r, true); if (r.advance) syncAdvanceLedger(r, true); });
  };

  // Attach the RCTI document (as a real PDF) to its Xero bill. Builds the RCTI HTML,
  // converts it to PDF via Apps Script (`rctiHtmlToPdf`), then PUTs it to the bill via
  // the Xero function (`attach-pdf`, which resolves the bill by GUID or RCTI reference).
  const attachPdf = async (rcti, explicitInvId, silent) => {
    const invId = explicitInvId || rcti.xeroInvoiceGuid || rcti.xeroInvoiceId || null;
    setSyncing(rcti.id);
    try {
      const htmlBase64 = await generatePDFBase64(rcti);
      const fileName = `${rcti.companyName || rcti.personalName || rcti.driverLogin} ${rcti.invoiceNumber} ${rcti.periodLabel}.pdf`;
      const pdfRes = await fetch(APPS_SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'rctiHtmlToPdf', htmlBase64, filename: fileName }) });
      const pdfData = await pdfRes.json();
      if (!pdfData.success || !pdfData.pdfBase64) { addNotification(`${rcti.invoiceNumber}: couldn't build PDF — ${pdfData.error || 'no PDF returned'}`, 'error'); return false; }
      const attRes = await fetch(`${XERO_FN}?action=attach-pdf`, { method: 'POST', body: JSON.stringify({ invoiceId: invId, reference: rcti.invoiceNumber, filename: pdfData.filename, pdfBase64: pdfData.pdfBase64 }) });
      const attData = await attRes.json();
      if (attData.success) {
        setRctis(prev => prev.map(r => r.id === rcti.id ? { ...r, pdfAttached: true } : r));
        setXeroAttach(prev => ({ ...prev, [rcti.invoiceNumber]: { found: true, hasAttachments: true } }));
        if (!silent) addNotification(attData.skipped ? `${rcti.invoiceNumber}: the Xero bill already has its PDF` : `${rcti.invoiceNumber}: RCTI PDF attached to the Xero bill ✓`, 'success');
        return true;
      }
      addNotification(`${rcti.invoiceNumber}: PDF attach failed — ${attData.error || ''}`, 'error');
      return false;
    } catch (e) { addNotification(`${rcti.invoiceNumber}: attach error — ${e.message}`, 'error'); return false; }
    finally { setSyncing(null); }
  };

  // Back-fill PDFs onto every synced bill in view that doesn't have one yet. Kept a
  // SEPARATE pass from the push so neither operation's API-call rate blows Xero's
  // 60/min limit (each attach = a lookup + a PUT; the PDF build adds natural spacing).
  const attachAllPdfs = async () => {
    const queue = filtered.filter(needsPdf);
    if (!queue.length) { addNotification('No bills need a PDF', 'info'); return; }
    const etaMin = Math.max(1, Math.round((queue.length * 4000) / 60000));
    addNotification(`Attaching RCTI PDFs to ${queue.length} bills (~${etaMin} min). Keep this tab open.`, 'info');
    let ok = 0;
    for (let i = 0; i < queue.length; i++) {
      if (i > 0) await new Promise(res => setTimeout(res, 1500));
      if (await attachPdf(queue[i])) ok++;
    }
    addNotification(`Attached ${ok}/${queue.length} RCTI PDFs${ok < queue.length ? ' — click again to retry the rest' : ' ✓'}`, ok === queue.length ? 'success' : 'error');
  };

  const startAddLines = (rctiId) => setEditingLines(prev => ({ ...prev, [rctiId]: [emptyLine()] }));
  const cancelAddLines = (rctiId) => setEditingLines(prev => { const n = { ...prev }; delete n[rctiId]; return n; });
  const addEditLine = (rctiId) => setEditingLines(prev => ({ ...prev, [rctiId]: [...(prev[rctiId] || []), emptyLine()] }));
  const removeEditLine = (rctiId, lineId) => setEditingLines(prev => {
    const lines = prev[rctiId] || [];
    return { ...prev, [rctiId]: lines.length > 1 ? lines.filter(l => l.id !== lineId) : lines };
  });
  const updateAddLine = (rctiId, lineId, field, value) => setEditingLines(prev => ({
    ...prev,
    [rctiId]: (prev[rctiId] || []).map(l => {
      if (l.id !== lineId) return l;
      const updated = { ...l, [field]: value };
      // Pre-fill the default rate when a known preset is chosen.
      if (field === 'preset' && PRESET_RATES[value] != null && (!updated.rate || Number(updated.rate) === 0)) {
        updated.rate = PRESET_RATES[value];
      }
      if (field === 'quantity' || field === 'rate' || field === 'preset') {
        const q = parseFloat(field === 'quantity' ? value : updated.quantity) || 0;
        const r = parseFloat(field === 'rate' ? value : updated.rate) || 0;
        const raw = Math.round(q * r * 100) / 100;
        updated.amount = updated.sign === '-' ? -Math.abs(raw) : Math.abs(raw);
      }
      if (field === 'sign') {
        updated.amount = value === '-' ? -Math.abs(updated.amount) : Math.abs(updated.amount);
      }
      return updated;
    }),
  }));
  // Correcting an RCTI that is already approved (and often already emailed and in Xero) —
  // e.g. an accident-excess deduction added after the pay run (Paul, 10 Oct 2026). Approve
  // is the only thing that publishes to the driver portal, and its button is hidden once a
  // row is pushed, so the portal kept the old RCTI. Re-publish the corrected copy and
  // re-price the advance on the new total. An emailed RCTI is marked unsent so Send RCTI
  // comes back for the corrected one. Xero is left to Re-push (void the old bill first).
  const afterEdit = (prior, updated) => (prior.approved && prior.emailStatus === 'sent'
    ? { ...updated, emailStatus: 'unsent', pdfAttached: false } : updated);
  const republishApproved = (updated) => {
    if (!updated.approved || updated.isHistorical) return;
    syncPortalArchive(updated, true);
    if (updated.advance) syncAdvanceLedger(updated, true);
  };
  // Open ONE archived (already approved, emailed and pushed) RCTI for correction — e.g. an
  // accident-excess deduction found after the pay run (Paul, 10 Oct 2026). The only other
  // way in was Build, which un-approves the whole period and re-prices it from today's
  // sheets. The archived copy is what went out, so it becomes a live row as-is: approved,
  // sent and synced, so Add Items edits it, the edit republishes the portal copy, and
  // Re-push replaces the bill once the old one is voided in Xero. Its id is the build id
  // (LOGIN|D-Mon-YYYY), so a later Build of the period finds it approved and keeps it.
  const openForCorrection = (row) => {
    const iso = row.periodEndISO || row.reportEndDate;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
    if (!m || !row.driverLogin) { addNotification('This archived RCTI has no login or period end — it can\'t be opened for correction', 'error'); return; }
    const MON = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const reportEndDate = `${Number(m[3])}-${MON[Number(m[2]) - 1]}-${m[1]}`;
    const id = `${row.driverLogin}|${reportEndDate}`;
    if (rctis.some(r => r.id === id)) { addNotification(`${row.invoiceNumber} is already open for correction`, 'info'); return; }
    const { isHistorical, ...archived } = row;
    setRctis(prev => [...prev, {
      ...archived, id, reportEndDate, periodEndISO: iso, driverName: row.driverLogin,
      isCorrection: true, approved: true, emailStatus: 'sent', xeroStatus: 'synced', status: 'Posted',
    }]);
    addNotification(`${row.invoiceNumber} open for correction — expand it, Add Items, then void the old bill in Xero and Re-push`, 'success');
  };
  // A driver who moves from their own ABN to a company keeps the same login, so an RCTI
  // issued before the office caught up still names the sole trader (LANDERR → TWOBOB APD
  // PTY LTD, Oct 2026: he gave the company on the portal 13 Sep; RCTI-1335 went out 11 Oct
  // in his own name). Re-address takes the legal identity from the live driver record and
  // nothing else: same number, lines and totals, and the bank details stay as paid.
  // Offered only on a correction row, and only by hand: it is Paul's call which issued
  // RCTIs belong to the new entity, so this never happens on its own.
  const normAbn = (s) => (s || '').toString().replace(/\D/g, '');
  const liveIdentity = (rcti) => {
    const pool = (drivers && drivers.length ? drivers : sheetData?.drivers) || [];
    const d = pool.find(x => (x.loginName || '').toUpperCase() === (rcti.driverLogin || '').toUpperCase());
    if (!d || !normAbn(d.abn)) return null;
    const differs = normAbn(d.abn) !== normAbn(rcti.abn)
      || (d.companyName || '').trim().toLowerCase() !== (rcti.companyName || '').trim().toLowerCase();
    return differs ? d : null;
  };
  const readdressFromLive = (rcti) => {
    const d = liveIdentity(rcti);
    if (!d) return;
    const to = d.companyName || d.name;
    if (!window.confirm(`Re-address ${rcti.invoiceNumber} to ${to} (ABN ${d.abn})?\n\nWas: ${rcti.companyName || rcti.personalName || '—'} (ABN ${rcti.abn || '—'}).\nSame number, lines and totals. The portal copy updates and Send RCTI comes back so you can re-issue it.`)) return;
    const updated = afterEdit(rcti, {
      ...rcti, companyName: d.companyName || '', abn: d.abn,
      address: d.address || rcti.address, email: d.email || rcti.email, phone: d.phone || rcti.phone,
      readdressedAt: new Date().toISOString(),
    });
    setRctis(prev => prev.map(r => (r.id === rcti.id ? updated : r)));
    republishApproved(updated);
    addNotification(`${rcti.invoiceNumber} re-addressed to ${to}. Send RCTI to re-issue it. In Xero, change the bill's contact (or void it and Re-push).`, 'success');
  };
  const saveAddedLines = async (rcti) => {
    const newLines = (editingLines[rcti.id] || []).filter(l => (l.preset || l.customDesc) && l.amount !== 0);
    if (newLines.length === 0) { alert('Please add at least one line item with a description and amount.'); return; }
    const newLineItems = newLines.map(l => ({
      description: l.preset === 'Other (type below)' ? l.customDesc : l.preset,
      quantity: parseFloat(l.quantity) || 1,
      rate: parseFloat(l.rate) || Math.abs(l.amount),
      amount: l.amount,
    }));
    // Block adding a line whose description already exists on the invoice (e.g. the
    // auto-generated recurring fee) — amend that line instead of double-charging.
    const existing = new Set((rcti.lineItems || []).map(li => (li.description || '').trim().toLowerCase()));
    const dup = newLineItems.find(li => existing.has((li.description || '').trim().toLowerCase()));
    if (dup) { addNotification(`"${dup.description}" is already on ${rcti.invoiceNumber} — amend that line instead of adding a duplicate.`, 'error'); return; }
    const allLineItems = [...rcti.lineItems, ...newLineItems];
    const t = recomputeRctiTotals(rcti, allLineItems);
    const updatedRcti = afterEdit(rcti, { ...rcti, lineItems: allLineItems, grossIncome: t.grossIncome, subtotal: t.subtotal, gst: t.gst, total: t.total, totalFormatted: fmt(t.total), advance: t.advance, deductions: { ...rcti.deductions, leaseFee: t.leaseFee } });
    setRctis(prev => prev.map(r => r.id === rcti.id ? updatedRcti : r));
    cancelAddLines(rcti.id);
    addNotification(`${newLineItems.length} item(s) added to ${rcti.invoiceNumber} — saving...`, 'info');
    // Persist to localStorage (survives page refresh)
    try {
      const stored = JSON.parse(localStorage.getItem('hp-rcti-mods') || '{}');
      stored[rcti.id] = { lineItems: updatedRcti.lineItems, grossIncome: updatedRcti.grossIncome, subtotal: updatedRcti.subtotal, gst: updatedRcti.gst, total: updatedRcti.total, totalFormatted: updatedRcti.totalFormatted, deductions: updatedRcti.deductions };
      localStorage.setItem('hp-rcti-mods', JSON.stringify(stored));
    } catch {}
    // Persist to Google Sheets (survives browser clear)
    try {
      const res = await fetch(MANUAL_RCTI_URL, {
        method: 'POST',
        body: JSON.stringify({ action: 'saveManualRCTI', rcti: storedEdit(updatedRcti, rcti) }),
      });
      const data = await res.json();
      addNotification(data.success ? `${rcti.invoiceNumber} saved permanently` : `Saved locally — sync failed`, data.success ? 'success' : 'error');
    } catch {
      addNotification(`Saved locally — Sheets sync failed`, 'error');
    }
    republishApproved(updatedRcti);
  };
  // Persist a full lineItems set as a modification (recomputes totals). Shared by
  // amend/remove of inserted line items.
  const persistLineItems = async (rcti, newLineItems, note) => {
    const t = recomputeRctiTotals(rcti, newLineItems);
    const updatedRcti = afterEdit(rcti, { ...rcti, lineItems: newLineItems, grossIncome: t.grossIncome, subtotal: t.subtotal, gst: t.gst, total: t.total, totalFormatted: fmt(t.total), advance: t.advance, deductions: { ...rcti.deductions, leaseFee: t.leaseFee } });
    setRctis(prev => prev.map(r => r.id === rcti.id ? updatedRcti : r));
    try {
      const stored = JSON.parse(localStorage.getItem('hp-rcti-mods') || '{}');
      stored[rcti.id] = { lineItems: updatedRcti.lineItems, grossIncome: updatedRcti.grossIncome, subtotal: updatedRcti.subtotal, gst: updatedRcti.gst, total: updatedRcti.total, totalFormatted: updatedRcti.totalFormatted, deductions: updatedRcti.deductions };
      localStorage.setItem('hp-rcti-mods', JSON.stringify(stored));
    } catch {}
    try {
      const res = await fetch(MANUAL_RCTI_URL, { method: 'POST', body: JSON.stringify({ action: 'saveManualRCTI', rcti: storedEdit(updatedRcti, rcti) }) });
      const data = await res.json();
      addNotification(data.success ? `${rcti.invoiceNumber} ${note || 'updated'}` : `Saved locally — sync failed`, data.success ? 'success' : 'error');
    } catch { addNotification(`Saved locally — Sheets sync failed`, 'error'); }
    republishApproved(updatedRcti);
  };
  const removeLineItem = (rcti, idx) => {
    const li = rcti.lineItems[idx];
    if (!li) return;
    if (!window.confirm(`Remove "${li.description}" (${fmt(li.amount)}) from ${rcti.invoiceNumber}?`)) return;
    if (editLI && editLI.rctiId === rcti.id) setEditLI(null);
    persistLineItems(rcti, rcti.lineItems.filter((_, j) => j !== idx), 'line removed');
  };
  const saveLineItemEdit = (rcti) => {
    if (!editLI || editLI.rctiId !== rcti.id) return;
    const q = parseFloat(editLI.quantity); const r = parseFloat(editLI.rate);
    if (isNaN(q) || isNaN(r)) { addNotification('Enter a valid qty and rate', 'error'); return; }
    const newItems = rcti.lineItems.map((li, j) => j === editLI.idx
      ? { ...li, quantity: q, rate: r, amount: Math.round(q * r * 100) / 100 } : li);
    setEditLI(null);
    persistLineItems(rcti, newItems, 'line amended');
  };
  // Pro-rate the van lease fee as a modification. Only the lease deduction changes; gross
  // and line items are untouched, so we recompute subtotal/GST/total directly (no line
  // rebuild). Persisted like any other mod so it survives refresh AND a rebuild — the mod
  // snapshot now carries `deductions`, and applyMod restores it (see the sheet-mod branch).
  const persistLeaseFee = async (rcti, newLeaseFee) => {
    const round2 = n => Math.round(n * 100) / 100;
    const grossIncome = rcti.grossIncome || 0;
    const subtotal = round2(grossIncome - newLeaseFee);
    const gst = round2(subtotal * (rcti.gstRegistered === false ? 0 : 0.1));
    const total = round2(subtotal + gst);
    const deductions = { ...rcti.deductions, leaseFee: newLeaseFee };
    // Pro-rating the lease moves the total, so the capped advance instalment moves with it.
    const advance = priceAdvance(rcti.advance, total);
    const updatedRcti = { ...rcti, subtotal, gst, total, totalFormatted: fmt(total), deductions, advance };
    setRctis(prev => prev.map(r => r.id === rcti.id ? updatedRcti : r));
    try {
      const stored = JSON.parse(localStorage.getItem('hp-rcti-mods') || '{}');
      stored[rcti.id] = { lineItems: updatedRcti.lineItems, grossIncome, subtotal, gst, total, totalFormatted: updatedRcti.totalFormatted, deductions };
      localStorage.setItem('hp-rcti-mods', JSON.stringify(stored));
    } catch {}
    try {
      const res = await fetch(MANUAL_RCTI_URL, { method: 'POST', body: JSON.stringify({ action: 'saveManualRCTI', rcti: storedEdit(updatedRcti, rcti) }) });
      const data = await res.json();
      addNotification(data.success ? `${rcti.invoiceNumber} lease fee pro-rated to ${fmt(newLeaseFee)}` : `Saved locally — sync failed`, data.success ? 'success' : 'error');
    } catch { addNotification(`Saved locally — Sheets sync failed`, 'error'); }
  };
  const saveLeaseProRata = (rcti) => {
    if (!editLease || editLease.rctiId !== rcti.id) return;
    const days = parseFloat(editLease.days);
    if (isNaN(days) || days < 0 || days > LEASE_DAYS_PER_PERIOD) {
      addNotification(`Enter days worked between 0 and ${LEASE_DAYS_PER_PERIOD}`, 'error'); return;
    }
    // Always pro-rate from the FULL-period fee (fixedLeaseFee), so re-running with a
    // different day count recomputes from the baseline instead of compounding.
    const ded = rcti.deductions || {};
    const base = ded.fixedLeaseFee > 0 ? ded.fixedLeaseFee : (ded.leaseFee || 0);
    const newFee = Math.round(base * (days / LEASE_DAYS_PER_PERIOD) * 100) / 100;
    setEditLease(null);
    persistLeaseFee(rcti, newFee);
  };

const livePeriods = [...new Set(rctis.filter(r => !r.isManual && !r.isCorrection).map(r => r.periodLabel))];
  // Dedupe by period END DATE, not by label text — a period rebuilt at a different time
  // can carry a different spelling ("13 Jun" vs "13 June") and a label-only Set would
  // list the same period twice. Live labels come first, so they win the key.
  const allPeriods = [];
  const seenPeriodEnds = new Set();
  for (const p of [...livePeriods, ...historicalPeriods]) {
    if (!p) continue;
    const key = labelToEndISO(p) || p;
    if (seenPeriodEnds.has(key)) continue;
    seenPeriodEnds.add(key);
    allPeriods.push(p);
  }
  // Newest first, by the end date parsed out of the label. (The old inline monthMap here
  // only knew three-letter months, so "13 June"/"11 July"/"5 Sept" all read as January
  // and sorted below "16 May 2026" — which is how May ended up tagged "(Current)".)
  allPeriods.sort((a, b) => (labelToEndISO(b) || '').localeCompare(labelToEndISO(a) || ''));
  // The builder also offers the open period, which has no built or archived rows yet —
  // a one-off payment mid-period (XIEL5's WC certificate, Sep 2026) belongs there.
  const openPeriodLabel = isoToPeriodLabel(activePeriodISO);
  const builderPeriods = openPeriodLabel && !allPeriods.some(p => labelToEndISO(p) === activePeriodISO)
    ? [openPeriodLabel, ...allPeriods] : allPeriods;
  const payPeriods = allPeriods;

  const mostRecentPeriod = allPeriods[0] || '';
  const selEndISO = selectedPeriod === 'all' ? '' : labelToEndISO(selectedPeriod);
  // Match a row to the selected period by END DATE first (robust to "Jun" vs "June"
  // label differences between the build and the period list), falling back to the
  // exact label for manuals / legacy rows that may lack periodEndISO.
  const periodMatch = r => (selEndISO && r.periodEndISO === selEndISO) || r.periodLabel === selectedPeriod;
  // A correction row (one archived RCTI opened for editing) doesn't make the period "built":
  // the other archived RCTIs keep showing around it.
  const isLivePeriod = selectedPeriod === 'all' || livePeriods.includes(selectedPeriod) || rctis.some(r => !r.isManual && !r.isCorrection && periodMatch(r));

  // Viewing a period this browser has no built rows for: show the server archive. That is
  // the APPROVED set that actually went to the contractors — read-only, because it is a
  // record, not a rebuild. isHistorical is what disables the Xero push, the line-item and
  // lease edits and Attach PDF. They were also stamped emailStatus 'sent', which made an
  // archived period look emailed when it wasn't (6 Sep–3 Oct 2026); Sent comes from the
  // email log now (withEmailStatus below). To change one, rebuild the period.
  const historyDisplayRows = (!isLivePeriod && selectedPeriod !== 'all')
    ? (archiveRows[selEndISO] || []).map(r => ({
        ...r,
        id: `ARCHIVE|${r.driverLogin || r.personalName}|${r.periodEndISO || selEndISO}`,
        driverName: r.driverLogin || '',
        periodLabel: r.periodLabel || selectedPeriod,
        reportEndDate: r.periodEndISO || selEndISO,
        lineItems: r.lineItems || [],
        deductions: r.deductions || {},
        gccd: r.gccd || {},
        approved: true,
        xeroStatus: 'pending',
        xeroInvoiceId: null,
        isHistorical: true,
      }))
    : [];

// A manual RCTI is never in the archive (only built rows are published there), so the
  // archive view of a period used to hide it — RCTI-1298/1299 for 9 Aug–5 Sep were
  // invisible unless you pressed Build. They are live rows: show them alongside.
  const periodFiltered = selectedPeriod === 'all'
    ? rctis
    : isLivePeriod
      ? rctis.filter(periodMatch)
      : [
          ...historyDisplayRows.filter(h => !rctis.some(r => r.isCorrection && periodMatch(r) && r.driverLogin === h.driverLogin)),
          ...rctis.filter(r => (r.isManual || r.isCorrection) && periodMatch(r)),
        ];
  // From EMAIL_LOG_FROM on, Sent means the server logged an email of THIS version: same
  // invoice number and same total (a correction keeps its number, so the total decides).
  // Earlier periods keep what the row says (archived ones went out — Gmail has them).
  const withEmailStatus = r => {
    const end = (r.periodEndISO || '').slice(0, 10);
    if (!end || end < EMAIL_LOG_FROM) return r.isHistorical ? { ...r, emailStatus: 'sent' } : r;
    const sent = emailLog[`${end}|${(r.driverLogin || '').toUpperCase()}`];
    // A re-address keeps the number and total, so a send logged before it went to the old entity.
    const same = sent && sent.invoiceNumber === r.invoiceNumber && Math.abs((Number(sent.total) || 0) - (Number(r.total) || 0)) < 0.005
      && !(r.readdressedAt && (sent.at || '') < r.readdressedAt);
    return { ...r, emailStatus: same ? 'sent' : 'unsent', emailedAt: sent ? sent.at : null };
  };
  const filtered = (driverFilter ? periodFiltered.filter(r => r.driverName === driverFilter) : periodFiltered).map(withEmailStatus);
  const totalGross = filtered.reduce((s, r) => s + (r.grossIncome || 0), 0);
  const totalEx = filtered.reduce((s, r) => s + (r.subtotal || 0), 0);
  const totalInc = filtered.reduce((s, r) => s + (r.total || 0), 0);
  const pendingCount = filtered.filter(r => r.xeroStatus !== 'synced').length;
  // Archive rows are excluded here as well as in syncAll — a count that includes them would
  // light up "Push to Xero (1)" on a period that is only a record of what already went out.
  const approvedPendingCount = filtered.filter(r => r.approved && !r.isHistorical && r.xeroStatus !== 'synced').length;
  const unapprovedCount = filtered.filter(r => !r.approved && r.xeroStatus !== 'synced').length;
  // Only APPROVED, unsent RCTIs count toward Send All — the button label and its enabled
  // state both derive from this, so held-back (unapproved) RCTIs can never be batch-emailed.
  // Archived rows count: they are the approved RCTIs, and Sent now comes from the email log.
  const unsentCount = filtered.filter(r => r.approved && r.emailStatus !== 'sent').length;
  // Asked of Xero, not of this browser: the in-app 'pdfAttached' flag is lost on rebuild,
  // so the button used to count every row in view (103 on 12 Oct 2026, with every bill
  // already carrying its PDF). A row needs a PDF only if Xero has its bill without one.
  const needsPdf = r => !r.isHistorical && r.invoiceNumber && r.invoiceNumber !== '—' && !r.pdfAttached
    && xeroAttach[r.invoiceNumber] && xeroAttach[r.invoiceNumber].found && !xeroAttach[r.invoiceNumber].hasAttachments;
  const pdfNeededCount = filtered.filter(needsPdf).length;
  const attachRefsKey = xeroConnected
    ? [...new Set(filtered.filter(r => !r.isHistorical && r.invoiceNumber && r.invoiceNumber !== '—').map(r => r.invoiceNumber))].sort().join(',')
    : '';
  React.useEffect(() => {
    if (!attachRefsKey) return;
    const refs = attachRefsKey.split(',').filter(ref => !xeroAttach[ref]);
    if (!refs.length) return;
    fetch(`${XERO_FN}?action=attachment-status`, { method: 'POST', body: JSON.stringify({ references: refs }) })
      .then(r => r.json()).then(d => { if (d.success) setXeroAttach(prev => ({ ...prev, ...d.status })); }).catch(() => {});
  }, [attachRefsKey]);

  return (
    <React.Fragment>
      {previewRCTI && <PreviewModal rcti={previewRCTI} onClose={() => setPreviewRCTI(null)} />}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        background: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.25)',
        borderRadius: 8, padding: '9px 16px', marginBottom: 12,
      }}>
        <Fuel size={15} color="var(--hp-purple)" style={{ flexShrink: 0 }} />
        <span style={{ flex: 1, fontSize: '.8rem', color: 'var(--hp-dim)', lineHeight: 1.4 }}>
          Before generating RCTIs, make sure you've run the{' '}
          <strong style={{ color: 'var(--hp-text)' }}>Fuel Workbench</strong>{' '}
          for this period — fuel adjustments must be staged and confirmed first.
        </span>
        <button
          className="btn btn-ghost btn-sm"
          style={{ flexShrink: 0, fontSize: '.75rem', color: 'var(--hp-purple)' }}
          onClick={() => navigate('/fuel-workbench')}
        >
          <Fuel size={12} /> Fuel Workbench →
        </button>
      </div>
      <div className="page-header">
        <div className="page-header-left">
          <h2>RCTI & Xero</h2>
          <p>Google Sheets → GCCD Engine → Recipient Created Tax Invoices → Xero</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={toggleSendMode}
            title={testMode ? 'Emails go to paul@hollypaul.com only. Click to switch to LIVE (send to contractors).' : 'LIVE — emails go to real contractors. Click to switch back to Test.'}
            className="btn btn-sm"
            style={{
              fontWeight: 700, fontSize: '.72rem', border: '1px solid',
              borderColor: testMode ? 'rgba(201,168,76,.5)' : 'var(--hp-red, #dc2626)',
              color: testMode ? 'var(--hp-gold)' : '#fff',
              background: testMode ? 'rgba(201,168,76,.12)' : 'var(--hp-red, #dc2626)',
            }}>
            {testMode ? '🧪 TEST MODE — emails to you' : '🔴 LIVE — emails to contractors'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => { setShowManualBuilder(v => !v); setActiveTab('register'); }} style={{ fontSize: '.75rem' }}>
            <Plus size={13} /> Manual RCTI
          </button>
          <button className="btn btn-secondary btn-sm" disabled={unsentCount === 0 || sendingAll} onClick={handleSendAll}>
            {sendingAll ? <React.Fragment><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Sending...</React.Fragment> : <React.Fragment><Send size={13} /> Send All ({unsentCount})</React.Fragment>}
          </button>
          {unapprovedCount > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={approveAll} style={{ fontSize: '.75rem' }}>
              <CheckCircle size={13} /> Approve All ({unapprovedCount})
            </button>
          )}
          <button className="btn btn-secondary btn-sm" disabled={!xeroConnected || approvedPendingCount === 0} onClick={syncAll}>
            <RefreshCw size={13} /> Push to Xero ({approvedPendingCount})
          </button>
          <button className="btn btn-secondary btn-sm" disabled={!xeroConnected || pdfNeededCount === 0} onClick={attachAllPdfs} title="Attach the RCTI PDF document to each Xero bill in view that doesn't have one yet">
            <Paperclip size={13} /> Attach PDFs ({pdfNeededCount})
          </button>
          <button className="btn btn-primary btn-sm" onClick={importFromSheets} disabled={loading || sheetLoading}>
            {loading ? <React.Fragment><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Building...</React.Fragment> : 'Build RCTIs from Sheets'}
          </button>
          {!xeroConnected && (
            <button className="btn btn-primary btn-sm" onClick={connectXero}>
              <Zap size={13} /> Connect Xero
            </button>
          )}
        </div>
      </div>
      <div className="page-body">
        {xeroConnected ? (
          <div style={{ background: 'rgba(34,197,94,.06)', border: '1px solid rgba(34,197,94,.2)', borderRadius: 8, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, fontSize: '.8rem' }}>
            <CheckCircle size={15} color="var(--hp-green)" /> Connected to Xero{xeroOrg ? <strong style={{ marginLeft: 4 }}>{xeroOrg}</strong> : ''}
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost btn-sm" style={{ fontSize: '.72rem' }} onClick={() => setShowXeroSettings(v => !v)}>{showXeroSettings ? 'Hide settings' : 'Xero settings'}</button>
              <button className="btn btn-ghost btn-sm" style={{ fontSize: '.72rem' }} onClick={disconnectXero}>Disconnect</button>
            </span>
          </div>
        ) : null}
        {xeroConnected && !xeroPayrollReady ? (
          <div style={{ background: 'rgba(201,168,76,.06)', border: '1px solid rgba(201,168,76,.25)', borderRadius: 8, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: '.8rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertTriangle size={14} color="var(--hp-gold)" />
              This connection covers accounting only. Re-authorise to add Payroll — needed to send night-sort timesheets.
            </span>
            <button className="btn btn-primary btn-sm" onClick={connectXero}>Re-authorise with Payroll</button>
          </div>
        ) : null}
        {xeroConnected ? (
          <div style={{ display: 'none' }} />
        ) : (
          <div style={{ background: 'rgba(201,168,76,.06)', border: '1px solid rgba(201,168,76,.25)', borderRadius: 8, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '.8rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertTriangle size={14} color="var(--hp-gold)" /> Connect Xero to push approved RCTIs as supplier bills
            </span>
            <button className="btn btn-primary btn-sm" onClick={connectXero}>Connect Xero</button>
          </div>
        )}

        {xeroConnected && showXeroSettings && xeroConfig && (
          <XeroSettingsPanel config={xeroConfig} saving={savingConfig} onSave={saveXeroConfig} accounts={xeroAccounts} />
        )}

        {/* Period picker — generates 4-week aligned options from the Sheet anchor date */}
        {(() => {
          const sheetEndISO = sheetData?.controls?.reportEndDate ? toISO(sheetData.controls.reportEndDate) : '';

          // Use local date formatting (not toISOString) to avoid UTC timezone shift
          const toLocalISO = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
          // Snap the Sheet anchor to its Saturday — this only fixes the 28-day grid alignment.
          const snapToSaturday = d => { const s = new Date(d); const day = s.getDay(); if (day !== 6) s.setDate(s.getDate() - ((day + 1) % 7)); return s; };
          const rawAnchor = sheetEndISO ? new Date(sheetEndISO + 'T00:00:00') : null;
          const anchorSat = rawAnchor && !isNaN(rawAnchor.getTime()) ? snapToSaturday(rawAnchor) : null;
          // CURRENT period = the 28-day cycle on that grid that contains TODAY, so the page
          // auto-recognises the live period even when the Controls date is a cycle or two behind.
          const autoCurrentISO = anchorSat ? detectPeriodEnd(todayISO(), toLocalISO(anchorSat)) : '';
          const generatedPeriods = [];
          if (autoCurrentISO) {
            const base = new Date(autoCurrentISO + 'T00:00:00');
            for (let offset = 1; offset >= -13; offset--) {   // 1 ahead + current + 13 back
              const end = new Date(base);
              end.setDate(base.getDate() + offset * 28);
              generatedPeriods.push(toLocalISO(end));
            }
          }

          const selectedISO = buildPeriodISO || autoCurrentISO;

          return (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)',
              borderRadius: 8, padding: '10px 16px', marginBottom: 16,
            }}>
              <Calendar size={15} color="var(--hp-gold)" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', whiteSpace: 'nowrap' }}>
                Build Period
              </span>
              {generatedPeriods.length > 0 ? (
                <select
                  className="form-select"
                  style={{ maxWidth: 380 }}
                  value={selectedISO}
                  onChange={e => setActivePeriodISO(e.target.value)}
                >
                  {generatedPeriods.map((iso, i) => (
                    <option key={iso} value={iso}>
                      {isoToPeriodLabel(iso)}{i === 1 ? ' — current period' : i === 0 ? ' — next period' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <span style={{ fontSize: '.78rem', color: 'var(--hp-dim)' }}>
                  {sheetLoading ? 'Loading period from Sheet...' : 'Load sheet data to select period'}
                </span>
              )}
            </div>
          );
        })()}

        {showManualBuilder && (
          <ManualRCTIBuilder
            drivers={drivers}
            driversLoading={manualDriversLoading}
            payPeriods={builderPeriods}
            rctis={rctis}
            onGenerate={handleGenerateManual}
          />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <label style={{ fontSize: '.72rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.1em', whiteSpace: 'nowrap' }}>Pay Period</label>
<select className="form-select" style={{ width: 'auto' }} value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)}>
            <option value="all">All periods</option>
            {payPeriods.map(p => (
<option key={p} value={p}>
{p === mostRecentPeriod ? `${p} (Current)` : `${p} (History)`}
              </option>
            ))}
          </select>
{!isLivePeriod && selectedPeriod !== 'all' && (
            <span style={{ fontSize: '.72rem', color: 'var(--hp-amber)', fontFamily: 'var(--font-mono)', background: 'rgba(201,168,76,.1)', padding: '3px 10px', borderRadius: 6 }}>
              {historyDisplayRows.length
                ? `From the archive — ${historyDisplayRows.length} approved RCTI${historyDisplayRows.length === 1 ? '' : 's'}, read-only. To fix one, expand it and press Correct.`
                : 'Nothing archived for this period — rebuild it from Build Period above.'}
            </span>
          )}
          <label style={{ fontSize: '.72rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.1em', whiteSpace: 'nowrap', marginLeft: 12 }}>Contractor</label>
          <select className="form-select" style={{ width: 'auto', minWidth: 260 }} value={driverFilter} onChange={e => setDriverFilter(e.target.value)}>
            <option value="">All contractors</option>
            {[...filtered]
              .sort((a, b) => (a.companyName || a.personalName || '').localeCompare(b.companyName || b.personalName || ''))
              .map(r => (
                <option key={r.id} value={r.driverName}>
                  {r.driverName} — {r.companyName || r.personalName || '—'}
                </option>
              ))}
          </select>
          {driverFilter && (
            <button className="btn btn-ghost btn-sm" onClick={() => setDriverFilter('')} style={{ fontSize: '.7rem' }}>
              Show all
            </button>
          )}
        </div>
        <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(5,1fr)', marginBottom: 20 }}>
          {[
            { label: 'Drivers', value: filtered.length, accent: 'var(--hp-gold)' },
            { label: 'Gross Income', value: fmt(totalGross), accent: 'var(--hp-blue)', sm: true },
            { label: 'Total Ex-GST', value: fmt(totalEx), accent: 'var(--hp-purple)', sm: true },
            { label: 'Total Inc-GST', value: fmt(totalInc), accent: 'var(--hp-green)', sm: true },
            { label: 'Emails Unsent', value: unsentCount, accent: unsentCount > 0 ? 'var(--hp-amber)' : 'var(--hp-green)' },
          ].map(s => (
            <div className="stat-card" key={s.label} style={{ '--accent': s.accent }}>
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ fontSize: s.sm ? '1.1rem' : '1.8rem' }}>{s.value}</div>
            </div>
          ))}
        </div>
        <div className="tabs">
          {['register', 'advances', 'rankings', 'how-it-works'].map(t => (
            <button key={t} className={`tab${activeTab === t ? ' active' : ''}`} onClick={() => setActiveTab(t)}>
              {t === 'register' ? 'RCTI Register'
                : t === 'advances' ? `Advances${advances.filter(a => a.status === 'active' && a.outstanding > 0).length ? ` (${advances.filter(a => a.status === 'active' && a.outstanding > 0).length})` : ''}`
                : t === 'rankings' ? 'Rankings' : 'How It Works'}
            </button>
          ))}
        </div>
        {activeTab === 'advances' && (
          <AdvancesPanel
            advances={advances} rctis={rctis} form={advForm} setForm={setAdvForm}
            busy={advBusy} onSave={saveAdvance} onVoid={voidAdvance} onRefresh={loadAdvances}
            onSend={sendAdvanceAgreement} onWithdraw={withdrawAdvanceAgreement} onDownload={downloadSignedAdvance}
          />
        )}
        {activeTab === 'rankings' && (
          <div className="card" style={{ padding: 16 }}>
            <RctiRankings rctis={filtered} />
          </div>
        )}
        {activeTab === 'register' && selEndISO && Object.keys(excludedRuns[selEndISO] || {}).length > 0 && (
          <div className="card" style={{ padding: '10px 14px', marginBottom: 12, borderColor: 'rgba(220,80,80,.35)' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.62rem', textTransform: 'uppercase', color: 'var(--hp-dim)', marginBottom: 6 }}>Removed from this run</div>
            {Object.values(excludedRuns[selEndISO]).map(e => (
              <div key={e.login} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: '.78rem', padding: '3px 0' }}>
                <span style={{ fontFamily: 'var(--font-mono)' }}>{e.invoiceNumber || '—'}</span>
                <strong>{e.login}</strong>
                <span style={{ color: 'var(--hp-dim)' }}>{e.name}</span>
                <span>{fmt(e.total || 0)}</span>
                <span style={{ color: 'var(--hp-dim)', fontSize: '.72rem' }}>removed {e.at ? new Date(e.at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'Australia/Sydney' }) : ''}{e.by ? ` by ${e.by}` : ''}</span>
                <button className="btn btn-ghost btn-sm" onClick={() => restoreToRun(e)} style={{ fontSize: '.68rem', padding: '2px 8px', marginLeft: 'auto' }}>
                  <RefreshCw size={10} /> Restore
                </button>
              </div>
            ))}
          </div>
        )}
        {activeTab === 'register' && (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 28 }}></th>
                  <th>Invoice</th><th>Driver</th><th>Company Name</th><th>Period</th>
                  <th>Gross Income</th><th>Lease Fee</th><th>Ex-GST</th><th>Inc-GST</th>
                  <th>GCCD</th><th>Xero</th><th>Email</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(rcti => (
                  <React.Fragment key={rcti.id}>
                    <tr style={{ cursor: 'pointer', background: rcti.isManual ? 'rgba(234,88,12,.04)' : undefined }} onClick={() => setExpanded(expanded === rcti.id ? null : rcti.id)}>
                      <td>{expanded === rcti.id ? <ChevronDown size={13} color="var(--hp-gold)" /> : <ChevronRight size={13} color="var(--hp-dim)" />}</td>
                      <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: '.7rem', color: 'var(--hp-gold)' }}>{rcti.invoiceNumber}</span></td>
                      <td style={{ fontWeight: 500, fontFamily: 'var(--font-mono)', fontSize: '.8rem' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <DriverAvatar login={rcti.driverLogin} photo={photos[(rcti.driverLogin || '').toUpperCase()]} size={26} />
                          {rcti.driverName}
                        </span>
                      </td>
                      <td style={{ fontSize: '.8rem' }}>{rcti.companyName || rcti.personalName || '—'}</td>
                      <td style={{ color: 'var(--hp-dim)', fontSize: '.78rem' }}>{rcti.periodLabel}</td>
                      <td style={{ fontWeight: 600, color: 'var(--hp-gold)' }}>{fmt(rcti.grossIncome || 0)}</td>
                      <td style={{ color: 'var(--hp-red)', fontSize: '.8rem' }}>{rcti.deductions && rcti.deductions.leaseFee > 0 ? `-${fmt(rcti.deductions.leaseFee)}` : '—'}</td>
                      <td style={{ fontWeight: 500 }}>{fmt(rcti.subtotal || 0)}</td>
                      <td style={{ fontWeight: 600 }}>{fmt(rcti.total || 0)}</td>
                      <td>
                        {rcti.isManual
                          ? <span className="badge badge-amber">Manual</span>
                          : rcti.gccd && rcti.gccd.meetsMinimum
                            ? <span className="badge badge-green">Meets Min</span>
                            : <span className="badge badge-amber">Top Up</span>}
                      </td>
                      <td>{rcti.xeroInvoiceId
                        ? (rcti.xeroDeepLink
                            ? <a href={rcti.xeroDeepLink} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--hp-green)', fontSize: '.76rem', textDecoration: 'none' }}>{rcti.xeroInvoiceId} <ExternalLink size={10} /></a>
                            : <span style={{ color: 'var(--hp-green)', fontSize: '.76rem' }}>{rcti.xeroInvoiceId}</span>)
                        : rcti.xeroStatus === 'error'
                          ? <span style={{ color: 'var(--hp-red)', fontSize: '.76rem' }}>Error</span>
                          : <span style={{ color: 'var(--hp-muted)', fontSize: '.76rem' }}>—</span>}</td>
                      <td>
                        {rcti.emailStatus === 'sent'
                          ? <span style={{ fontSize: '.72rem', color: 'var(--hp-green)' }}><CheckCircle size={11} /> Sent</span>
                          : <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)' }}><Mail size={11} /> Unsent</span>}
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 4 }}>
<button className="btn btn-ghost btn-sm" onClick={() => setPreviewRCTI(rcti)} style={{ fontSize: '.7rem', padding: '3px 9px' }}>
                            <Eye size={10} /> View
                          </button>
                          {buildWhatsAppLink(rcti) && (
                            <a href={buildWhatsAppLink(rcti)} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ fontSize: '.7rem', padding: '3px 9px', color: '#25D366', textDecoration: 'none' }}>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 3 }}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                              WA
                            </a>
                          )}
                          {rcti.emailStatus !== 'sent' && (
                            <button className="btn btn-secondary btn-sm" disabled={sending === rcti.id || !rcti.approved} onClick={() => handleSendOne(rcti)} title={!rcti.approved ? 'Approve first' : 'Email this RCTI'} style={{ fontSize: '.7rem', padding: '3px 9px' }}>
                              {sending === rcti.id ? <React.Fragment><RefreshCw size={10} style={{ animation: 'spin 1s linear infinite' }} /> Sending</React.Fragment> : <React.Fragment><Mail size={10} /> Send</React.Fragment>}
                            </button>
                          )}
                          {rcti.xeroStatus !== 'synced' && !rcti.isHistorical && (
                            <button className="btn btn-ghost btn-sm" onClick={() => toggleApprove(rcti)} title="Mark reviewed & approved for Xero" style={{ fontSize: '.7rem', padding: '3px 9px', color: rcti.approved ? 'var(--hp-green)' : 'var(--hp-dim)' }}>
                              <CheckCircle size={10} /> {rcti.approved ? 'Approved' : 'Approve'}
                            </button>
                          )}
                          {rcti.xeroStatus !== 'synced'
                            ? <button className="btn btn-primary btn-sm" disabled={syncing === rcti.id || !xeroConnected || !rcti.approved || rcti.isHistorical} onClick={() => syncToXero(rcti)} title={rcti.isHistorical ? 'Build RCTIs for this period before pushing' : (!rcti.approved ? 'Approve first' : 'Push to Xero')} style={{ fontSize: '.7rem', padding: '3px 9px' }}>
                                {syncing === rcti.id ? <React.Fragment><RefreshCw size={10} style={{ animation: 'spin 1s linear infinite' }} /> Pushing</React.Fragment> : <React.Fragment><Zap size={10} /> Push</React.Fragment>}
                              </button>
                            : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: '.72rem', color: 'var(--hp-green)' }}><CheckCircle size={11} /> Synced</span>
                                <button className="btn btn-ghost btn-sm" disabled={syncing === rcti.id || !xeroConnected} onClick={() => rePushToXero(rcti)} title="Re-check Xero and push again — use after voiding the old bill to replace it with the corrected RCTI" style={{ fontSize: '.66rem', padding: '2px 7px' }}>
                                  {syncing === rcti.id ? <RefreshCw size={10} style={{ animation: 'spin 1s linear infinite' }} /> : <React.Fragment><RefreshCw size={10} /> Re-push</React.Fragment>}
                                </button>
                              </span>}
                          {rcti.isManual && (
                            <button className="btn btn-ghost btn-sm" onClick={() => deleteManual(rcti)} title="Delete this manual RCTI" style={{ fontSize: '.7rem', padding: '3px 9px', color: 'var(--hp-red)' }}>
                              <Trash2 size={10} /> Delete
                            </button>
                          )}
                          {!rcti.isManual && (
                            <button className="btn btn-ghost btn-sm" onClick={() => removeFromRun(rcti)} title="Take this RCTI out of the period's run — nothing else is rebuilt or un-approved; Restore puts it back" style={{ fontSize: '.66rem', padding: '2px 7px', color: 'var(--hp-red)' }}>
                              <X size={10} /> Remove
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded === rcti.id && (
                      <tr>
                        <td colSpan={13} style={{ padding: 0 }}>
                          <div style={{ background: 'rgba(201,168,76,.04)', borderTop: '1px solid rgba(201,168,76,.15)', padding: '16px 20px 16px 44px' }}>
                            <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                              <div>
                                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.6rem', textTransform: 'uppercase', color: 'var(--hp-dim)', marginBottom: 10 }}>RCTI Line Items</div>
                                <table style={{ width: '100%', fontSize: '.76rem', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid var(--hp-border)' }}>
                                      {['Description','Qty','Rate','Amount'].map(h => (
                                        <th key={h} style={{ padding: '4px 8px', color: 'var(--hp-dim)', fontSize: '.6rem', textAlign: h === 'Description' ? 'left' : 'right' }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rcti.lineItems.map((li, i) => {
                                      const editable = !rcti.isHistorical && (!li.type || li.type === 'extra');
                                      const isEd = editLI && editLI.rctiId === rcti.id && editLI.idx === i;
                                      const inp = { width: 56, textAlign: 'right', background: 'var(--hp-surface)', border: '1px solid var(--hp-gold)', borderRadius: 4, color: 'var(--hp-text)', fontSize: '.72rem', padding: '2px 4px' };
                                      const ico = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '.74rem', padding: '0 3px' };
                                      return (
                                      <tr key={i} style={{ borderBottom: '1px solid rgba(42,42,51,.4)' }}>
                                        <td style={{ padding: '5px 8px', fontSize: '.72rem' }}>
                                          {li.description}
                                          {editable && !isEd && (
                                            <span style={{ whiteSpace: 'nowrap' }}>
                                              <button title="Amend" onClick={() => setEditLI({ rctiId: rcti.id, idx: i, quantity: li.quantity, rate: li.rate })} style={{ ...ico, color: 'var(--hp-gold)' }}>✎</button>
                                              <button title="Remove" onClick={() => removeLineItem(rcti, i)} style={{ ...ico, color: 'var(--hp-red)' }}>🗑</button>
                                            </span>
                                          )}
                                          {isEd && (
                                            <span style={{ whiteSpace: 'nowrap' }}>
                                              <button title="Save" onClick={() => saveLineItemEdit(rcti)} style={{ ...ico, color: 'var(--hp-green)', fontWeight: 700 }}>✓</button>
                                              <button title="Cancel" onClick={() => setEditLI(null)} style={{ ...ico, color: 'var(--hp-dim)' }}>✕</button>
                                            </span>
                                          )}
                                        </td>
                                        <td style={{ textAlign: 'right', padding: '5px 8px' }}>
                                          {isEd ? <input type="number" min="0" step="0.01" value={editLI.quantity} onChange={e => setEditLI({ ...editLI, quantity: e.target.value })} style={inp} /> : li.quantity}
                                        </td>
                                        <td style={{ textAlign: 'right', padding: '5px 8px', color: 'var(--hp-dim)' }}>
                                          {isEd ? <input type="number" min="0" step="0.0001" value={editLI.rate} onChange={e => setEditLI({ ...editLI, rate: e.target.value })} style={inp} /> : fmtRate(li.rate)}
                                        </td>
                                        <td style={{ textAlign: 'right', padding: '5px 8px', fontWeight: 500 }}>{isEd ? fmt((parseFloat(editLI.quantity) || 0) * (parseFloat(editLI.rate) || 0)) : fmt(li.amount)}</td>
                                      </tr>
                                    );})}
                                    <tr style={{ borderTop: '2px solid var(--hp-border)' }}>
                                      <td colSpan={3} style={{ padding: '6px 8px', fontWeight: 600, color: 'var(--hp-gold)' }}>Gross Service Income</td>
                                      <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: 'var(--hp-gold)' }}>{fmt(rcti.grossIncome || 0)}</td>
                                    </tr>
                                    {rcti.deductions && rcti.deductions.leaseFee > 0 && (() => {
                                      const leaseEditable = !rcti.isHistorical && !(rcti.deductions.leaseRate > 0);
                                      const isEdL = editLease && editLease.rctiId === rcti.id;
                                      const base = rcti.deductions.fixedLeaseFee > 0 ? rcti.deductions.fixedLeaseFee : rcti.deductions.leaseFee;
                                      const days = parseFloat(isEdL ? editLease.days : 0) || 0;
                                      const preview = Math.round(base * (days / LEASE_DAYS_PER_PERIOD) * 100) / 100;
                                      const lInp = { width: 46, textAlign: 'right', background: 'var(--hp-surface)', border: '1px solid var(--hp-gold)', borderRadius: 4, color: 'var(--hp-text)', fontSize: '.72rem', padding: '2px 4px' };
                                      const lIco = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '.74rem', padding: '0 3px' };
                                      return (
                                      <tr>
                                        <td colSpan={3} style={{ padding: '5px 8px', color: 'var(--hp-red)' }}>
                                          {rcti.deductions.leaseLabel || 'Van Lease Fee'}
                                          {leaseEditable && !isEdL && (
                                            <button title={`Pro-rate the lease fee by days worked (out of ${LEASE_DAYS_PER_PERIOD})`} onClick={() => setEditLease({ rctiId: rcti.id, days: String(LEASE_DAYS_PER_PERIOD) })} style={{ ...lIco, color: 'var(--hp-gold)', fontSize: '.66rem', fontWeight: 600 }}>Pro rata</button>
                                          )}
                                          {isEdL && (
                                            <span style={{ whiteSpace: 'nowrap', marginLeft: 6, color: 'var(--hp-dim)', fontSize: '.72rem' }}>
                                              <input type="number" min="0" max={LEASE_DAYS_PER_PERIOD} step="1" value={editLease.days} onChange={e => setEditLease({ ...editLease, days: e.target.value })} style={lInp} /> / {LEASE_DAYS_PER_PERIOD} days
                                              <span style={{ color: 'var(--hp-red)', marginLeft: 6 }}>→ -{fmt(preview)}</span>
                                              <button title="Save" onClick={() => saveLeaseProRata(rcti)} style={{ ...lIco, color: 'var(--hp-green)', fontWeight: 700 }}>✓</button>
                                              <button title="Cancel" onClick={() => setEditLease(null)} style={{ ...lIco, color: 'var(--hp-dim)' }}>✕</button>
                                            </span>
                                          )}
                                        </td>
                                        <td style={{ textAlign: 'right', padding: '5px 8px', color: 'var(--hp-red)' }}>-{fmt(rcti.deductions.leaseFee)}</td>
                                      </tr>
                                      );
                                    })()}
                                    <tr>
                                      <td colSpan={3} style={{ padding: '5px 8px' }}>Subtotal (ex GST)</td>
                                      <td style={{ textAlign: 'right', padding: '5px 8px', fontWeight: 600 }}>{fmt(rcti.subtotal || 0)}</td>
                                    </tr>
                                    <tr>
                                      <td colSpan={3} style={{ padding: '4px 8px', color: 'var(--hp-dim)' }}>{(rcti.gst || 0) === 0 ? 'No GST Applicable' : 'GST (10%)'}</td>
                                      <td style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--hp-dim)' }}>{fmt(rcti.gst || 0)}</td>
                                    </tr>
                                    <tr style={{ borderTop: '2px solid var(--hp-border)' }}>
                                      <td colSpan={3} style={{ padding: '7px 8px', fontWeight: 700, color: 'var(--hp-gold)' }}>Total Amount Payable (inc GST)</td>
                                      <td style={{ textAlign: 'right', padding: '7px 8px', fontWeight: 700, color: 'var(--hp-gold)' }}>{fmt(rcti.total || 0)}</td>
                                    </tr>
                                    {rcti.advance && rcti.advance.amount > 0 && (
                                      <>
                                        <tr>
                                          <td colSpan={3} style={{ padding: '4px 8px', color: 'var(--hp-red)' }}>
                                            {rcti.advance.label || 'Advance Repayment'}
                                            {rcti.advance.capApplied ? ' (capped)' : ''}
                                          </td>
                                          <td style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--hp-red)' }}>-{fmt(rcti.advance.amount)}</td>
                                        </tr>
                                        <tr style={{ borderTop: '2px solid var(--hp-border)' }}>
                                          <td colSpan={3} style={{ padding: '7px 8px', fontWeight: 700 }}>Net Payment to Bank Account</td>
                                          <td style={{ textAlign: 'right', padding: '7px 8px', fontWeight: 700 }}>{fmt(netRemittance(rcti))}</td>
                                        </tr>
                                        <tr>
                                          <td colSpan={4} style={{ padding: '3px 8px', color: 'var(--hp-dim)', fontSize: '.68rem' }}>
                                            Advance outstanding after this payment: {fmt(rcti.advance.balanceAfter)}
                                            {rcti.approved ? '' : ' — not recorded until approved'}
                                          </td>
                                        </tr>
                                      </>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                              <div>
                                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.6rem', textTransform: 'uppercase', color: 'var(--hp-dim)', marginBottom: 10 }}>
                                  {rcti.isManual ? 'Manual RCTI' : 'GCCD Reconciliation'}
                                </div>
                                {rcti.isManual ? (
                                  <div style={{ background: 'var(--hp-surface)', borderRadius: 8, padding: '12px 14px', fontSize: '.76rem', color: 'var(--hp-dim)' }}>
                                    <div style={{ marginBottom: 8 }}>This RCTI was created manually as an adjustment.</div>
                                    <div><strong>Period:</strong> {rcti.periodLabel}</div>
                                    <div><strong>Contractor:</strong> {rcti.companyName || rcti.personalName}</div>
                                    <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 6, background: 'rgba(234,88,12,.1)', textAlign: 'center', fontWeight: 700, fontSize: '.78rem', color: '#ea580c' }}>MANUAL ADJUSTMENT</div>
                                  </div>
                                ) : (
                                  <div style={{ background: 'var(--hp-surface)', borderRadius: 8, padding: '12px 14px', fontSize: '.76rem' }}>
                                    {[
                                      { label: 'Total Hours', value: ((rcti.gccd && rcti.gccd.totalHours) || 0).toFixed(2) + ' hrs' },
                                      { label: 'Minimum Hourly Rate', value: fmt((rcti.gccd && rcti.gccd.minRate) || 0) },
                                      { label: 'Minimum Payable', value: fmt((rcti.gccd && rcti.gccd.minPayable) || 0), bold: true },
                                      { label: 'Actual Earnings', value: fmt((rcti.gccd && rcti.gccd.actualEarnings) || 0) },
                                      { label: 'Variance', value: fmt((rcti.gccd && rcti.gccd.variance) || 0), color: ((rcti.gccd && rcti.gccd.variance) || 0) < 0 ? 'var(--hp-red)' : 'var(--hp-green)' },
                                      { label: 'GCCD Levy Applied', value: fmt((rcti.gccd && rcti.gccd.gccdLevy) || 0), color: 'var(--hp-gold)' },
                                    ].map(row => (
                                      <div key={row.label} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, padding: '4px 0', borderBottom: '1px solid var(--hp-border)' }}>
                                        <span style={{ color: 'var(--hp-dim)' }}>{row.label}</span>
                                        <span style={{ fontWeight: row.bold ? 700 : 400, color: row.color || 'var(--hp-text)', fontSize: '.74rem' }}>{row.value}</span>
                                      </div>
                                    ))}
                                    <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 6, background: rcti.gccd && rcti.gccd.meetsMinimum ? 'rgba(34,197,94,.1)' : 'rgba(251,191,36,.1)', textAlign: 'center', fontWeight: 700, fontSize: '.78rem', color: rcti.gccd && rcti.gccd.meetsMinimum ? 'var(--hp-green)' : 'var(--hp-amber)' }}>
                                      {rcti.gccd && rcti.gccd.meetsMinimum ? 'MEETS MINIMUM' : 'TOP UP APPLIED'}
                                    </div>
                                  </div>
                                )}
                                <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                  {!rcti.isHistorical && (
                                    <button className="btn btn-ghost btn-sm" onClick={() => editingLines[rcti.id] ? cancelAddLines(rcti.id) : startAddLines(rcti.id)} style={{ color: 'var(--hp-gold)', fontSize: '.72rem' }}>
                                      <Plus size={12} /> {editingLines[rcti.id] ? 'Cancel Adding' : 'Add Items'}
                                    </button>
                                  )}
                                  {rcti.isHistorical && !rcti.isManual && (
                                    <button className="btn btn-ghost btn-sm" onClick={() => openForCorrection(rcti)} title="Open this one RCTI for editing — the rest of the period stays as it is" style={{ color: 'var(--hp-gold)', fontSize: '.72rem' }}>
                                      <Plus size={12} /> Correct
                                    </button>
                                  )}
                                  {rcti.isCorrection && liveIdentity(rcti) && (
                                    <button className="btn btn-ghost btn-sm" onClick={() => readdressFromLive(rcti)} title={`The driver record now says ${liveIdentity(rcti).companyName || liveIdentity(rcti).name}, ABN ${liveIdentity(rcti).abn}`} style={{ color: 'var(--hp-gold)', fontSize: '.72rem' }}>
                                      Re-address to {liveIdentity(rcti).companyName || liveIdentity(rcti).name}
                                    </button>
                                  )}
<button className="btn btn-ghost btn-sm" onClick={() => setPreviewRCTI(rcti)}>
                                    <Eye size={12} /> Preview and PDF
                                  </button>
                                  {buildWhatsAppLink(rcti) && (
                                    <a href={buildWhatsAppLink(rcti)} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ color: '#25D366', textDecoration: 'none' }}>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 4 }}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                      WhatsApp
                                    </a>
                                  )}
                                  {rcti.emailStatus !== 'sent' && (
                                    <button className="btn btn-secondary btn-sm" disabled={sending === rcti.id || !rcti.approved} onClick={() => handleSendOne(rcti)} title={!rcti.approved ? 'Approve first' : 'Email this RCTI'}>
                                      <Mail size={12} /> Send RCTI
                                    </button>
                                  )}
                                  {rcti.xeroStatus !== 'synced' && !rcti.isHistorical && (
                                    <button className="btn btn-ghost btn-sm" onClick={() => toggleApprove(rcti)} style={{ color: rcti.approved ? 'var(--hp-green)' : 'var(--hp-dim)' }}>
                                      <CheckCircle size={12} /> {rcti.approved ? 'Approved' : 'Approve'}
                                    </button>
                                  )}
                                  {rcti.xeroStatus !== 'synced' && (
                                    <button className="btn btn-primary btn-sm" disabled={!xeroConnected || syncing === rcti.id || !rcti.approved || rcti.isHistorical} onClick={() => syncToXero(rcti)} title={rcti.isHistorical ? 'Build RCTIs for this period before pushing' : undefined}>
                                      <Zap size={12} /> Push to Xero
                                    </button>
                                  )}
                                  {rcti.xeroStatus === 'synced' && rcti.xeroDeepLink && (
                                    <a href={rcti.xeroDeepLink} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }}>
                                      <ExternalLink size={12} /> Open in Xero
                                    </a>
                                  )}
                                  {rcti.xeroStatus === 'synced' && (
                                    <button className="btn btn-ghost btn-sm" disabled={!xeroConnected || syncing === rcti.id} onClick={() => rePushToXero(rcti)} title="Re-check Xero and push again — use after voiding the old bill to replace it with the corrected RCTI">
                                      <RefreshCw size={12} /> Re-push
                                    </button>
                                  )}
                                  {!rcti.isHistorical && rcti.invoiceNumber && rcti.invoiceNumber !== '—' && (
                                    <button className="btn btn-ghost btn-sm" disabled={!xeroConnected || syncing === rcti.id} onClick={() => attachPdf(rcti)} title="Attach this RCTI as a PDF on its Xero bill (push the bill to Xero first)" style={{ color: rcti.pdfAttached ? 'var(--hp-green)' : undefined }}>
                                      <Paperclip size={12} /> {rcti.pdfAttached ? 'PDF attached' : 'Attach PDF'}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                            {editingLines[rcti.id] && (
                              <div style={{ marginTop: 16, borderTop: '1px solid rgba(201,168,76,.2)', paddingTop: 16 }}>
                                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.6rem', textTransform: 'uppercase', color: 'var(--hp-gold)', marginBottom: 10 }}>Add Line Items to {rcti.invoiceNumber}</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '2fr auto 1fr 1fr 1fr auto', gap: 8, marginBottom: 6 }}>
                                  {['Description', '+/−', 'Qty', 'Rate (ex GST)', 'Amount (ex GST)', ''].map(h => (
                                    <div key={h} style={{ fontSize: '.62rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', textAlign: h === '+/−' ? 'center' : 'left' }}>{h}</div>
                                  ))}
                                </div>
                                {editingLines[rcti.id].map(line => (
                                  <div key={line.id} style={{ display: 'grid', gridTemplateColumns: '2fr auto 1fr 1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'start' }}>
                                    <div>
                                      <select
                                        className="form-select"
                                        value={line.preset}
                                        onChange={e => updateAddLine(rcti.id, line.id, 'preset', e.target.value)}
                                        style={{ marginBottom: line.preset === 'Other (type below)' ? 6 : 0 }}
                                      >
                                        <option value="">— Select description —</option>
                                        <optgroup label="Additions">
                                          {PRESET_DESCRIPTIONS.filter(d => d !== 'Other (type below)').map(d => <option key={d} value={d}>{d}</option>)}
                                        </optgroup>
                                        <optgroup label="Deductions / Corrections">
                                          {DEDUCTION_DESCRIPTIONS.filter(d => d !== 'Other (type below)').map(d => <option key={d} value={d}>{d}</option>)}
                                        </optgroup>
                                        <option value="Other (type below)">Other (type below)</option>
                                      </select>
                                      {line.preset === 'Other (type below)' && (
                                        <input className="form-input" placeholder="Type custom description..." value={line.customDesc} onChange={e => updateAddLine(rcti.id, line.id, 'customDesc', e.target.value)} />
                                      )}
                                    </div>
                                    <button
                                      onClick={() => updateAddLine(rcti.id, line.id, 'sign', line.sign === '+' ? '-' : '+')}
                                      style={{ background: line.sign === '-' ? 'rgba(239,68,68,.15)' : 'rgba(34,197,94,.12)', border: `1px solid ${line.sign === '-' ? 'var(--hp-red)' : 'var(--hp-green)'}`, borderRadius: 6, color: line.sign === '-' ? 'var(--hp-red)' : 'var(--hp-green)', fontWeight: 700, fontSize: '1rem', width: 36, height: 36, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                      {line.sign}
                                    </button>
                                    <input className="form-input" type="number" min="0" step="0.01" value={line.quantity} onChange={e => updateAddLine(rcti.id, line.id, 'quantity', e.target.value)} style={{ textAlign: 'right' }} />
                                    <input className="form-input" type="number" min="0" step="0.01" placeholder="0.00" value={line.rate} onChange={e => updateAddLine(rcti.id, line.id, 'rate', e.target.value)} style={{ textAlign: 'right' }} />
                                    <div style={{ padding: '8px 10px', background: 'var(--hp-surface)', borderRadius: 6, textAlign: 'right', fontSize: '.8rem', fontWeight: 600, color: line.sign === '-' ? 'var(--hp-red)' : undefined }}>{fmt(line.amount)}</div>
                                    <button className="btn btn-ghost btn-sm" onClick={() => removeEditLine(rcti.id, line.id)} style={{ padding: '6px 8px', color: 'var(--hp-red)' }}><Trash2 size={13} /></button>
                                  </div>
                                ))}
                                <div style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'center' }}>
                                  <button className="btn btn-ghost btn-sm" onClick={() => addEditLine(rcti.id)} style={{ fontSize: '.75rem' }}><Plus size={12} /> Add Line</button>
                                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                    <button className="btn btn-ghost btn-sm" onClick={() => cancelAddLines(rcti.id)} style={{ fontSize: '.75rem' }}>Cancel</button>
                                    <button className="btn btn-primary btn-sm" onClick={() => saveAddedLines(rcti)} style={{ fontSize: '.75rem', fontWeight: 700 }}><Plus size={12} /> Save to Invoice</button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--hp-dim)' }}>
                <Zap size={36} style={{ marginBottom: 12, opacity: .3 }} />
                <p style={{ fontSize: '.875rem' }}>No RCTIs loaded — click Build RCTIs from Sheets</p>
              </div>
            )}
          </div>
        )}
        {activeTab === 'how-it-works' && (
          <div className="card">
            <h4 style={{ marginBottom: 20, color: 'var(--hp-gold)' }}>Google Sheets - GCCD Engine - RCTI - Xero</h4>
            <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)', lineHeight: 2 }}>
              <p>1. Sheet1 (live source): Parcel/Express scan quantities per driver, filtered to the billing period by Event Date.</p>
              <p>2. Rates tab: Per-driver rates. Format: LoginName|ParcelType - Rate.</p>
              <p>3. GCCD Engine: Hours x Min Rate = Min Payable. If Actual less than Min, top-up applied.</p>
              <p>4. RCTI_Adjustments: Fuel levy, DOE, ad hoc adjustments per driver.</p>
              <p>5. Xero ACCPAY Draft: Synced as Accounts Payable with INPUT2 tax type.</p>
              <p>6. GCCD History: Every build appends reconciliation data to Google Sheets for trend analysis.</p>
              <p>7. Email: RCTIs sent with PDF attachment generated by Google Docs via Apps Script.</p>
              <p>8. Manual RCTI: Use the Manual RCTI button to create adjustment invoices for any period.</p>
            </div>
          </div>
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </React.Fragment>
  );
}
